<template>
  <div class="main container-fluid wp-concept-page">
    <header class="wpc-hero d-flex align-items-start justify-content-between gap-3">
      <div>
        <h4 class="wpc-hero__title mb-1">Разработка концепции сайта</h4>
        <small class="wpc-hero__subtitle">Создайте и настройте профиль сайта на основе выбранной концепции.</small>
      </div>
      <div class="d-flex gap-2 wpc-hero__actions">
        <button v-if="isProjectsMode" class="btn btn-outline-light btn-sm" @click="startNewProject">
          Новый проект
        </button>
        <button v-else class="btn btn-outline-light btn-sm" @click="goToProjectsList">К списку</button>
      </div>
    </header>

    <div class="tool-notifications" aria-live="polite" aria-atomic="true">
      <transition-group name="tool-toast" tag="div" class="tool-notifications__list">
        <div v-if="uiError" key="tool-error" class="tool-notification tool-notification--error">
          {{ uiError }}
        </div>
        <div v-if="infoMessage" key="tool-info" class="tool-notification tool-notification--info">
          {{ infoMessage }}
        </div>
      </transition-group>
    </div>

    <section v-if="isProjectsMode" class="px-3 pb-2">
      <div class="card shadow-sm border-0 section-card wpc-surface">
        <div class="wpc-projects-head history-headline">
          <div>
            <h6 class="mb-1">Проекты пользователя</h6>
            <small class="text-muted">Выберите существующий проект или создайте новый.</small>
          </div>
          <div class="wpc-projects-head__actions">
            <button class="btn btn-outline-primary btn-sm" :disabled="projectsLoading" @click="loadProjects">
              <span v-if="projectsLoading" class="spinner-border spinner-border-sm me-1"></span>
              Обновить
            </button>
          </div>
        </div>

        <div v-if="projectsError" class="wpc-projects-error">{{ projectsError }}</div>
        <div v-else-if="projectsLoading" class="wpc-loading-state mt-2">
          <span class="spinner-border spinner-border-sm me-2"></span>
          Загружаем список проектов...
        </div>
        <div v-else-if="!hasProjectList" class="wpc-empty-state mt-2">
          <i class="bi bi-folder2-open"></i>
          <p class="mb-0">У вас пока нет проектов. Нажмите «Новый проект».</p>
        </div>
        <div v-else class="wpc-projects-list">
          <article
            v-for="item in projectList"
            :key="item.id"
            class="wpc-project-item"
            :class="{ active: currentProjectId === item.id }"
          >
            <div class="wpc-project-item__main">
              <div class="wpc-project-item__date">{{ formatDateTime(item.updatedAt || item.createdAt) }}</div>
              <div class="wpc-project-item__meta">
                <span class="wpc-meta-pill">Статус: {{ formatProjectStatus(item.status) }}</span>
                <span class="wpc-meta-pill">Язык: {{ item.selectedLocaleLabel || 'Не выбран' }}</span>
                <span class="wpc-meta-pill">Концепция: {{ item.selectedConceptName || '—' }}</span>
                <span class="wpc-meta-pill">Название сайта: {{ item.localizedSiteTitle || '—' }}</span>
                <span class="wpc-meta-pill">Домен сайта: {{ item.selectedDomainName || '—' }}</span>
              </div>
              <div class="wpc-project-item__topic">{{ item.topicName || 'Без тематики' }}</div>
            </div>
            <div class="wpc-project-item__actions">
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                :disabled="deletingProjectId || (isLoadingProject && currentProjectId === item.id)"
                @click="openExistingProject(item.id)"
              >
                Открыть
              </button>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm wpc-project-delete-btn"
                :disabled="Boolean(deletingProjectId)"
                @click="requestDeleteProject(item)"
              >
                <span v-if="deletingProjectId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                Удалить
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>

    <div v-if="showDeleteProjectModal" class="wpc-modal-backdrop" @click.self="closeDeleteProjectModal">
      <div class="wpc-modal">
        <div class="wpc-modal__head">
          <h6 class="mb-0">Удалить проект</h6>
        </div>
        <div class="wpc-modal__body">
          <p class="mb-0">
            Проект <strong>{{ projectDeleteCandidate?.topicName || '—' }}</strong> будет удален без возможности восстановления.
          </p>
        </div>
        <div class="wpc-modal__actions">
          <button class="btn btn-outline-secondary btn-sm" :disabled="Boolean(deletingProjectId)" @click="closeDeleteProjectModal">
            Отмена
          </button>
          <button class="btn btn-danger btn-sm" :disabled="Boolean(deletingProjectId)" @click="confirmDeleteProject">
            <span v-if="Boolean(deletingProjectId)" class="spinner-border spinner-border-sm me-1"></span>
            Удалить
          </button>
        </div>
      </div>
    </div>

    <section v-if="!isProjectsMode" class="px-3 pb-4">
      <div class="wpc-surface">
        <div class="wpc-steps">
          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 1, viewing: activeStep === 1, complete: hasConcepts }"
            @click="openStep(1)"
          >
            <span class="wpc-step__index">1</span>
            <span class="wpc-step__label">Концепция</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 2, viewing: activeStep === 2, complete: canOpenStepTwo }"
            :disabled="!canOpenStepTwo"
            @click="openStep(2)"
          >
            <span class="wpc-step__index">2</span>
            <span class="wpc-step__label">Идентичность</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 3, viewing: activeStep === 3, complete: canOpenStepThree }"
            :disabled="!canOpenStepThree"
            @click="openStep(3)"
          >
            <span class="wpc-step__index">3</span>
            <span class="wpc-step__label">Локализация</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 4, viewing: activeStep === 4, complete: canOpenStepFour }"
            :disabled="!canOpenStepFour"
            @click="openStep(4)"
          >
            <span class="wpc-step__index">4</span>
            <span class="wpc-step__label">Домен</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 5, viewing: activeStep === 5, complete: canOpenStepSix }"
            :disabled="!canOpenStepFive"
            @click="openStep(5)"
          >
            <span class="wpc-step__index">5</span>
            <span class="wpc-step__label">Логотип</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 6, viewing: activeStep === 6, complete: canOpenStepSeven }"
            :disabled="!canOpenStepSix"
            @click="openStep(6)"
          >
            <span class="wpc-step__index">6</span>
            <span class="wpc-step__label">Favicon</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ active: workingStep === 7, viewing: activeStep === 7, complete: isTemplateCompleted }"
            :disabled="!canOpenStepSeven"
            @click="openStep(7)"
          >
            <span class="wpc-step__index">7</span>
            <span class="wpc-step__label">Шаблон</span>
          </button>

          <button
            type="button"
            class="wpc-step"
            :class="{ viewing: activeStep === 8, complete: isWizardComplete }"
            :disabled="!canOpenStepEight"
            @click="openStep(8)"
          >
            <span class="wpc-step__index">8</span>
            <span class="wpc-step__label">Итог</span>
          </button>
        </div>

        <div v-if="activeStep === 1" class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head">
              <h6 class="mb-1">Шаг 1. Создание проекта и генерация концепций</h6>
            </div>

            <div class="wpc-config-grid">
              <div class="wpc-field">
                <label class="form-label">Тематика сайта</label>
                <select
                  v-model="selectedTopicName"
                  class="form-select form-select-sm"
                  :disabled="topicsLoading || isGenerating || hasConcepts"
                >
                  <option value="" disabled>
                    {{ topicsLoading ? 'Загрузка тематик...' : 'Выберите тематику' }}
                  </option>
                  <option v-for="item in topicOptions" :key="item.code || item.value" :value="item.value">
                    {{ item.label }}
                  </option>
                </select>
                <small v-if="topicsError" class="text-danger">{{ topicsError }}</small>
              </div>

              <div class="wpc-action">
                <button class="btn btn-primary btn-sm wpc-generate-btn" :disabled="isGenerating || !selectedTopicName || hasConcepts" @click="generateConcepts">
                  <span v-if="isGenerating" class="spinner-border spinner-border-sm me-1"></span>
                  {{ isGenerating ? 'Генерируем...' : 'Разработать концепции' }}
                </button>
              </div>
            </div>
            <small v-if="hasConcepts" class="text-muted d-block mt-2">
              Концепции уже созданы для текущего проекта.
            </small>

            <div v-if="false && project" class="wpc-project-meta">
              <span class="wpc-meta-pill">
                ID: <span class="mono">{{ project.id }}</span>
              </span>
              <span class="wpc-meta-pill">
                Статус: {{ formatProjectStatus(project.status) }}
              </span>
              <span class="wpc-meta-pill" v-if="project.updatedAt">
                Обновлен: {{ formatDateTime(project.updatedAt) }}
              </span>
            </div>
          </div>

          <div v-if="isLoadingProject" class="wpc-loading-state">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Загружаем проект...
          </div>

          <div v-else-if="!hasConcepts" class="wpc-empty-state">
            <i class="bi bi-lightbulb"></i>
            <p class="mb-0">
              После генерации здесь появятся карточки концепций для выбора.
            </p>
          </div>

          <div v-else class="wpc-concepts-block">
            <div class="wpc-concepts-grid">
              <article
                v-for="concept in concepts"
                :key="concept.id || `concept-${concept.conceptOrder}`"
                class="wpc-concept-card"
                :class="{ selected: isSelectedConcept(concept), dimmed: isEditingAnyConcept && !isEditingConcept(concept) }"
              >
                <div class="wpc-concept-card__head">
                  <span class="wpc-concept-card__order">Концепция {{ concept.conceptOrder }}</span>
                  <button
                    v-if="!isEditingConcept(concept)"
                    type="button"
                    class="btn btn-sm wpc-select-btn"
                    :class="isSelectedConcept(concept) ? 'btn-primary is-selected' : 'btn-outline-primary'"
                    :disabled="isDeletingConcept || isSelectingConcept || isSavingConcept || (isEditingAnyConcept && !isEditingConcept(concept))"
                    @click="setSelectedConcept(concept)"
                  >
                    <i v-if="isSelectedConcept(concept)" class="bi bi-check2-circle me-1"></i>
                    {{ isSelectedConcept(concept) ? 'Выбрана' : 'Выбрать' }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm wpc-edit-btn"
                    :disabled="isDeletingConcept || isSelectingConcept || isSavingConcept || (isEditingAnyConcept && !isEditingConcept(concept))"
                    @click="isEditingConcept(concept) ? cancelEditConcept() : startEditConcept(concept)"
                  >
                    <i :class="isEditingConcept(concept) ? 'bi bi-x-lg' : 'bi bi-pencil'"></i>
                  </button>
                  <button
                    v-if="!isEditingConcept(concept)"
                    type="button"
                    class="btn btn-outline-danger btn-sm wpc-delete-btn"
                    title="Удалить концепцию"
                    aria-label="Удалить концепцию"
                    :disabled="isDeletingConcept || isSelectingConcept || isSavingConcept || isEditingAnyConcept"
                    @click="requestDeleteConcept(concept)"
                  >
                    <span v-if="deletingConceptId === concept.id" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="bi bi-x-lg"></i>
                  </button>
                </div>

                <template v-if="isEditingConcept(concept) && conceptDraft">
                  <div class="wpc-edit-grid">
                    <label class="wpc-edit-field">
                      <span>Название</span>
                      <input v-model="conceptDraft.conceptName" type="text" class="form-control form-control-sm" />
                    </label>
                    <label class="wpc-edit-field">
                      <span>Угол подачи</span>
                      <input v-model="conceptDraft.conceptAngle" type="text" class="form-control form-control-sm" />
                    </label>
                    <label class="wpc-edit-field">
                      <span>Позиционирование</span>
                      <textarea v-model="conceptDraft.positioning" class="form-control form-control-sm" rows="6"></textarea>
                    </label>
                    <label class="wpc-edit-field">
                      <span>Дифференциация</span>
                      <textarea v-model="conceptDraft.differentiation" class="form-control form-control-sm" rows="6"></textarea>
                    </label>
                    <label class="wpc-edit-field">
                      <span>Tone style</span>
                      <input v-model="conceptDraft.toneStyle" type="text" class="form-control form-control-sm" />
                    </label>
                    <label class="wpc-edit-field">
                      <span>Уровень экспертизы</span>
                      <input v-model="conceptDraft.toneExpertiseLevel" type="text" class="form-control form-control-sm" />
                    </label>
                    <label class="wpc-edit-field wpc-edit-field--full">
                      <span>Целевая аудитория (каждая строка — отдельный пункт)</span>
                      <textarea v-model="conceptDraft.targetAudience" class="form-control form-control-sm" rows="6"></textarea>
                    </label>
                    <label class="wpc-edit-field wpc-edit-field--full">
                      <span>Форматы статей (каждая строка — отдельный пункт)</span>
                      <textarea v-model="conceptDraft.articleTypes" class="form-control form-control-sm" rows="6"></textarea>
                    </label>
                    <label class="wpc-edit-field wpc-edit-field--full">
                      <span>Ограничения (каждая строка — отдельный пункт)</span>
                      <textarea v-model="conceptDraft.contentNotAllowed" class="form-control form-control-sm" rows="6"></textarea>
                    </label>
                  </div>
                  <div class="wpc-concept-card__edit-actions">
                    <button class="btn btn-outline-secondary btn-sm" :disabled="isSavingConcept" @click="cancelEditConcept">Отмена</button>
                    <button class="btn btn-primary btn-sm" :disabled="isSavingConcept" @click="saveConceptEdits(concept)">
                      <span v-if="savingConceptId === concept.id" class="spinner-border spinner-border-sm me-1"></span>
                      Сохранить
                    </button>
                  </div>
                </template>
                <template v-else>
                <h6 class="wpc-concept-card__title mb-1">{{ concept.conceptName }}</h6>
                <p class="wpc-concept-card__angle mb-2">{{ concept.conceptAngle || 'Без уточненного угла подачи' }}</p>

                <div class="wpc-info-row">
                  <span class="wpc-info-label">Позиционирование</span>
                  <span class="wpc-info-value">{{ concept.positioning || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Дифференциация</span>
                  <span class="wpc-info-value">{{ concept.differentiation || '—' }}</span>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Тон коммуникации</span>
                  <div class="wpc-tone-subgroup">
                    <span class="wpc-tone-subgroup__label">Стиль</span>
                    <div class="wpc-tags">
                      <span class="wpc-tag" :class="{ 'wpc-tag--muted': !concept.toneStyle }">{{ concept.toneStyle || '—' }}</span>
                    </div>
                  </div>
                  <div class="wpc-tone-subgroup">
                    <span class="wpc-tone-subgroup__label">Уровень экспертности</span>
                    <div class="wpc-tags">
                      <span class="wpc-tag" :class="{ 'wpc-tag--muted': !concept.toneExpertiseLevel }">{{ concept.toneExpertiseLevel || '—' }}</span>
                    </div>
                  </div>
                  <div class="wpc-tone-subgroup">
                    <span class="wpc-tone-subgroup__label">Чего избегать</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in concept.toneAvoid"
                        :key="`tone-avoid-${concept.conceptOrder}-${index}-${item}`"
                        class="wpc-tag wpc-tag--danger"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!concept.toneAvoid.length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                </div>

                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Целевая аудитория</span>
                  <div class="wpc-tags">
                    <span v-for="item in concept.targetAudience" :key="`aud-${concept.conceptOrder}-${item}`" class="wpc-tag">
                      {{ item }}
                    </span>
                    <span v-if="!concept.targetAudience.length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>

                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Форматы статей</span>
                  <div class="wpc-tags">
                    <span v-for="item in concept.articleTypes" :key="`art-${concept.conceptOrder}-${item}`" class="wpc-tag">
                      {{ item }}
                    </span>
                    <span v-if="!concept.articleTypes.length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>

                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Разрешённый контент</span>
                  <div class="wpc-tags">
                    <span v-for="(item, index) in concept.contentAllowed" :key="`allowed-${concept.conceptOrder}-${index}-${item}`" class="wpc-tag">
                      {{ item }}
                    </span>
                    <span v-if="!concept.contentAllowed.length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>

                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Недопустимый контент</span>
                  <div class="wpc-tags">
                    <span v-for="item in concept.contentNotAllowed" :key="`na-${concept.conceptOrder}-${item}`" class="wpc-tag wpc-tag--danger">
                      {{ item }}
                    </span>
                    <span v-if="!concept.contentNotAllowed.length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>

                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Обязательные дисклеймеры</span>
                  <div class="wpc-tags">
                    <span
                      v-for="(item, index) in concept.requiredDisclaimers"
                      :key="`disc-${concept.conceptOrder}-${index}-${item}`"
                      class="wpc-tag wpc-tag--muted"
                    >
                      {{ item }}
                    </span>
                    <span v-if="!concept.requiredDisclaimers.length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>
                </template>
              </article>
            </div>

            <div class="wpc-concepts-actions">
              <div class="wpc-concepts-actions__summary">
                <span class="wpc-concepts-actions__label">Выбрано:</span>
                <strong>{{ selectedConcept?.conceptName || '—' }}</strong>
                <span v-if="hasUnsavedSelection" class="wpc-concepts-actions__pending">не сохранено</span>
              </div>
              <button class="btn btn-primary btn-sm" :disabled="!canProceedStepTwo" @click="goToNextStep">
                <span v-if="isSelectingConcept" class="spinner-border spinner-border-sm me-1"></span>
                {{ isSelectingConcept ? 'Сохраняем выбор...' : 'Следующий шаг' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeStep === 2" class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head d-flex justify-content-between align-items-start gap-2">
              <h6 class="mb-1">Шаг 2. Идентичность</h6>
              <div class="d-flex gap-2">
                <button class="btn btn-primary btn-sm" :disabled="!canGenerateIdentity" @click="requestGenerateIdentity">
                  <span v-if="identityGenerating" class="spinner-border spinner-border-sm me-1"></span>
                  {{ identityGenerating ? 'Формируем...' : 'Разработать' }}
                </button>
                <button class="btn btn-outline-danger btn-sm" :disabled="!canDeleteIdentity" @click="deleteIdentityProfile">
                  <span v-if="identityDeleting" class="spinner-border spinner-border-sm me-1"></span>
                  Удалить
                </button>
              </div>
            </div>

            <div class="wpc-project-meta mb-2">
              <span v-if="false" class="wpc-meta-pill">Проект: <span class="mono">{{ project?.id || '—' }}</span></span>
              <span class="wpc-meta-pill">Концепция: {{ selectedProjectConcept?.conceptName || '—' }}</span>
            </div>

            <div v-if="identityLoading" class="wpc-loading-state">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Загружаем профиль сайта...
            </div>

            <template v-else>
              <div class="wpc-concept-card__head mb-2">
                <span class="wpc-concept-card__order">Профиль сайта</span>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm wpc-edit-btn"
                  :disabled="isIdentityBusy"
                  @click="isEditingIdentity ? cancelEditIdentity() : startEditIdentity()"
                >
                  <i :class="isEditingIdentity ? 'bi bi-x-lg' : 'bi bi-pencil'"></i>
                </button>
              </div>

              <div v-if="isEditingIdentity" class="wpc-edit-grid">
                <label class="wpc-edit-field wpc-edit-field--full">
                  <span>Название сайта</span>
                  <input v-model="identityDraft.siteTitle" type="text" class="form-control form-control-sm" :disabled="isIdentityBusy" />
                </label>
                <label class="wpc-edit-field wpc-edit-field--full">
                  <span>Tagline</span>
                  <input v-model="identityDraft.siteTagline" type="text" class="form-control form-control-sm" :disabled="isIdentityBusy" />
                </label>
                <label class="wpc-edit-field wpc-edit-field--full">
                  <span>Описание главной страницы</span>
                  <textarea v-model="identityDraft.homeDescription" rows="5" class="form-control form-control-sm" :disabled="isIdentityBusy"></textarea>
                </label>
                <div class="wpc-concept-card__edit-actions mb-2">
                  <button class="btn btn-outline-secondary btn-sm" :disabled="identitySaving" @click="cancelEditIdentity">Отмена</button>
                  <button class="btn btn-primary btn-sm" :disabled="isIdentityBusy || !project?.id" @click="saveIdentity">
                    <span v-if="identitySaving" class="spinner-border spinner-border-sm me-1"></span>
                    Сохранить
                  </button>
                </div>
              </div>

              <div v-else class="wpc-identity-fields">
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Название сайта</span>
                  <span class="wpc-info-value">{{ siteIdentity?.siteTitle || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Tagline</span>
                  <span class="wpc-info-value">{{ siteIdentity?.siteTagline || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Описание главной</span>
                  <span class="wpc-info-value">{{ siteIdentity?.homeDescription || '—' }}</span>
                </div>
              </div>

              <div class="wpc-identity-categories">
                <div class="wpc-identity-categories__head">
                  <h6 class="mb-0">Категории</h6>
                </div>

                <div v-if="!identityCategories.length" class="wpc-empty-state mt-2">
                  <i class="bi bi-tags"></i>
                  <p class="mb-0">Категории пока не добавлены.</p>
                </div>

                <div v-else class="wpc-identity-categories__list">
                  <article v-for="category in identityCategories" :key="category.id || category.slug" class="wpc-identity-category">
                    <template v-if="isEditingCategory(category)">
                      <div class="wpc-edit-grid w-100">
                        <label class="wpc-edit-field">
                          <span>Name</span>
                          <input v-model="categoryEditDraft.name" type="text" class="form-control form-control-sm" :disabled="isIdentityBusy" />
                        </label>
                        <label class="wpc-edit-field">
                          <span>Slug</span>
                          <input v-model="categoryEditDraft.slug" type="text" class="form-control form-control-sm" :disabled="isIdentityBusy" />
                        </label>
                        <label class="wpc-edit-field wpc-edit-field--full">
                          <span>Description</span>
                          <textarea v-model="categoryEditDraft.description" rows="3" class="form-control form-control-sm" :disabled="isIdentityBusy"></textarea>
                        </label>
                        <div class="wpc-concept-card__edit-actions">
                          <button class="btn btn-outline-secondary btn-sm" :disabled="identityCategorySaving" @click="cancelEditCategory">Отмена</button>
                          <button class="btn btn-primary btn-sm" :disabled="isIdentityBusy" @click="saveCategoryEdits(category)">
                            <span v-if="identityCategorySaving" class="spinner-border spinner-border-sm me-1"></span>
                            Сохранить
                          </button>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="wpc-identity-category__main">
                        <strong>{{ category.name }}</strong>
                        <span class="wpc-identity-category__slug">{{ category.slug }}</span>
                        <p class="mb-0">{{ category.description || '—' }}</p>
                      </div>
                      <div class="wpc-identity-category__actions">
                        <button
                          type="button"
                          class="btn btn-outline-secondary btn-sm wpc-edit-btn"
                          :disabled="isIdentityBusy"
                          @click="startEditCategory(category)"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm wpc-delete-btn"
                          :disabled="isIdentityBusy"
                          @click="deleteIdentityCategory(category.id)"
                        >
                          <span v-if="deletingCategoryId === category.id" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </template>
                  </article>
                </div>

                <div v-if="isAddingCategory" class="wpc-identity-categories__form">
                  <label class="wpc-edit-field">
                    <span>Name</span>
                    <input v-model="categoryDraft.name" type="text" class="form-control form-control-sm" :disabled="isIdentityBusy || !siteIdentity?.id" />
                  </label>
                  <label class="wpc-edit-field">
                    <span>Slug</span>
                    <input v-model="categoryDraft.slug" type="text" class="form-control form-control-sm" :disabled="isIdentityBusy || !siteIdentity?.id" />
                  </label>
                  <label class="wpc-edit-field wpc-edit-field--full">
                    <span>Description</span>
                    <textarea v-model="categoryDraft.description" rows="3" class="form-control form-control-sm" :disabled="isIdentityBusy || !siteIdentity?.id"></textarea>
                  </label>
                  <div class="wpc-concept-card__edit-actions">
                    <button class="btn btn-outline-secondary btn-sm" :disabled="identityCategorySaving" @click="cancelAddCategory">Отмена</button>
                    <button class="btn btn-outline-primary btn-sm" :disabled="isIdentityBusy || !siteIdentity?.id" @click="addIdentityCategory">
                      <span v-if="identityCategorySaving" class="spinner-border spinner-border-sm me-1"></span>
                      Добавить категорию
                    </button>
                  </div>
                </div>

                <div class="wpc-identity-categories__actions">
                  <button
                    class="btn btn-outline-primary btn-sm"
                    :disabled="isIdentityBusy || !siteIdentity?.id"
                    @click="isAddingCategory ? cancelAddCategory() : startAddCategory()"
                  >
                    {{ isAddingCategory ? 'Отмена' : 'Добавить категорию' }}
                  </button>
                </div>
              </div>
            </template>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(1)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" :disabled="!canOpenStepThree || isIdentityBusy || isEditingIdentity" @click="goToStepThree">
                Следующий шаг
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeStep === 4" class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head d-flex justify-content-between align-items-start gap-2">
              <h6 class="mb-1">Шаг 4. Домен</h6>
              <button class="btn btn-primary btn-sm" :disabled="!canGenerateDomains" @click="requestGenerateDomains">
                <span v-if="generatingDomains" class="spinner-border spinner-border-sm me-1"></span>
                {{ generatingDomains ? 'Формируем...' : 'Подобрать домены' }}
              </button>
            </div>

            <div class="wpc-project-meta mb-2">
              <span v-if="false" class="wpc-meta-pill">Проект: <span class="mono">{{ project?.id || '—' }}</span></span>
              <span class="wpc-meta-pill">Название сайта: {{ localizationData?.site_identity?.siteTitle || '—' }}</span>
              <span class="wpc-meta-pill">Выбранный домен: {{ selectedDomain?.domainName || '—' }}</span>
            </div>

            <div class="wpc-domain-tlds">
              <span class="wpc-tags-label">Предпочтительные зоны</span>
              <div class="wpc-domain-tlds__list">
                <label v-for="tld in DOMAIN_TLD_OPTIONS" :key="tld" class="wpc-domain-tld">
                  <input
                    type="checkbox"
                    :checked="selectedTlds.includes(tld)"
                    :disabled="generatingDomains"
                    @change="togglePreferredTld(tld)"
                  />
                  <span>.{{ tld }}</span>
                </label>
              </div>
            </div>

            <div class="wpc-domain-availability-actions">
              <button class="btn btn-outline-primary btn-sm" :disabled="!canCheckDomainAvailability" @click="checkDomainsAvailability">
                <span v-if="isCheckingDomainAvailability" class="spinner-border spinner-border-sm me-1"></span>
                {{ isCheckingDomainAvailability ? 'Проверяем...' : 'Проверить доступность' }}
              </button>
            </div>
            <div v-if="domainAvailabilityError" class="wpc-domain-availability-error">
              {{ domainAvailabilityError }}
            </div>

            <div v-if="!domains.length" class="wpc-empty-state mt-2">
              <i class="bi bi-globe2"></i>
              <p class="mb-0">Сгенерируйте список доменов и выберите основной домен проекта.</p>
            </div>

            <div v-else class="wpc-domain-list">
              <article
                v-for="domain in domains"
                :key="domain.id || `${domain.domainOrder}-${domain.domainName}`"
                class="wpc-domain-item"
                :class="{ selected: isSelectedDomain(domain), busy: selectingDomainId === domain.id }"
                @click="handleDomainCardClick(domain)"
              >
                <div class="wpc-domain-item__main">
                  <strong>{{ domain.domainName }}</strong>
                  <span v-if="selectingDomainId === domain.id" class="wpc-concepts-actions__pending">
                    <span class="spinner-border spinner-border-sm me-1"></span>
                    выбираем...
                  </span>
                </div>
                <div class="wpc-domain-item__actions">
                  <span class="wpc-domain-status" :class="getDomainAvailabilityBadge(domain).className">
                    {{ getDomainAvailabilityBadge(domain).label }}
                  </span>
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm wpc-delete-btn"
                    :disabled="isSelectingDomain || isDeletingDomain || isCreatingDomain"
                    @click.stop="deleteDomain(domain)"
                  >
                    <span v-if="deletingDomainId === domain.id" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="bi bi-x-lg"></i>
                  </button>
                </div>
              </article>
            </div>

            <div v-if="isAddingDomain" class="wpc-domain-add-form">
              <label class="wpc-edit-field">
                <span>Домен</span>
                <input v-model="newDomainName" type="text" class="form-control form-control-sm" :disabled="isCreatingDomain" placeholder="example.com" />
              </label>
              <div class="wpc-concept-card__edit-actions">
                <button class="btn btn-outline-secondary btn-sm" :disabled="isCreatingDomain" @click="cancelAddDomain">Отмена</button>
                <button class="btn btn-primary btn-sm" :disabled="isCreatingDomain" @click="addDomain">
                  <span v-if="isCreatingDomain" class="spinner-border spinner-border-sm me-1"></span>
                  Добавить
                </button>
              </div>
            </div>

            <div v-if="!isAddingDomain" class="wpc-domain-add-actions">
              <button
                class="btn btn-outline-primary btn-sm"
                :disabled="isSelectingDomain || isDeletingDomain || isCreatingDomain || generatingDomains"
                @click="startAddDomain()"
              >
                Добавить домен
              </button>
            </div>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(3)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" :disabled="!canOpenStepFive || isSelectingDomain || isDeletingDomain || isCreatingDomain || generatingDomains" @click="goToStepFive">
                Следующий шаг
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeStep === 3" class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head d-flex justify-content-between align-items-start gap-2">
              <h6 class="mb-1">Шаг 3. Локализация</h6>
            </div>

            <div v-if="false" class="wpc-config-grid">
              <label class="wpc-field">
                <span class="form-label">Язык локализации</span>
                <select v-model="selectedLocalizationLanguageCode" class="form-select form-select-sm" :disabled="isLocalizationBusy">
                  <option v-for="item in availableLocalizationOptions" :key="item.code" :value="item.code">
                    {{ item.label }}
                  </option>
                </select>
              </label>
            </div>

            <div class="wpc-project-meta mb-2">
              <span class="wpc-meta-pill">Выбран язык проекта:<span class="wpc-meta-pill__value">{{ selectedProjectLocaleLabel }}</span></span>
              <span v-if="resolvedContentMeta.fallbackToSource" class="wpc-meta-pill">fallback to source</span>
            </div>

            <div v-if="false" class="wpc-concepts-actions mb-2">
              <div class="wpc-concepts-actions__summary">
                <strong>{{ viewedLocaleLabel }}</strong>
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-outline-primary btn-sm" :disabled="!canSelectCurrentLocalization" @click="selectCurrentLocalization">
                  <span v-if="selectingLocalizationId" class="spinner-border spinner-border-sm me-1"></span>
                  Выбрать языком проекта
                </button>
                <button class="btn btn-outline-danger btn-sm" :disabled="!canDeleteCurrentLocalization" @click="requestDeleteCurrentLocalization">
                  <span v-if="deletingLocalizationLanguageCode" class="spinner-border spinner-border-sm me-1"></span>
                  Удалить
                </button>
              </div>
            </div>

            <div class="wpc-localization-toolbar mb-2">
              <label class="wpc-field wpc-localization-toolbar__locale">
                <select v-model="selectedLocalizationLanguageCode" class="form-select form-select-sm" :disabled="isLocalizationBusy">
                  <option v-for="item in availableLocalizationOptions" :key="item.code" :value="item.code">
                    {{ item.label }}
                  </option>
                </select>
              </label>
              <button class="btn btn-primary btn-sm" :disabled="!canGenerateLocalization" @click="generateLocalizationByAi">
                <span v-if="localizationGenerating" class="spinner-border spinner-border-sm me-1"></span>
                {{ localizationGenerating ? 'Локализуем...' : 'Создать локализацию' }}
              </button>
              <button class="btn btn-outline-primary btn-sm" :disabled="!canSelectCurrentLocalization" @click="selectCurrentLocalization">
                <span v-if="selectingLocalizationId" class="spinner-border spinner-border-sm me-1"></span>
                Выбрать Языком проекта
              </button>
              <button class="btn btn-outline-danger btn-sm" :disabled="!canDeleteCurrentLocalization" @click="requestDeleteCurrentLocalization">
                <span v-if="deletingLocalizationLanguageCode" class="spinner-border spinner-border-sm me-1"></span>
                Удалить
              </button>
            </div>

            <div v-if="localizationLoading" class="wpc-loading-state mt-2">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Загружаем локализацию...
            </div>

            <template v-else>
              <div v-if="hasLocalizationData" class="wpc-localization-preview mt-2">
                <section class="wpc-localization-panel">
                  <div class="wpc-localization-panel__head">
                    <h6 class="mb-0">Локализованная концепция</h6>
                  </div>
                  <h6 class="wpc-concept-card__title mb-1">{{ localizationData?.selected_concept?.conceptName || '—' }}</h6>
                  <p class="wpc-concept-card__angle mb-2">{{ localizationData?.selected_concept?.conceptAngle || '—' }}</p>
                  <div class="wpc-info-row">
                    <span class="wpc-info-label">Позиционирование</span>
                    <span class="wpc-info-value">{{ localizationData?.selected_concept?.positioning || '—' }}</span>
                  </div>
                  <div class="wpc-info-row">
                    <span class="wpc-info-label">Дифференциация</span>
                    <span class="wpc-info-value">{{ localizationData?.selected_concept?.differentiation || '—' }}</span>
                  </div>
                  <div class="wpc-tags-group">
                    <span class="wpc-tags-label">Тон коммуникации</span>
                    <div class="wpc-tone-subgroup">
                      <span class="wpc-tone-subgroup__label">Стиль</span>
                      <div class="wpc-tags">
                        <span class="wpc-tag" :class="{ 'wpc-tag--muted': !localizationData?.selected_concept?.toneStyle }">
                          {{ localizationData?.selected_concept?.toneStyle || '—' }}
                        </span>
                      </div>
                    </div>
                    <div class="wpc-tone-subgroup">
                      <span class="wpc-tone-subgroup__label">Уровень экспертности</span>
                      <div class="wpc-tags">
                        <span class="wpc-tag" :class="{ 'wpc-tag--muted': !localizationData?.selected_concept?.toneExpertiseLevel }">
                          {{ localizationData?.selected_concept?.toneExpertiseLevel || '—' }}
                        </span>
                      </div>
                    </div>
                    <div class="wpc-tone-subgroup">
                      <span class="wpc-tone-subgroup__label">Чего избегать</span>
                      <div class="wpc-tags">
                        <span
                          v-for="(item, index) in (localizationData?.selected_concept?.toneAvoid || [])"
                          :key="`loc-tone-avoid-${index}-${item}`"
                          class="wpc-tag wpc-tag--danger"
                        >
                          {{ item }}
                        </span>
                        <span v-if="!(localizationData?.selected_concept?.toneAvoid || []).length" class="wpc-tag wpc-tag--muted">—</span>
                      </div>
                    </div>
                  </div>
                  <div class="wpc-tags-group">
                    <span class="wpc-tags-label">Целевая аудитория</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in (localizationData?.selected_concept?.targetAudience || [])"
                        :key="`loc-audience-${index}-${item}`"
                        class="wpc-tag"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!(localizationData?.selected_concept?.targetAudience || []).length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                  <div class="wpc-tags-group">
                    <span class="wpc-tags-label">Форматы статей</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in (localizationData?.selected_concept?.articleTypes || [])"
                        :key="`loc-article-types-${index}-${item}`"
                        class="wpc-tag"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!(localizationData?.selected_concept?.articleTypes || []).length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                  <div class="wpc-tags-group">
                    <span class="wpc-tags-label">Разрешённый контент</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in (localizationData?.selected_concept?.contentAllowed || [])"
                        :key="`loc-content-allowed-${index}-${item}`"
                        class="wpc-tag"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!(localizationData?.selected_concept?.contentAllowed || []).length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                  <div class="wpc-tags-group">
                    <span class="wpc-tags-label">Недопустимый контент</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in (localizationData?.selected_concept?.contentNotAllowed || [])"
                        :key="`loc-content-not-allowed-${index}-${item}`"
                        class="wpc-tag wpc-tag--danger"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!(localizationData?.selected_concept?.contentNotAllowed || []).length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                  <div class="wpc-tags-group">
                    <span class="wpc-tags-label">Обязательные дисклеймеры</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in (localizationData?.selected_concept?.requiredDisclaimers || [])"
                        :key="`loc-required-disclaimers-${index}-${item}`"
                        class="wpc-tag wpc-tag--muted"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!(localizationData?.selected_concept?.requiredDisclaimers || []).length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                </section>

                <section class="wpc-localization-panel">
                  <div class="wpc-localization-panel__head">
                    <h6 class="mb-0">Локализованная идентичность</h6>
                  </div>
                  <div class="wpc-info-row">
                    <span class="wpc-info-label">Название сайта</span>
                    <span class="wpc-info-value">{{ localizationData?.site_identity?.siteTitle || '—' }}</span>
                  </div>
                  <div class="wpc-info-row">
                    <span class="wpc-info-label">Tagline</span>
                    <span class="wpc-info-value">{{ localizationData?.site_identity?.siteTagline || '—' }}</span>
                  </div>
                  <div class="wpc-info-row">
                    <span class="wpc-info-label">Описание главной</span>
                    <span class="wpc-info-value">{{ localizationData?.site_identity?.homeDescription || '—' }}</span>
                  </div>
                  <div class="wpc-identity-categories mt-2">
                    <div class="wpc-identity-categories__head">
                      <h6 class="mb-0">Категории</h6>
                    </div>
                    <div
                      v-if="Array.isArray(localizationData?.site_identity?.categories) && localizationData.site_identity.categories.length"
                      class="wpc-identity-categories__list"
                    >
                      <article
                        v-for="category in localizationData.site_identity.categories"
                        :key="`loc-cat-preview-${category.slug || category.name}`"
                        class="wpc-identity-category"
                      >
                        <div class="wpc-identity-category__main">
                          <strong>{{ category.name || '—' }}</strong>
                          <span class="wpc-identity-category__slug">{{ category.slug || '—' }}</span>
                          <p class="mb-0">{{ category.description || '—' }}</p>
                        </div>
                      </article>
                    </div>
                    <div v-else class="wpc-empty-state mt-2">
                      <p class="mb-0">Категории пока не заданы.</p>
                    </div>
                  </div>
                </section>
              </div>

              <div v-if="false && hasLocalizationData" class="wpc-identity-fields mt-2">
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Concept</span>
                  <span class="wpc-info-value">{{ localizationData?.selected_concept?.conceptName || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Angle</span>
                  <span class="wpc-info-value">{{ localizationData?.selected_concept?.conceptAngle || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Название сайта</span>
                  <span class="wpc-info-value">{{ localizationData?.site_identity?.siteTitle || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Tagline</span>
                  <span class="wpc-info-value">{{ localizationData?.site_identity?.siteTagline || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Описание главной</span>
                  <span class="wpc-info-value">{{ localizationData?.site_identity?.homeDescription || '—' }}</span>
                </div>
              </div>

              <div v-if="false && Array.isArray(localizationData?.site_identity?.categories) && localizationData.site_identity.categories.length" class="wpc-identity-categories mt-2">
                <div class="wpc-identity-categories__head">
                  <h6 class="mb-0">Категории</h6>
                </div>
                <div class="wpc-identity-categories__list">
                  <article
                    v-for="category in localizationData.site_identity.categories"
                    :key="`loc-cat-${category.slug || category.name}`"
                    class="wpc-identity-category"
                  >
                    <div class="wpc-identity-category__main">
                      <strong>{{ category.name || '—' }}</strong>
                      <span class="wpc-identity-category__slug">{{ category.slug || '—' }}</span>
                      <p class="mb-0">{{ category.description || '—' }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div v-if="!hasLocalizationData" class="wpc-empty-state mt-2">
                <i class="bi bi-translate"></i>
                <p class="mb-0">Сначала выполните локализацию на выбранный язык.</p>
              </div>
            </template>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(2)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" :disabled="!canOpenStepFour || isLocalizationBusy" @click="goToStepFour">
                Следующий шаг
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeStep === 5" class="wpc-step-pane">
            <div class="wpc-config-card">
            <div class="wpc-config-card__head">
              <h6 class="mb-1">Шаг 5. Логотип</h6>
            </div>

            <div class="wpc-project-meta mb-2">
              <span class="wpc-meta-pill">Название сайта: {{ localizationData?.site_identity?.siteTitle || '—' }}</span>
              <span class="wpc-meta-pill">Выбранный домен: {{ selectedDomain?.domainName || '—' }}</span>
            </div>

            <div class="wpc-logo-status-row mb-2">
              <span class="wpc-domain-status" :class="logoStatusBadge.className">{{ logoStatusBadge.label }}</span>
            </div>

            <div class="wpc-logo-top-actions mb-2">
              <button class="btn btn-primary btn-sm" :disabled="isLogoBusy || isLogoCompleted || !canOpenStepFive" @click="generateLogo()">
                <span v-if="isLogoGenerating" class="spinner-border spinner-border-sm me-1"></span>
                {{ isLogoGenerating ? 'Запускаем...' : 'Создать логотип' }}
              </button>
              <button
                class="btn btn-outline-primary btn-sm"
                :disabled="isLogoBusy || !canOpenStepFive"
                @click="triggerLogoUploadSelect"
              >
                <span v-if="logoUploading" class="spinner-border spinner-border-sm me-1"></span>
                {{ logoUploading ? 'Загружаем...' : 'Загрузить файл' }}
              </button>
              <input
                ref="logoUploadInputRef"
                type="file"
                class="d-none"
                accept="image/*"
                @change="handleLogoUploadChange"
              />
              <button
                v-if="logoStatus === 'completed' && selectedLogoUrl"
                class="btn btn-outline-primary btn-sm"
                :disabled="isLogoBusy || !canOpenStepFive"
                @click="suggestAnotherLogo"
              >
                Предложить другой
              </button>
              <button
                v-if="logoStatus === 'failed'"
                class="btn btn-outline-primary btn-sm"
                :disabled="isLogoBusy || !canOpenStepFive"
                @click="retryGenerateLogo"
              >
                Повторить
              </button>
            </div>

            <div v-if="logoStatus === 'failed' && logoImage?.errorMessage" class="wpc-domain-availability-error">
              {{ logoImage.errorMessage }}
            </div>

            <div v-if="logoStatus === 'completed' && selectedLogoUrl" class="wpc-logo-preview">
              <img :src="selectedLogoUrl" alt="Сгенерированный логотип" class="wpc-logo-preview__image" />
            </div>

            <div v-if="logoStatus === 'completed' && logoGenerations.length > 1" class="wpc-logo-gallery mt-2">
              <span class="wpc-logo-toolbar__label">Сгенерированные логотипы</span>
              <div class="wpc-logo-gallery__grid">
                <button
                  v-for="gen in logoGenerations"
                  :key="gen.id"
                  type="button"
                  class="wpc-logo-gallery__item"
                  :class="{ active: gen.id === logoImage?.selectedGenerationId }"
                  :disabled="isLogoBusy"
                  :aria-pressed="gen.id === logoImage?.selectedGenerationId"
                  @click="selectLogoGeneration(gen.id)"
                >
                  <img :src="gen.publicUrl" alt="Вариант логотипа" class="wpc-logo-gallery__img" />
                  <span v-if="logoSelectingGeneration === gen.id" class="spinner-border spinner-border-sm wpc-logo-gallery__spinner"></span>
                </button>
              </div>
            </div>

            <div v-if="logoStatus === 'completed' && logoImage?.iconPublicUrl" class="wpc-logo-editor mt-3">
              <span class="wpc-logo-toolbar__label">Редактор логотипа</span>

              <div ref="logoEditorStageRef" class="wpc-logo-editor__stage">
                <div
                  class="wpc-logo-editor__icon-wrap"
                  :class="{ dragging: logoEditorDrag.active && logoEditorDrag.target === 'icon' }"
                  :style="logoEditorIconStyle"
                  @pointerdown.prevent="startLogoEditorDrag($event, 'icon')"
                  @pointermove.prevent="moveLogoEditorDrag"
                  @pointerup.prevent="endLogoEditorDrag"
                  @pointercancel.prevent="endLogoEditorDrag"
                >
                  <img
                    ref="logoEditorIconRef"
                    :src="logoImage.iconPublicUrl"
                    alt="Иконка логотипа"
                    class="wpc-logo-editor__icon"
                    draggable="false"
                    @load="handleLogoEditorIconLoad"
                  />
                  <span
                    class="wpc-logo-editor__handle"
                    title="Потяните, чтобы изменить размер картинки"
                    @pointerdown.stop.prevent="startLogoEditorResize($event, 'icon')"
                  ></span>
                </div>
                <div
                  class="wpc-logo-editor__layer"
                  :class="{ dragging: logoEditorDrag.active && logoEditorDrag.target === 'brand' }"
                  :style="logoEditorBrandStyle"
                  @pointerdown.prevent="startLogoEditorDrag($event, 'brand')"
                  @pointermove.prevent="moveLogoEditorDrag"
                  @pointerup.prevent="endLogoEditorDrag"
                  @pointercancel.prevent="endLogoEditorDrag"
                >{{ logoEditorText || ' ' }}<span
                    class="wpc-logo-editor__handle"
                    title="Потяните, чтобы изменить размер"
                    @pointerdown.stop.prevent="startLogoEditorResize($event, 'brand')"
                  ></span></div>
                <div
                  v-if="logoEditorHasSubtitle"
                  class="wpc-logo-editor__layer"
                  :class="{ dragging: logoEditorDrag.active && logoEditorDrag.target === 'subtitle' }"
                  :style="logoEditorSubtitleLayerStyle"
                  @pointerdown.prevent="startLogoEditorDrag($event, 'subtitle')"
                  @pointermove.prevent="moveLogoEditorDrag"
                  @pointerup.prevent="endLogoEditorDrag"
                  @pointercancel.prevent="endLogoEditorDrag"
                >{{ logoEditorSubtitle || ' ' }}<span
                    class="wpc-logo-editor__handle"
                    title="Потяните, чтобы изменить размер"
                    @pointerdown.stop.prevent="startLogoEditorResize($event, 'subtitle')"
                  ></span></div>
                <button
                  type="button"
                  class="btn btn-primary btn-sm wpc-logo-editor__apply"
                  :disabled="!canApplyLogoEditor"
                  @click="applyLogoEditor"
                >
                  <span v-if="logoEditorApplying" class="spinner-border spinner-border-sm me-1"></span>
                  Применить
                </button>
              </div>

              <div class="wpc-logo-editor__controls mt-2">
                <!-- Brand text layer -->
                <div class="wpc-logo-editor__group">
                  <span class="wpc-logo-editor__group-title">Основной текст</span>
                  <input type="text" class="form-control form-control-sm" v-model="logoEditorText" :disabled="isLogoBusy" placeholder="Текст" />
                  <div class="wpc-logo-editor__row">
                    <div class="wpc-logo-editor__color" @click.stop>
                      <button
                        type="button"
                        class="wpc-logo-editor__color-trigger"
                        :style="{ backgroundColor: logoEditorColor }"
                        :disabled="isLogoBusy"
                        aria-label="Цвет текста"
                        @click="toggleLogoColorPopover('brand')"
                      ></button>
                      <div v-if="logoEditorColorPopover === 'brand'" class="wpc-logo-editor__color-pop">
                        <button
                          v-for="(color, idx) in logoEditorPalette"
                          :key="`brand-${color}-${idx}`"
                          type="button"
                          class="wpc-logo-editor__swatch"
                          :class="{ active: color === logoEditorColor.toLowerCase() }"
                          :style="{ backgroundColor: color }"
                          :aria-label="`Цвет ${color}`"
                          @click="setLogoColor('brand', color)"
                        ></button>
                        <input type="color" class="wpc-logo-editor__color-input" :value="logoEditorColor" aria-label="Свой цвет" @input="setLogoColor('brand', $event.target.value, false)" />
                      </div>
                    </div>
                    <select class="form-select form-select-sm wpc-logo-editor__font-select" v-model="logoEditorFontKey" :disabled="isLogoBusy">
                      <option v-for="font in LOGO_FONT_OPTIONS" :key="font.key" :value="font.key">{{ font.label }}</option>
                    </select>
                  </div>
                </div>

                <!-- Subtitle text layer (independent) -->
                <div class="wpc-logo-editor__group">
                  <span class="wpc-logo-editor__group-title">Подзаголовок</span>
                  <template v-if="logoEditorHasSubtitle">
                    <input type="text" class="form-control form-control-sm" v-model="logoEditorSubtitle" :disabled="isLogoBusy" placeholder="Текст" />
                    <div class="wpc-logo-editor__row">
                      <div class="wpc-logo-editor__color" @click.stop>
                        <button
                          type="button"
                          class="wpc-logo-editor__color-trigger"
                          :style="{ backgroundColor: logoEditorSubtitleColor }"
                          :disabled="isLogoBusy"
                          aria-label="Цвет подзаголовка"
                          @click="toggleLogoColorPopover('subtitle')"
                        ></button>
                        <div v-if="logoEditorColorPopover === 'subtitle'" class="wpc-logo-editor__color-pop">
                          <button
                            v-for="(color, idx) in logoEditorPalette"
                            :key="`sub-${color}-${idx}`"
                            type="button"
                            class="wpc-logo-editor__swatch"
                            :class="{ active: color === logoEditorSubtitleColor.toLowerCase() }"
                            :style="{ backgroundColor: color }"
                            :aria-label="`Цвет ${color}`"
                            @click="setLogoColor('subtitle', color)"
                          ></button>
                          <input type="color" class="wpc-logo-editor__color-input" :value="logoEditorSubtitleColor" aria-label="Свой цвет" @input="setLogoColor('subtitle', $event.target.value, false)" />
                        </div>
                      </div>
                      <select class="form-select form-select-sm wpc-logo-editor__font-select" v-model="logoEditorSubtitleFontKey" :disabled="isLogoBusy">
                        <option v-for="font in LOGO_FONT_OPTIONS" :key="font.key" :value="font.key">{{ font.label }}</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm wpc-logo-editor__group-btn"
                      :disabled="isLogoBusy"
                      @click="removeLogoEditorSubtitle"
                    >Удалить подзаголовок</button>
                  </template>
                  <button
                    v-else
                    type="button"
                    class="btn btn-outline-secondary btn-sm wpc-logo-editor__group-btn"
                    :disabled="isLogoBusy"
                    @click="addLogoEditorSubtitle"
                  >+ добавить подзаголовок</button>
                </div>
              </div>
            </div>

            <div v-if="logoStatus === 'not_started'" class="wpc-empty-state mt-2">
              <i class="bi bi-image"></i>
              <p class="mb-0">Нажмите «Создать логотип», чтобы запустить генерацию.</p>
            </div>

            <div v-else-if="logoStatus === 'queued' || logoStatus === 'processing'" class="wpc-loading-state mt-2">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Генерируем логотип...
            </div>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(4)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" :disabled="!canOpenStepSix || isLogoBusy" @click="goToStepSix">
                Следующий шаг
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeStep === 6" class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head">
              <h6 class="mb-1">Шаг 6. Favicon</h6>
            </div>

            <div class="wpc-project-meta mb-2">
              <span class="wpc-meta-pill">Название сайта: {{ localizationData?.site_identity?.siteTitle || '—' }}</span>
              <span class="wpc-meta-pill">Выбранный домен: {{ selectedDomain?.domainName || '—' }}</span>
            </div>

            <div class="wpc-logo-status-row mb-2">
              <span class="wpc-domain-status" :class="faviconStatusBadge.className">{{ faviconStatusBadge.label }}</span>
            </div>

            <div class="wpc-logo-top-actions mb-2">
              <button class="btn btn-primary btn-sm" :disabled="isFaviconBusy || isFaviconCompleted || !canOpenStepSix" @click="generateFavicon()">
                <span v-if="isFaviconGenerating" class="spinner-border spinner-border-sm me-1"></span>
                {{ isFaviconGenerating ? 'Запускаем...' : 'Создать favicon' }}
              </button>
              <button
                class="btn btn-outline-primary btn-sm"
                :disabled="isFaviconBusy || !canOpenStepSix"
                @click="triggerFaviconUploadSelect"
              >
                <span v-if="faviconUploading" class="spinner-border spinner-border-sm me-1"></span>
                {{ faviconUploading ? 'Загружаем...' : 'Загрузить файл' }}
              </button>
              <input
                ref="faviconUploadInputRef"
                type="file"
                class="d-none"
                accept="image/*"
                @change="handleFaviconUploadChange"
              />
              <button
                v-if="faviconStatus === 'completed' && selectedFaviconUrl"
                class="btn btn-outline-primary btn-sm"
                :disabled="isFaviconBusy || !canOpenStepSix"
                @click="suggestAnotherFavicon"
              >
                Предложить другой
              </button>
              <button
                v-if="faviconStatus === 'failed'"
                class="btn btn-outline-primary btn-sm"
                :disabled="isFaviconBusy || !canOpenStepSix"
                @click="retryGenerateFavicon"
              >
                Повторить
              </button>
            </div>

            <div v-if="faviconStatus === 'failed' && faviconImage?.errorMessage" class="wpc-domain-availability-error">
              {{ faviconImage.errorMessage }}
            </div>

            <div v-if="faviconStatus === 'completed' && selectedFaviconUrl" class="wpc-logo-preview">
              <img :src="selectedFaviconUrl" alt="Сгенерированный favicon" class="wpc-favicon-preview__image" />
            </div>

            <div v-else-if="faviconStatus === 'not_started'" class="wpc-empty-state mt-2">
              <i class="bi bi-image"></i>
              <p class="mb-0">Нажмите «Создать favicon», чтобы запустить генерацию.</p>
            </div>

            <div v-else-if="faviconStatus === 'queued' || faviconStatus === 'processing'" class="wpc-loading-state mt-2">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Генерируем favicon...
            </div>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(5)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" :disabled="!canOpenStepSeven || isFaviconBusy" @click="goToStepSeven">
                Следующий шаг
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeStep === 7" class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head d-flex justify-content-between align-items-start gap-2">
              <h6 class="mb-1">Шаг 7. Шаблон</h6>
              <div class="d-flex gap-2">
                <button class="btn btn-primary btn-sm" :disabled="!siteIdentity?.id || designGenerating" @click="generateDesignByAi">
                  <span v-if="designGenerating" class="spinner-border spinner-border-sm me-1"></span>
                  {{ designGenerating ? 'Создаём...' : (siteDesign ? 'Пересоздать' : 'Создать шаблон') }}
                </button>
                <button v-if="siteDesign && !isEditingDesign" class="btn btn-outline-secondary btn-sm" @click="toggleDesignEdit">
                  Редактировать
                </button>
                <button v-if="isEditingDesign" class="btn btn-outline-secondary btn-sm" @click="toggleDesignEdit">
                  Отмена
                </button>
                <button v-if="isEditingDesign" class="btn btn-primary btn-sm" :disabled="designSaving" @click="saveDesignEdits">
                  <span v-if="designSaving" class="spinner-border spinner-border-sm me-1"></span>
                  Сохранить
                </button>
              </div>
            </div>

            <div class="wpc-project-meta mb-2">
              <span class="wpc-meta-pill">Название сайта: {{ siteIdentity?.siteTitle || '—' }}</span>
            </div>

            <div v-if="designLoading" class="wpc-loading-state">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Загружаем шаблон...
            </div>

            <div v-else-if="!siteDesign" class="wpc-empty-state mt-2">
              <i class="bi bi-palette"></i>
              <p class="mb-0">Нажмите «Создать шаблон», чтобы сгенерировать палитру, шрифт, тексты и политику конфиденциальности.</p>
            </div>

            <template v-else>
              <div v-if="!isEditingDesign" class="wpc-identity-fields">
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Палитра</span>
                  <span class="wpc-info-value">
                    <span
                      v-for="role in designPaletteRoles"
                      :key="role"
                      class="wpc-template-swatch"
                      :style="{ backgroundColor: designView.palette[role] || '#ffffff' }"
                      :title="`${role}: ${designView.palette[role] || '—'}`"
                    ></span>
                  </span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Шрифт</span>
                  <span class="wpc-info-value">{{ designView.fontKey }}<template v-if="designView.googleFontName"> · {{ designView.googleFontName }}</template></span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Метки главной</span>
                  <span class="wpc-info-value">{{ designView.textsSummary }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Политика</span>
                  <span class="wpc-info-value">{{ designView.privacyTitle }} ({{ designView.privacySectionsCount }} разд.)</span>
                </div>
                <ul v-if="designView.privacyHeadings.length" class="wpc-template-privacy">
                  <li v-for="(h, i) in designView.privacyHeadings" :key="i">{{ h }}</li>
                </ul>
              </div>

              <div v-else class="wpc-edit-grid">
                <label v-for="role in designPaletteRoles" :key="role" class="wpc-edit-field">
                  <span>{{ role }}</span>
                  <input type="color" v-model="designDraft.palette[role]" />
                </label>
                <label class="wpc-edit-field wpc-edit-field--full">
                  <span>Шрифт (fontKey)</span>
                  <select v-model="designDraft.typography.fontKey">
                    <option v-for="fk in designFontKeys" :key="fk" :value="fk">{{ fk }}</option>
                  </select>
                </label>
                <label v-for="m in designMicrocopyKeys" :key="m" class="wpc-edit-field wpc-edit-field--full">
                  <span>{{ m }}</span>
                  <input type="text" v-model="designDraft.texts[m]" />
                </label>
                <label class="wpc-edit-field wpc-edit-field--full">
                  <span>Политика — заголовок</span>
                  <input type="text" v-model="designDraft.privacyPolicy.title" />
                </label>
                <div v-for="(sec, idx) in designDraft.privacyPolicy.sections" :key="idx" class="wpc-edit-field wpc-edit-field--full">
                  <span>Раздел {{ idx + 1 }}</span>
                  <input type="text" v-model="sec.heading" placeholder="Заголовок" />
                  <textarea v-model="sec.paragraphsText" rows="3" placeholder="Абзацы (по одному на строку)"></textarea>
                  <textarea v-model="sec.itemsText" rows="2" placeholder="Пункты списка (по одному на строку)"></textarea>
                  <button type="button" class="btn btn-outline-danger btn-sm mt-1" @click="removeDesignSection(idx)">Удалить раздел</button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="addDesignSection">+ Добавить раздел</button>
              </div>
            </template>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(6)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" @click="goToStepEight">Следующий шаг</button>
            </div>
          </div>
        </div>

        <div v-else class="wpc-step-pane">
          <div class="wpc-config-card">
            <div class="wpc-config-card__head">
              <h6 class="mb-1">Шаг 8. Итог</h6>
            </div>

            <div class="wpc-localization-preview wpc-localization-preview--final-assets">
              <section class="wpc-localization-panel wpc-final-asset-panel wpc-final-asset-panel--logo">
                <div class="wpc-localization-panel__head">
                  <h6 class="mb-0">Логотип</h6>
                </div>
                <div v-if="selectedLogoUrl" class="wpc-logo-preview mt-1">
                  <img :src="selectedLogoUrl" alt="Логотип проекта" class="wpc-logo-preview__image" />
                </div>
                <div v-else class="wpc-empty-state mt-1">
                  <p class="mb-0">Логотип не выбран.</p>
                </div>
              </section>

              <section class="wpc-localization-panel wpc-final-asset-panel wpc-final-asset-panel--domain">
                <div class="wpc-localization-panel__head">
                  <h6 class="mb-0">Домен</h6>
                </div>
                <div class="wpc-final-domain-value">{{ selectedDomain?.domainName || '—' }}</div>
              </section>

              <section class="wpc-localization-panel wpc-final-asset-panel wpc-final-asset-panel--favicon">
                <div class="wpc-localization-panel__head">
                  <h6 class="mb-0">Favicon</h6>
                </div>
                <div v-if="selectedFaviconUrl" class="wpc-logo-preview mt-1">
                  <img :src="selectedFaviconUrl" alt="Favicon проекта" class="wpc-favicon-preview__image" />
                </div>
                <div v-else class="wpc-empty-state mt-1">
                  <p class="mb-0">Favicon не выбран.</p>
                </div>
              </section>
            </div>

            <div class="wpc-localization-preview mt-2">
              <section class="wpc-localization-panel">
                <div class="wpc-localization-panel__head">
                  <h6 class="mb-0">Локализованная концепция</h6>
                </div>
                <h6 class="wpc-concept-card__title mb-1">{{ localizationData?.selected_concept?.conceptName || '—' }}</h6>
                <p class="wpc-concept-card__angle mb-2">{{ localizationData?.selected_concept?.conceptAngle || '—' }}</p>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Позиционирование</span>
                  <span class="wpc-info-value">{{ localizationData?.selected_concept?.positioning || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Дифференциация</span>
                  <span class="wpc-info-value">{{ localizationData?.selected_concept?.differentiation || '—' }}</span>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Тон коммуникации</span>
                  <div class="wpc-tone-subgroup">
                    <span class="wpc-tone-subgroup__label">Стиль</span>
                    <div class="wpc-tags">
                      <span class="wpc-tag" :class="{ 'wpc-tag--muted': !localizationData?.selected_concept?.toneStyle }">
                        {{ localizationData?.selected_concept?.toneStyle || '—' }}
                      </span>
                    </div>
                  </div>
                  <div class="wpc-tone-subgroup">
                    <span class="wpc-tone-subgroup__label">Уровень экспертности</span>
                    <div class="wpc-tags">
                      <span class="wpc-tag" :class="{ 'wpc-tag--muted': !localizationData?.selected_concept?.toneExpertiseLevel }">
                        {{ localizationData?.selected_concept?.toneExpertiseLevel || '—' }}
                      </span>
                    </div>
                  </div>
                  <div class="wpc-tone-subgroup">
                    <span class="wpc-tone-subgroup__label">Чего избегать</span>
                    <div class="wpc-tags">
                      <span
                        v-for="(item, index) in (localizationData?.selected_concept?.toneAvoid || [])"
                        :key="`final-loc-tone-avoid-${index}-${item}`"
                        class="wpc-tag wpc-tag--danger"
                      >
                        {{ item }}
                      </span>
                      <span v-if="!(localizationData?.selected_concept?.toneAvoid || []).length" class="wpc-tag wpc-tag--muted">—</span>
                    </div>
                  </div>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Целевая аудитория</span>
                  <div class="wpc-tags">
                    <span
                      v-for="(item, index) in (localizationData?.selected_concept?.targetAudience || [])"
                      :key="`final-loc-audience-${index}-${item}`"
                      class="wpc-tag"
                    >
                      {{ item }}
                    </span>
                    <span v-if="!(localizationData?.selected_concept?.targetAudience || []).length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Форматы статей</span>
                  <div class="wpc-tags">
                    <span
                      v-for="(item, index) in (localizationData?.selected_concept?.articleTypes || [])"
                      :key="`final-loc-article-types-${index}-${item}`"
                      class="wpc-tag"
                    >
                      {{ item }}
                    </span>
                    <span v-if="!(localizationData?.selected_concept?.articleTypes || []).length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Разрешённый контент</span>
                  <div class="wpc-tags">
                    <span
                      v-for="(item, index) in (localizationData?.selected_concept?.contentAllowed || [])"
                      :key="`final-loc-content-allowed-${index}-${item}`"
                      class="wpc-tag"
                    >
                      {{ item }}
                    </span>
                    <span v-if="!(localizationData?.selected_concept?.contentAllowed || []).length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Недопустимый контент</span>
                  <div class="wpc-tags">
                    <span
                      v-for="(item, index) in (localizationData?.selected_concept?.contentNotAllowed || [])"
                      :key="`final-loc-content-not-allowed-${index}-${item}`"
                      class="wpc-tag wpc-tag--danger"
                    >
                      {{ item }}
                    </span>
                    <span v-if="!(localizationData?.selected_concept?.contentNotAllowed || []).length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>
                <div class="wpc-tags-group">
                  <span class="wpc-tags-label">Обязательные дисклеймеры</span>
                  <div class="wpc-tags">
                    <span
                      v-for="(item, index) in (localizationData?.selected_concept?.requiredDisclaimers || [])"
                      :key="`final-loc-required-disclaimers-${index}-${item}`"
                      class="wpc-tag wpc-tag--muted"
                    >
                      {{ item }}
                    </span>
                    <span v-if="!(localizationData?.selected_concept?.requiredDisclaimers || []).length" class="wpc-tag wpc-tag--muted">—</span>
                  </div>
                </div>
              </section>

              <section class="wpc-localization-panel">
                <div class="wpc-localization-panel__head">
                  <h6 class="mb-0">Локализованная идентичность</h6>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Название сайта</span>
                  <span class="wpc-info-value">{{ localizationData?.site_identity?.siteTitle || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Tagline</span>
                  <span class="wpc-info-value">{{ localizationData?.site_identity?.siteTagline || '—' }}</span>
                </div>
                <div class="wpc-info-row">
                  <span class="wpc-info-label">Описание главной</span>
                  <span class="wpc-info-value">{{ localizationData?.site_identity?.homeDescription || '—' }}</span>
                </div>
                <div class="wpc-identity-categories mt-2">
                  <div class="wpc-identity-categories__head">
                    <h6 class="mb-0">Категории</h6>
                  </div>
                  <div
                    v-if="Array.isArray(localizationData?.site_identity?.categories) && localizationData.site_identity.categories.length"
                    class="wpc-identity-categories__list"
                  >
                    <article
                      v-for="category in localizationData.site_identity.categories"
                      :key="`final-loc-cat-preview-${category.slug || category.name}`"
                      class="wpc-identity-category"
                    >
                      <div class="wpc-identity-category__main">
                        <strong>{{ category.name || '—' }}</strong>
                        <span class="wpc-identity-category__slug">{{ category.slug || '—' }}</span>
                        <p class="mb-0">{{ category.description || '—' }}</p>
                      </div>
                    </article>
                  </div>
                  <div v-else class="wpc-empty-state mt-2">
                    <p class="mb-0">Категории пока не заданы.</p>
                  </div>
                </div>
              </section>
            </div>

            <section v-if="siteDesign" class="wpc-localization-panel mt-3">
              <div class="wpc-concept-card__head mb-2">
                <span class="wpc-concept-card__order">Шаблон</span>
              </div>
              <div class="wpc-info-row">
                <span class="wpc-info-label">Палитра</span>
                <span class="wpc-info-value">
                  <span
                    v-for="role in designPaletteRoles"
                    :key="role"
                    class="wpc-template-swatch"
                    :style="{ backgroundColor: designView.palette[role] || '#ffffff' }"
                    :title="`${role}: ${designView.palette[role] || '—'}`"
                  ></span>
                </span>
              </div>
              <div class="wpc-info-row">
                <span class="wpc-info-label">Шрифт</span>
                <span class="wpc-info-value">{{ designView.fontKey }}<template v-if="designView.googleFontName"> · {{ designView.googleFontName }}</template></span>
              </div>
              <div class="wpc-info-row">
                <span class="wpc-info-label">Политика</span>
                <span class="wpc-info-value">{{ designView.privacyTitle }} ({{ designView.privacySectionsCount }} разд.)</span>
              </div>
            </section>

            <div class="mt-3 wpc-step-nav">
              <button class="btn btn-outline-primary btn-sm" @click="openStep(7)">Предыдущий шаг</button>
              <button class="btn btn-primary btn-sm ms-2" @click="finishConceptWizard">
                Завершить
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="showDeleteLocalizationModal" class="wpc-modal-backdrop" @click.self="closeDeleteLocalizationModal">
      <div class="wpc-modal">
        <div class="wpc-modal__head">
          <h6 class="mb-0">Удалить локализацию</h6>
        </div>
        <div class="wpc-modal__body">
          <p class="mb-0">
            Локализация <strong>{{ (localizationDeleteCandidate?.languageCode || '—').toUpperCase() }}</strong> будет удалена без возможности восстановления.
          </p>
        </div>
        <div class="wpc-modal__actions">
          <button class="btn btn-outline-secondary btn-sm" :disabled="Boolean(deletingLocalizationLanguageCode)" @click="closeDeleteLocalizationModal">
            Отмена
          </button>
          <button class="btn btn-danger btn-sm" :disabled="Boolean(deletingLocalizationLanguageCode)" @click="confirmDeleteLocalization">
            <span v-if="Boolean(deletingLocalizationLanguageCode)" class="spinner-border spinner-border-sm me-1"></span>
            Удалить
          </button>
        </div>
      </div>
    </div>

    <div v-if="showIdentityOverwriteModal" class="wpc-modal-backdrop" @click.self="closeIdentityOverwriteModal">
      <div class="wpc-modal">
        <div class="wpc-modal__head">
          <h6 class="mb-0">Перезаписать профиль сайта?</h6>
        </div>
        <div class="wpc-modal__body">
          <div class="wpc-overwrite-warning">
            <span class="wpc-overwrite-warning__label">Важно</span>
            <span>При повторной разработке текущие поля профиля сайта и категории будут полностью заменены.</span>
          </div>
        </div>
        <div class="wpc-modal__actions">
          <button class="btn btn-outline-secondary btn-sm" :disabled="identityGenerating" @click="closeIdentityOverwriteModal">
            Отмена
          </button>
          <button class="btn btn-primary btn-sm" :disabled="identityGenerating" @click="confirmGenerateIdentityOverwrite">
            <span v-if="identityGenerating" class="spinner-border spinner-border-sm me-1"></span>
            Перезаписать
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDomainsOverwriteModal" class="wpc-modal-backdrop" @click.self="closeDomainsOverwriteModal">
      <div class="wpc-modal">
        <div class="wpc-modal__head">
          <h6 class="mb-0">Перезаписать список доменов?</h6>
        </div>
        <div class="wpc-modal__body">
          <div class="wpc-overwrite-warning">
            <span class="wpc-overwrite-warning__label">Важно</span>
            <span>При повторном подборе текущий список доменов будет полностью заменен.</span>
          </div>
        </div>
        <div class="wpc-modal__actions">
          <button class="btn btn-outline-secondary btn-sm" :disabled="generatingDomains" @click="closeDomainsOverwriteModal">
            Отмена
          </button>
          <button class="btn btn-primary btn-sm" :disabled="generatingDomains" @click="confirmGenerateDomainsOverwrite">
            <span v-if="generatingDomains" class="spinner-border spinner-border-sm me-1"></span>
            Перезаписать
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConceptModal" class="wpc-modal-backdrop" @click.self="closeDeleteConceptModal">
      <div class="wpc-modal">
        <div class="wpc-modal__head">
          <h6 class="mb-0">Удалить концепцию</h6>
        </div>
        <div class="wpc-modal__body">
          <p class="mb-0">
            Концепция <strong>{{ conceptDeleteCandidate?.name || '—' }}</strong> будет удалена без возможности восстановления.
          </p>
        </div>
        <div class="wpc-modal__actions">
          <button class="btn btn-outline-secondary btn-sm" :disabled="isDeletingConcept" @click="closeDeleteConceptModal">
            Отмена
          </button>
          <button class="btn btn-danger btn-sm" :disabled="isDeletingConcept" @click="confirmDeleteConcept">
            <span v-if="isDeletingConcept" class="spinner-border spinner-border-sm me-1"></span>
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { resolveAuthHeaderValue } from '@/utils/authToken';
import {
  addSiteProjectDomain,
  addSiteProjectIdentityCategory,
  buildSiteProjectDomainAvailabilityEventsUrl,
  createSiteProjectLocalization,
  createSiteProject,
  createSiteProjectIdentity,
  composeSiteProjectLogo,
  deleteSiteProjectLocalization,
  deleteSiteProjectIdentity,
  deleteSiteProjectIdentityCategory,
  deleteSiteProjectDomain,
  deleteSiteProject,
  deleteSiteProjectConcept,
  executeAiProfile,
  getSiteProjectFavicon,
  getSiteProject,
  getSiteProjectIdentity,
  getSiteProjectLocalization,
  getSiteProjectLogo,
  getSiteProjectResolvedContent,
  getSiteProjects,
  getSiteTopicDictionaryValues,
  generateSiteProjectFavicon,
  generateSiteProjectLogo,
  saveSiteProjectConcepts,
  saveSiteProjectDomains,
  selectSiteProjectLogoGeneration,
  selectSiteProjectLocalization,
  startSiteProjectDomainAvailabilityCheck,
  selectSiteProjectConcept,
  selectSiteProjectDomain,
  uploadSiteProjectFavicon,
  uploadSiteProjectLogo,
  updateSiteProjectIdentity,
  updateSiteProjectDesign,
  getSiteProjectDesign,
  updateSiteProjectConcept,
} from '@/services/wordpressConceptService';

const AI_PROFILE_STEP_ONE_KEY = 'concept_step_1';
const AI_PROFILE_STEP_TWO_KEY = 'concept_step_2';
const AI_PROFILE_DESIGN_KEY = 'design_step';
const designPaletteRoles = ['accent', 'accentDark', 'pageBg', 'surface', 'textStrong', 'textMuted', 'border'];
const designFontKeys = ['SANS_SYSTEM', 'SANS_MODERN', 'SANS_NEWS', 'SANS_GEOMETRIC', 'SANS_PT', 'SERIF_EDITORIAL', 'SERIF_CLASSIC', 'SERIF_DISPLAY'];
const designMicrocopyKeys = ['homeFeaturedLabel', 'homeRecentTitle', 'homePopularTitle', 'homeReadMore'];
const AI_PROFILE_STEP_THREE_KEY = 'concept_step_3';
const AI_PROFILE_STEP_FOUR_KEY = 'concept_step_4';
const DOMAIN_TLD_OPTIONS = ['ru', 'eu', 'us', 'com', 'org', 'net'];
const DOMAIN_TLDS_BY_LOCALE = {
  ru: ['ru'],
  en: ['us', 'com', 'org', 'net'],
  es: ['eu', 'com', 'org', 'net'],
};
const LOCALIZATION_LANGUAGE_OPTIONS = [
  { code: 'ru', label: 'Russian', targetLanguage: 'Russian' },
  { code: 'en', label: 'English', targetLanguage: 'English' },
  { code: 'es', label: 'Spanish', targetLanguage: 'Spanish' },
];

const route = useRoute();
const router = useRouter();

const topicOptions = ref([]);
const topicsLoading = ref(false);
const topicsError = ref('');
const selectedTopicName = ref('');
const projectList = ref([]);
const projectsLoading = ref(false);
const projectsError = ref('');
const deletingProjectId = ref('');
const projectDeleteCandidate = ref(null);

const project = ref(null);
const isLoadingProject = ref(false);
const isGenerating = ref(false);
const isSelectingConcept = ref(false);
const deletingConceptId = ref('');
const savingConceptId = ref('');
const conceptDeleteCandidate = ref(null);
const editingConceptId = ref('');
const conceptDraft = ref(null);
const selectedConceptId = ref('');
const siteIdentity = ref(null);
const identityDraft = ref({
  siteTitle: '',
  siteTagline: '',
  homeDescription: '',
});
const isEditingIdentity = ref(false);
const categoryDraft = ref({
  name: '',
  slug: '',
  description: '',
});
const isAddingCategory = ref(false);
const editingCategoryId = ref('');
const categoryEditDraft = ref({
  name: '',
  slug: '',
  description: '',
});
const selectedTlds = ref(['ru']);
const generatingDomains = ref(false);
const selectingDomainId = ref('');
const deletingDomainId = ref('');
const isAddingDomain = ref(false);
const isCreatingDomain = ref(false);
const newDomainName = ref('');
const isCheckingDomainAvailability = ref(false);
const domainAvailabilityCheckId = ref('');
const domainAvailabilityProcessed = ref(0);
const domainAvailabilityTotal = ref(0);
const domainAvailabilityStatus = ref('');
const domainAvailabilityError = ref('');
const domainAvailabilityByKey = ref({});
const identityLoading = ref(false);
const identitySaving = ref(false);
const identityGenerating = ref(false);
const designGenerating = ref(false);
const siteDesign = ref(null);
const isEditingDesign = ref(false);
const designSaving = ref(false);
const designLoading = ref(false);
const designDraft = ref(null);
const identityDeleting = ref(false);
const identityCategorySaving = ref(false);
const deletingCategoryId = ref('');
const showIdentityOverwriteModal = ref(false);
const showDomainsOverwriteModal = ref(false);
const activeStep = ref(1);
const selectedLocalizationLanguageCode = ref('ru');
const projectDefaultLocaleCode = ref('ru');
const localizationData = ref(null);
const localizationRecord = ref(null);
const resolvedContentMeta = ref({
  localizationId: null,
  requestedLocale: null,
  resolvedLocale: '',
  sourceLocale: '',
  localized: false,
  fallbackToSource: false,
  stale: false,
});
const localizationLoading = ref(false);
const localizationGenerating = ref(false);
const selectingLocalizationId = ref('');
const deletingLocalizationLanguageCode = ref('');
const localizationDeleteCandidate = ref(null);
const logoImage = ref(null);
const logoLoading = ref(false);
const logoGenerating = ref(false);
const logoPolling = ref(false);
const logoDeleting = ref(false);
const logoUploading = ref(false);
const logoSelectingGeneration = ref('');
const logoUploadInputRef = ref(null);
const logoEditorIconRef = ref(null);
const logoEditorIconScale = ref(1);
const logoEditorIconOffset = ref({ x: 0, y: 0 });
const logoEditorStageRef = ref(null);
const logoEditorStageSize = ref({ width: 0, height: 0 });
// Rendered-px origin that maps icon-space (0,0); recomputed to center content on load/apply/resize.
const logoEditorOrigin = ref({ x: 16, y: 16 });
const logoEditorIconNatural = ref({ width: 0, height: 0 });
// Brand text layer.
const logoEditorText = ref('');
const logoEditorFontKey = ref('pt-sans');
const logoEditorColor = ref('#222222');
const logoEditorFontSize = ref(200);
const logoEditorOffset = ref({ x: 0, y: 0 });
// Independent subtitle text layer (own font/color/size/position).
const logoEditorHasSubtitle = ref(false);
const logoEditorSubtitle = ref('');
const logoEditorSubtitleFontKey = ref('pt-sans');
const logoEditorSubtitleColor = ref('#222222');
const logoEditorSubtitleFontSize = ref(110);
const logoEditorSubtitleOffset = ref({ x: 0, y: 0 });
const logoEditorApplying = ref(false);
const logoEditorColorPopover = ref(''); // '' | 'brand' | 'subtitle'
// active: move/resize a layer; mode: 'move' | 'resize'.
const logoEditorDrag = ref({ active: false, pointerId: null, target: '', mode: 'move', anchorX: 0, anchorY: 0, startX: 0, startY: 0, startSize: 0, startDist: 0 });
const EDITOR_ICON_DISPLAY_PX = 150;
const EDITOR_STAGE_PAD = 16;
const EDITOR_MIN_FONT = 12;
const EDITOR_MAX_FONT = 600;
// weight mirrors the server-bundled TTFs (all bold except Russo One, which ships regular).
const LOGO_FONT_OPTIONS = [
  { key: 'pt-sans', label: 'PT Sans', css: "'PT Sans', sans-serif", weight: 700 },
  { key: 'pt-serif', label: 'PT Serif', css: "'PT Serif', serif", weight: 700 },
  { key: 'philosopher', label: 'Philosopher', css: "'Philosopher', sans-serif", weight: 700 },
  { key: 'play', label: 'Play', css: "'Play', sans-serif", weight: 700 },
  { key: 'russo-one', label: 'Russo One', css: "'Russo One', sans-serif", weight: 400 },
];
const LOGO_FONT_KEYS = LOGO_FONT_OPTIONS.map(font => font.key);
// Per-font line metrics (ascent+descent as a fraction of em), measured once fonts have loaded.
// Used to cancel the CSS half-leading so the editor baseline matches the server render exactly.
const logoFontMetricsVersion = ref(0);
const logoFontMetricsCache = {};
let logoFontMetricsCanvas = null;

const faviconImage = ref(null);
const faviconLoading = ref(false);
const faviconGenerating = ref(false);
const faviconPolling = ref(false);
const faviconUploading = ref(false);
const faviconUploadInputRef = ref(null);

const uiError = ref('');
const infoMessage = ref('');
const INFO_MESSAGE_TIMEOUT_MS = 4000;
const LOGO_POLL_INTERVAL_MS = 2500;
const FAVICON_POLL_INTERVAL_MS = 2500;
let infoMessageTimeoutId = null;
let domainAvailabilityAbortController = null;
let logoPollingTimeoutId = null;
let faviconPollingTimeoutId = null;

const concepts = computed(() => project.value?.concepts || []);
const hasConcepts = computed(() => concepts.value.length > 0);
const isDeletingConcept = computed(() => Boolean(deletingConceptId.value));
const isSavingConcept = computed(() => Boolean(savingConceptId.value));
const isEditingAnyConcept = computed(() => Boolean(editingConceptId.value));
const showDeleteProjectModal = computed(() => Boolean(projectDeleteCandidate.value?.id));
const showDeleteConceptModal = computed(() => Boolean(conceptDeleteCandidate.value?.id));
const showDeleteLocalizationModal = computed(() => Boolean(localizationDeleteCandidate.value?.id));
const canOpenStepTwo = computed(() => Boolean(project.value?.selectedConceptId));
const canOpenStepThree = computed(() => Boolean(project.value?.id && siteIdentity.value?.id));
const projectSourceLocale = computed(() => normalizeString(project.value?.sourceLocale || project.value?.languageCode).toLowerCase());
const availableLocalizationOptions = computed(() => {
  const allowed = toStringArray(project.value?.targetLocales || project.value?.target_locales)
    .map(item => normalizeString(item).toLowerCase())
    .filter(Boolean);
  const fallbackLocale = 'ru';

  const configuredOptions = LOCALIZATION_LANGUAGE_OPTIONS
    .filter(item => item.code !== fallbackLocale)
    .filter(item => !allowed.length || allowed.includes(item.code));

  const fallbackOption = LOCALIZATION_LANGUAGE_OPTIONS.find(item => item.code === fallbackLocale)
    || { code: fallbackLocale, label: 'Russian', targetLanguage: 'Russian' };

  if (!allowed.length) {
    const unique = new Map([[fallbackOption.code, fallbackOption]]);
    configuredOptions.forEach(item => unique.set(item.code, item));
    return [...unique.values()];
  }

  const mappedAllowed = allowed.map(code => {
    const known = LOCALIZATION_LANGUAGE_OPTIONS.find(item => item.code === code);
    return known || { code, label: code.toUpperCase(), targetLanguage: code.toUpperCase() };
  });
  const unique = new Map([[fallbackOption.code, fallbackOption]]);
  mappedAllowed.forEach(item => unique.set(item.code, item));
  return [...unique.values()];
});
const selectedLocalizationOption = computed(() => {
  const selectedCode = normalizeString(selectedLocalizationLanguageCode.value).toLowerCase();
  return availableLocalizationOptions.value.find(item => item.code === selectedCode) || availableLocalizationOptions.value[0] || null;
});
const viewedLocaleCode = computed(() => normalizeString(selectedLocalizationLanguageCode.value).toLowerCase());
const viewedLocaleLabel = computed(() => {
  const code = viewedLocaleCode.value;
  if (!code) return '—';
  const known = LOCALIZATION_LANGUAGE_OPTIONS.find(item => item.code === code);
  return known?.label || code.toUpperCase();
});
const hasLocalizationData = computed(() => Boolean(
  localizationData.value?.selected_concept?.conceptName
  || localizationData.value?.site_identity?.siteTitle
  || localizationData.value?.site_identity?.siteTagline
  || localizationData.value?.site_identity?.homeDescription
  || localizationData.value?.site_identity?.categories?.length
));
const isCurrentLocalizationSelected = computed(() => {
  const selectedLocalId = normalizeString(project.value?.selectedLocalId);
  const localId = normalizeString(localizationRecord.value?.id);
  return Boolean(selectedLocalId && localId && selectedLocalId === localId);
});
const canSelectCurrentLocalization = computed(
  () => Boolean(project.value?.id && localizationRecord.value?.id)
    && !selectingLocalizationId.value
    && !isCurrentLocalizationSelected.value
);
const canDeleteCurrentLocalization = computed(
  () => Boolean(project.value?.id && localizationRecord.value?.id && localizationRecord.value?.languageCode)
    && !deletingLocalizationLanguageCode.value
    && !selectingLocalizationId.value
);
const canOpenStepFour = computed(
  () => Boolean(
    canOpenStepThree.value
    && hasLocalizationData.value
    && normalizeString(project.value?.selectedLocalId)
  )
);
const canOpenStepFive = computed(() => Boolean(canOpenStepFour.value && project.value?.selectedDomainId));
const canOpenStepSix = computed(() => Boolean(canOpenStepFive.value && isLogoCompleted.value));
const canOpenStepSeven = computed(() => Boolean(canOpenStepSix.value && isFaviconCompleted.value));
const canOpenStepEight = computed(() => Boolean(canOpenStepSeven.value));
const isTemplateCompleted = computed(() => Boolean(siteDesign.value));
const isWizardComplete = computed(() => Boolean(canOpenStepSeven.value && siteDesign.value));
const workingStep = computed(() => {
  if (canOpenStepSeven.value) return siteDesign.value ? 0 : 7;
  if (canOpenStepSix.value) return 6;
  if (canOpenStepFive.value) return 5;
  if (canOpenStepFour.value) return 4;
  if (canOpenStepThree.value) return 3;
  if (canOpenStepTwo.value) return 2;
  return 1;
});
const selectedDefaultLocaleCode = computed(() => normalizeString(projectDefaultLocaleCode.value || projectSourceLocale.value || 'ru').toLowerCase());
const selectedDefaultLocaleLabel = computed(() => {
  const code = normalizeString(selectedDefaultLocaleCode.value).toLowerCase();
  if (!code) return '—';
  const known = LOCALIZATION_LANGUAGE_OPTIONS.find(item => item.code === code);
  return known?.label || code.toUpperCase();
});
const selectedProjectLocaleLabel = computed(() => {
  const selectedLocalId = normalizeString(project.value?.selectedLocalId);
  if (!selectedLocalId) return 'Не выбран';
  return selectedDefaultLocaleLabel.value;
});
const selectedConcept = computed(
  () => concepts.value.find(item => normalizeString(item?.id) === normalizeString(selectedConceptId.value)) || null
);
const selectedProjectConcept = computed(
  () => concepts.value.find(item => normalizeString(item?.id) === normalizeString(project.value?.selectedConceptId)) || selectedConcept.value || null
);
const hasUnsavedSelection = computed(
  () => normalizeString(project.value?.selectedConceptId) !== normalizeString(selectedConceptId.value)
);
const canProceedStepTwo = computed(
  () => Boolean(project.value?.id && selectedConceptId.value) && !isSelectingConcept.value && !isDeletingConcept.value && !isSavingConcept.value && !isEditingAnyConcept.value
);
const hasProjectList = computed(() => projectList.value.length > 0);
const isProjectsMode = computed(() => route.name === 'WordPressConceptProjects');
const identityCategories = computed(() => siteIdentity.value?.categories || []);
const domains = computed(() => project.value?.domains || []);
const hasUncheckedDomains = computed(() => domains.value.some(domain => {
  const badge = getDomainAvailabilityBadge(domain);
  return badge.className === 'wpc-domain-status--unknown';
}));
const hasExistingDomains = computed(() => domains.value.length > 0);
const selectedDomain = computed(
  () => domains.value.find(item => normalizeString(item?.id) === normalizeString(project.value?.selectedDomainId)) || null
);
const isDeletingCategory = computed(() => Boolean(deletingCategoryId.value));
const isSelectingDomain = computed(() => Boolean(selectingDomainId.value));
const isDeletingDomain = computed(() => Boolean(deletingDomainId.value));
const hasExistingIdentity = computed(
  () => Boolean(
    siteIdentity.value?.id
    || siteIdentity.value?.siteTitle
    || siteIdentity.value?.siteTagline
    || siteIdentity.value?.homeDescription
    || identityCategories.value.length
  )
);
const isIdentityBusy = computed(
  () => identityLoading.value
    || identitySaving.value
    || identityGenerating.value
    || identityDeleting.value
    || identityCategorySaving.value
    || isDeletingCategory.value
);
const canGenerateIdentity = computed(
  () => Boolean(project.value?.id && selectedProjectConcept.value)
    && !hasExistingIdentity.value
    && !isIdentityBusy.value
    && !isLoadingProject.value
    && !isEditingIdentity.value
    && !normalizeString(editingCategoryId.value)
    && !isAddingCategory.value
);
const canDeleteIdentity = computed(
  () => Boolean(project.value?.id && hasExistingIdentity.value)
    && !isIdentityBusy.value
    && !isLoadingProject.value
    && !isEditingIdentity.value
    && !normalizeString(editingCategoryId.value)
    && !isAddingCategory.value
);
const canGenerateDomains = computed(
  () => Boolean(project.value?.id && siteIdentity.value?.id && selectedTlds.value.length)
    && !isLoadingProject.value
    && !generatingDomains.value
    && !isSelectingDomain.value
    && !isDeletingDomain.value
    && !isCreatingDomain.value
    && !isAddingDomain.value
);
const canCheckDomainAvailability = computed(
  () => Boolean(project.value?.id && domains.value.length && hasUncheckedDomains.value)
    && !isCheckingDomainAvailability.value
    && !isLoadingProject.value
    && !generatingDomains.value
    && !isAddingDomain.value
    && !isSelectingDomain.value
    && !isDeletingDomain.value
    && !isCreatingDomain.value
);
const hasLocalizationForViewedLanguage = computed(() => {
  const viewedCode = normalizeString(selectedLocalizationLanguageCode.value).toLowerCase();
  const recordId = normalizeString(localizationRecord.value?.id);
  const recordCode = normalizeString(localizationRecord.value?.languageCode).toLowerCase();
  return Boolean(viewedCode && recordId && recordCode && recordCode === viewedCode);
});
const canGenerateLocalization = computed(
  () => Boolean(project.value?.id && selectedProjectConcept.value && siteIdentity.value && selectedLocalizationOption.value)
    && !hasLocalizationForViewedLanguage.value
    && !isLoadingProject.value
    && !localizationLoading.value
    && !localizationGenerating.value
);
const selectedLocaleForDomains = computed(() => {
  const resolvedLocale = normalizeString(resolvedContentMeta.value?.resolvedLocale).toLowerCase();
  if (resolvedLocale) return resolvedLocale;
  const localizationLocale = normalizeString(localizationRecord.value?.languageCode).toLowerCase();
  if (localizationLocale) return localizationLocale;
  return normalizeString(selectedLocalizationLanguageCode.value).toLowerCase() || 'ru';
});
const isLocalizationBusy = computed(
  () => localizationLoading.value
    || localizationGenerating.value
    || Boolean(selectingLocalizationId.value)
    || Boolean(deletingLocalizationLanguageCode.value)
);
const logoStatus = computed(() => normalizeString(logoImage.value?.status || 'not_started').toLowerCase() || 'not_started');
const isLogoGenerating = computed(() => logoGenerating.value);
const isLogoBusy = computed(
  () => logoLoading.value
    || logoGenerating.value
    || logoPolling.value
    || logoDeleting.value
    || logoUploading.value
    || logoEditorApplying.value
    || Boolean(logoSelectingGeneration.value)
);
const logoGenerations = computed(() => (Array.isArray(logoImage.value?.generations) ? logoImage.value.generations : []));
const logoEditorScale = computed(() => {
  const naturalWidth = Number(logoEditorIconNatural.value.width) || 0;
  if (!naturalWidth) return 0.2;
  return EDITOR_ICON_DISPLAY_PX / naturalWidth;
});
const logoFontOption = key => LOGO_FONT_OPTIONS.find(font => font.key === key) || LOGO_FONT_OPTIONS[0];
const fontCssForKey = key => logoFontOption(key).css;

// Measure a font's ascent+descent as a fraction of em (cached once the webfont is loaded).
const measureLogoFontRatios = key => {
  const option = logoFontOption(key);
  const cacheKey = `${option.weight} ${option.css}`;
  if (logoFontMetricsCache[cacheKey]) return logoFontMetricsCache[cacheKey];
  let ratios = { ascent: 0.8, descent: 0.2 };
  try {
    if (!logoFontMetricsCanvas) logoFontMetricsCanvas = document.createElement('canvas');
    const ctx = logoFontMetricsCanvas.getContext('2d');
    const size = 200;
    const fontSpec = `${option.weight} ${size}px ${option.css}`;
    ctx.font = fontSpec;
    const m = ctx.measureText('Бháy');
    const asc = m.fontBoundingBoxAscent;
    const desc = m.fontBoundingBoxDescent;
    if (Number.isFinite(asc) && Number.isFinite(desc) && asc > 0) {
      ratios = { ascent: asc / size, descent: desc / size };
      // Only cache when the real webfont is active, so we don't lock in fallback metrics.
      if (!document.fonts || document.fonts.check(fontSpec)) {
        logoFontMetricsCache[cacheKey] = ratios;
      }
    }
  } catch (e) {
    // keep the conservative default ratios
  }
  return ratios;
};

// Pixels to add to a layer's CSS top so its baseline lands where the server draws it.
// CSS line-height:1 centers (ascent+descent) inside 1em, adding half-leading; cancel it.
const logoLayerLeadPx = (key, fontSizePx) => {
  logoFontMetricsVersion.value; // reactive dependency: re-measure after fonts load
  const r = measureLogoFontRatios(key);
  return fontSizePx * (r.ascent + r.descent - 1) / 2;
};

// Text width in icon-space units (font size given in icon-space px).
const measureLogoTextWidth = (key, fontSizeIconSpace, text) => {
  if (!text || fontSizeIconSpace <= 0) return 0;
  try {
    if (!logoFontMetricsCanvas) logoFontMetricsCanvas = document.createElement('canvas');
    const ctx = logoFontMetricsCanvas.getContext('2d');
    const option = logoFontOption(key);
    ctx.font = `${option.weight} ${fontSizeIconSpace}px ${option.css}`;
    return ctx.measureText(text).width;
  } catch (e) {
    return text.length * fontSizeIconSpace * 0.55;
  }
};

// Bounding box (icon-space) of the icon plus visible text layers.
const computeLogoEditorBounds = () => {
  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  let has = false;
  const consider = (x, y, w, h) => {
    if (!has) {
      minX = x; minY = y; maxX = x + w; maxY = y + h; has = true;
    } else {
      minX = Math.min(minX, x); minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w); maxY = Math.max(maxY, y + h);
    }
  };
  const iconScale = Number(logoEditorIconScale.value) || 1;
  const iconW = (Number(logoEditorIconNatural.value.width) || 0) * iconScale;
  const iconH = (Number(logoEditorIconNatural.value.height) || 0) * iconScale;
  if (iconW && iconH) {
    consider(Number(logoEditorIconOffset.value.x) || 0, Number(logoEditorIconOffset.value.y) || 0, iconW, iconH);
  }

  const brand = normalizeString(logoEditorText.value);
  if (brand) {
    const fs = Number(logoEditorFontSize.value) || 0;
    const r = measureLogoFontRatios(logoEditorFontKey.value);
    consider(Number(logoEditorOffset.value.x) || 0, Number(logoEditorOffset.value.y) || 0,
      measureLogoTextWidth(logoEditorFontKey.value, fs, brand), fs * (r.ascent + r.descent));
  }
  if (logoEditorHasSubtitle.value) {
    const sub = normalizeString(logoEditorSubtitle.value);
    if (sub) {
      const fs = Number(logoEditorSubtitleFontSize.value) || 0;
      const r = measureLogoFontRatios(logoEditorSubtitleFontKey.value);
      consider(Number(logoEditorSubtitleOffset.value.x) || 0, Number(logoEditorSubtitleOffset.value.y) || 0,
        measureLogoTextWidth(logoEditorSubtitleFontKey.value, fs, sub), fs * (r.ascent + r.descent));
    }
  }
  return has ? { minX, minY, maxX, maxY } : { minX: 0, minY: 0, maxX: 0, maxY: 0 };
};

const updateLogoEditorStageSize = () => {
  const el = logoEditorStageRef.value;
  if (!el) return;
  logoEditorStageSize.value = { width: Number(el.clientWidth) || 0, height: Number(el.clientHeight) || 0 };
};

// Recompute the origin so the content is left-aligned (small indent) and vertically centered.
// Called on load/apply/resize — NOT during a drag, so moving one element never shifts the others.
const recenterLogoEditor = () => {
  updateLogoEditorStageSize();
  const scale = logoEditorScale.value || 0.2;
  const b = computeLogoEditorBounds();
  const contentH = (b.maxY - b.minY) * scale;
  const stageH = Number(logoEditorStageSize.value.height) || 0;
  const baseX = EDITOR_STAGE_PAD;
  const baseY = stageH ? (stageH - contentH) / 2 : EDITOR_STAGE_PAD;
  logoEditorOrigin.value = {
    x: baseX - b.minX * scale,
    y: baseY - b.minY * scale,
  };
};

const logoEditorPalette = computed(() => {
  const fromImage = Array.isArray(logoImage.value?.logoPalette)
    ? logoImage.value.logoPalette.map(normalizeHexColor).filter(Boolean)
    : [];
  const base = fromImage.length ? fromImage : ['#1a73e8', '#0f9d58', '#d93025'];
  return Array.from(new Set([...base, '#222222'].map(color => color.toLowerCase())));
});
const logoEditorIconStyle = computed(() => {
  const scale = logoEditorScale.value || 0.2;
  return {
    width: `${EDITOR_ICON_DISPLAY_PX * (Number(logoEditorIconScale.value) || 1)}px`,
    left: `${logoEditorOrigin.value.x + (Number(logoEditorIconOffset.value.x) || 0) * scale}px`,
    top: `${logoEditorOrigin.value.y + (Number(logoEditorIconOffset.value.y) || 0) * scale}px`,
  };
});
const logoEditorBrandStyle = computed(() => {
  const scale = logoEditorScale.value || 0.2;
  const fontSizePx = Math.max(6, (Number(logoEditorFontSize.value) || 0) * scale);
  return {
    left: `${logoEditorOrigin.value.x + (Number(logoEditorOffset.value.x) || 0) * scale}px`,
    top: `${logoEditorOrigin.value.y + (Number(logoEditorOffset.value.y) || 0) * scale + logoLayerLeadPx(logoEditorFontKey.value, fontSizePx)}px`,
    fontFamily: fontCssForKey(logoEditorFontKey.value),
    fontWeight: logoFontOption(logoEditorFontKey.value).weight,
    fontSize: `${fontSizePx}px`,
    color: logoEditorColor.value,
  };
});
const logoEditorSubtitleLayerStyle = computed(() => {
  const scale = logoEditorScale.value || 0.2;
  const fontSizePx = Math.max(5, (Number(logoEditorSubtitleFontSize.value) || 0) * scale);
  return {
    left: `${logoEditorOrigin.value.x + (Number(logoEditorSubtitleOffset.value.x) || 0) * scale}px`,
    top: `${logoEditorOrigin.value.y + (Number(logoEditorSubtitleOffset.value.y) || 0) * scale + logoLayerLeadPx(logoEditorSubtitleFontKey.value, fontSizePx)}px`,
    fontFamily: fontCssForKey(logoEditorSubtitleFontKey.value),
    fontWeight: logoFontOption(logoEditorSubtitleFontKey.value).weight,
    fontSize: `${fontSizePx}px`,
    color: logoEditorSubtitleColor.value,
  };
});
const canApplyLogoEditor = computed(() => Boolean(
  logoStatus.value === 'completed'
  && normalizeString(logoImage.value?.iconPublicUrl)
  && !isLogoBusy.value
));
const selectedLogoUrl = computed(() => normalizeString(logoImage.value?.selectedPublicUrl || logoImage.value?.publicUrl));
const isLogoCompleted = computed(() => logoStatus.value === 'completed' && Boolean(selectedLogoUrl.value));
const logoStatusBadge = computed(() => {
  if (logoStatus.value === 'completed') return { className: 'wpc-domain-status--available', label: 'готово' };
  if (logoStatus.value === 'failed') return { className: 'wpc-domain-status--error', label: 'ошибка' };
  if (logoStatus.value === 'queued') return { className: 'wpc-domain-status--unknown', label: 'в очереди' };
  if (logoStatus.value === 'processing') return { className: 'wpc-domain-status--unknown', label: 'генерация' };
  return { className: 'wpc-domain-status--unknown', label: 'не запущено' };
});
const logoPromptLanguage = computed(() => {
  const localeCode = normalizeString(
    resolvedContentMeta.value?.resolvedLocale
    || localizationRecord.value?.languageCode
    || selectedLocalizationLanguageCode.value
  ).toLowerCase();
  if (!localeCode) return '';
  const known = LOCALIZATION_LANGUAGE_OPTIONS.find(item => item.code === localeCode);
  return normalizeString(known?.targetLanguage || known?.label || localeCode.toUpperCase());
});
const faviconStatus = computed(() => normalizeString(faviconImage.value?.status || 'not_started').toLowerCase() || 'not_started');
const isFaviconGenerating = computed(() => faviconGenerating.value);
const isFaviconBusy = computed(() => faviconLoading.value || faviconGenerating.value || faviconPolling.value || faviconUploading.value);
const selectedFaviconUrl = computed(() => {
  const explicit = normalizeString(faviconImage.value?.selectedPublicUrl);
  if (explicit) return explicit;
  return normalizeString(faviconImage.value?.publicUrl);
});
const isFaviconCompleted = computed(() => faviconStatus.value === 'completed' && Boolean(selectedFaviconUrl.value));
const faviconStatusBadge = computed(() => {
  if (faviconStatus.value === 'completed') return { className: 'wpc-domain-status--available', label: 'готово' };
  if (faviconStatus.value === 'failed') return { className: 'wpc-domain-status--error', label: 'ошибка' };
  if (faviconStatus.value === 'queued') return { className: 'wpc-domain-status--unknown', label: 'в очереди' };
  if (faviconStatus.value === 'processing') return { className: 'wpc-domain-status--unknown', label: 'генерация' };
  return { className: 'wpc-domain-status--unknown', label: 'не запущено' };
});

const requestGenerateDomains = async () => {
  if (!canGenerateDomains.value) return;
  if (hasExistingDomains.value) {
    showDomainsOverwriteModal.value = true;
    return;
  }
  await generateDomainsByAi();
};

const requestGenerateIdentity = async () => {
  if (!canGenerateIdentity.value) return;
  await generateIdentityByAi();
};

const deleteIdentityProfile = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || !canDeleteIdentity.value) return;

  identityDeleting.value = true;
  clearMessages();
  try {
    await deleteSiteProfileForProject(projectId);
    await loadProjects();
    infoMessage.value = 'Идентичность сайта удалена.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    identityDeleting.value = false;
  }
};

const normalizeString = value => String(value == null ? '' : value).trim();
const currentProjectId = computed(() => normalizeString(route.params.projectId));

const toNullableString = value => {
  const normalized = normalizeString(value);
  return normalized || null;
};

const toArray = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split(',').map(normalizeString).filter(Boolean);
  return [];
};

const toStringArray = value => toArray(value).map(normalizeString).filter(Boolean);
const toMultilineText = value => toStringArray(value).join('\n');
const multilineToArray = value => String(value == null ? '' : value).split('\n').map(normalizeString).filter(Boolean);

const toPlainObject = value => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

const extractError = error => {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) return responseData.trim();
  if (typeof responseData?.message === 'string' && responseData.message.trim()) return responseData.message.trim();
  if (Array.isArray(responseData?.errors) && responseData.errors.length) {
    return responseData.errors.map(normalizeString).filter(Boolean).join(', ');
  }
  return normalizeString(error?.message) || 'Не удалось выполнить запрос.';
};

const normalizeTopicOption = value => {
  if (typeof value === 'string') {
    const normalized = normalizeString(value);
    if (!normalized) return null;
    return { value: normalized, label: normalized, code: normalized, sortOrder: Number.POSITIVE_INFINITY };
  }

  if (!value || typeof value !== 'object') return null;

  const optionRu = normalizeString(value.valueRu || value.valueRU || value.ru || value.nameRu);
  const optionValue = optionRu || normalizeString(value.value || value.code || value.key || value.name || value.label || value.topicName);
  if (!optionValue) return null;

  const optionLabel = optionRu || normalizeString(value.label || value.name || value.title || optionValue) || optionValue;
  const code = normalizeString(value.valueCode || value.value_code || value.code || value.key || optionValue);
  const sortOrderRaw = Number(value.sortOrder);
  const sortOrder = Number.isFinite(sortOrderRaw) ? sortOrderRaw : Number.POSITIVE_INFINITY;

  return { value: optionValue, label: optionLabel, code, sortOrder };
};

const normalizeConcept = (concept, index = 0) => {
  const toneOfVoice = toPlainObject(concept?.toneOfVoice || concept?.tone_of_voice);
  const contentStrategy = toPlainObject(concept?.contentStrategy || concept?.content_strategy);
  const contentBoundaries = toPlainObject(concept?.contentBoundaries || concept?.content_boundaries);

  const conceptOrderRaw = Number(concept?.conceptOrder ?? concept?.concept_order);
  const conceptOrder = Number.isFinite(conceptOrderRaw) && conceptOrderRaw > 0 ? Math.round(conceptOrderRaw) : index + 1;

  return {
    id: toNullableString(concept?.id || concept?.conceptId || concept?.concept_id),
    conceptOrder,
    conceptName: normalizeString(concept?.conceptName || concept?.concept_name || concept?.name) || `Концепция ${conceptOrder}`,
    conceptAngle: normalizeString(concept?.conceptAngle || concept?.concept_angle),
    positioning: normalizeString(concept?.positioning),
    differentiation: normalizeString(concept?.differentiation),
    targetAudience: toStringArray(concept?.targetAudience || concept?.target_audience),
    toneStyle: normalizeString(concept?.toneStyle || concept?.tone_style || toneOfVoice.style),
    toneExpertiseLevel: normalizeString(concept?.toneExpertiseLevel || concept?.tone_expertise_level || toneOfVoice.expertiseLevel || toneOfVoice.expertise_level),
    toneAvoid: toStringArray(concept?.toneAvoid || concept?.tone_avoid || toneOfVoice.avoid),
    articleTypes: toStringArray(concept?.articleTypes || concept?.article_types || contentStrategy.articleTypes || contentStrategy.article_types),
    contentAllowed: toStringArray(concept?.contentAllowed || concept?.content_allowed || contentBoundaries.allowed),
    contentNotAllowed: toStringArray(concept?.contentNotAllowed || concept?.content_not_allowed || contentBoundaries.notAllowed || contentBoundaries.not_allowed),
    requiredDisclaimers: toStringArray(concept?.requiredDisclaimers || concept?.required_disclaimers || contentBoundaries.requiredDisclaimers || contentBoundaries.required_disclaimers),
  };
};

const normalizeDomain = (domain, index = 0) => {
  const orderRaw = Number(domain?.domainOrder ?? domain?.domain_order);
  const domainOrder = Number.isFinite(orderRaw) && orderRaw >= 1 ? Math.round(orderRaw) : index + 1;
  return {
    id: toNullableString(domain?.id),
    domainOrder,
    domainName: normalizeString(domain?.domainName || domain?.domain_name).toLowerCase(),
    isAvailable: typeof domain?.isAvailable === 'boolean'
      ? domain.isAvailable
      : (typeof domain?.is_available === 'boolean' ? domain.is_available : null),
    createdAt: toNullableString(domain?.createdAt || domain?.created_at),
    updatedAt: toNullableString(domain?.updatedAt || domain?.updated_at),
  };
};

const normalizeProject = payload => {
  const source = toPlainObject(payload);
  const conceptsList = Array.isArray(source.concepts) ? source.concepts.map((item, index) => normalizeConcept(item, index)) : [];
  const domainsList = Array.isArray(source.domains) ? source.domains.map((item, index) => normalizeDomain(item, index)) : [];
  const sourceLocale = toNullableString(source.sourceLocale || source.source_locale || source.languageCode || source.language_code);
  const targetLocales = toStringArray(source.targetLocales || source.target_locales)
    .map(item => normalizeString(item).toLowerCase())
    .filter(Boolean);

  return {
    ...source,
    id: normalizeString(source.id),
    topicName: normalizeString(source.topicName || source.topic_name),
    languageCode: toNullableString(source.languageCode || source.language_code) || sourceLocale,
    sourceLocale,
    targetLocales,
    status: normalizeString(source.status),
    selectedConceptId: toNullableString(source.selectedConceptId || source.selected_concept_id),
    selectedDomainId: toNullableString(source.selectedDomainId || source.selected_domain_id),
    selectedLocalId: toNullableString(source.selectedLocalId || source.selected_local_id),
    createdAt: toNullableString(source.createdAt || source.created_at),
    updatedAt: toNullableString(source.updatedAt || source.updated_at),
    concepts: conceptsList,
    domains: domainsList.sort((a, b) => Number(a.domainOrder) - Number(b.domainOrder)),
  };
};

const normalizeIdentityCategory = payload => {
  const source = toPlainObject(payload);
  return {
    id: toNullableString(source.id),
    name: normalizeString(source.name),
    slug: normalizeString(source.slug),
    description: normalizeString(source.description),
    createdAt: toNullableString(source.createdAt || source.created_at),
    updatedAt: toNullableString(source.updatedAt || source.updated_at),
  };
};

const normalizeSiteIdentity = payload => {
  const source = toPlainObject(payload);
  const categories = Array.isArray(source.categories) ? source.categories.map(item => normalizeIdentityCategory(item)) : [];
  return {
    id: toNullableString(source.id),
    siteTitle: normalizeString(source.siteTitle || source.site_title),
    siteTagline: normalizeString(source.siteTagline || source.site_tagline),
    homeDescription: normalizeString(source.homeDescription || source.home_description),
    createdAt: toNullableString(source.createdAt || source.created_at),
    updatedAt: toNullableString(source.updatedAt || source.updated_at),
    categories: categories.filter(item => item.name || item.slug || item.description),
  };
};

const syncIdentityDraft = identity => {
  identityDraft.value = {
    siteTitle: normalizeString(identity?.siteTitle),
    siteTagline: normalizeString(identity?.siteTagline),
    homeDescription: normalizeString(identity?.homeDescription),
  };
};

const setIdentity = payload => {
  const normalized = normalizeSiteIdentity(payload);
  siteIdentity.value = normalized.id || normalized.siteTitle || normalized.siteTagline || normalized.homeDescription || normalized.categories.length
    ? normalized
    : null;
  syncIdentityDraft(siteIdentity.value);
};

const clearIdentityState = () => {
  siteIdentity.value = null;
  localizationData.value = null;
  localizationRecord.value = null;
  selectingLocalizationId.value = '';
  deletingLocalizationLanguageCode.value = '';
  localizationDeleteCandidate.value = null;
  setResolvedContentMeta({});
  syncIdentityDraft(null);
  isEditingIdentity.value = false;
  isAddingCategory.value = false;
  editingCategoryId.value = '';
  categoryEditDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
  categoryDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
  showIdentityOverwriteModal.value = false;
};

const deleteSiteProfileForProject = async projectId => {
  const normalizedProjectId = normalizeString(projectId);
  if (!normalizedProjectId) return false;
  try {
    await deleteSiteProjectIdentity(normalizedProjectId);
    clearIdentityState();
    return true;
  } catch (error) {
    if (Number(error?.response?.status) === 404) {
      clearIdentityState();
      return false;
    }
    throw error;
  }
};

const createCategoryDraft = category => ({
  name: normalizeString(category?.name),
  slug: normalizeString(category?.slug),
  description: normalizeString(category?.description),
});

const isEditingCategory = category => normalizeString(category?.id || category?.slug) === normalizeString(editingCategoryId.value);

const formatArrayForPrompt = items => toStringArray(items).join(', ');

const formatSelectedConceptForPrompt = concept => {
  if (!concept) return '';
  const fragments = [];
  if (concept.conceptName) fragments.push(`Название: ${concept.conceptName}`);
  if (concept.conceptAngle) fragments.push(`Угол: ${concept.conceptAngle}`);
  if (concept.positioning) fragments.push(`Позиционирование: ${concept.positioning}`);
  if (concept.differentiation) fragments.push(`Дифференциация: ${concept.differentiation}`);
  if (concept.toneStyle || concept.toneExpertiseLevel) {
    fragments.push(`Tone of voice: ${[concept.toneStyle, concept.toneExpertiseLevel].filter(Boolean).join(', ')}`);
  }
  if (concept.targetAudience?.length) fragments.push(`Целевая аудитория: ${formatArrayForPrompt(concept.targetAudience)}`);
  if (concept.articleTypes?.length) fragments.push(`Форматы статей: ${formatArrayForPrompt(concept.articleTypes)}`);
  if (concept.contentNotAllowed?.length) fragments.push(`Ограничения: ${formatArrayForPrompt(concept.contentNotAllowed)}`);
  return fragments.join('; ').replace(/\s+/g, ' ').trim();
};

const selectedConceptPromptLine = computed(() => formatSelectedConceptForPrompt(selectedProjectConcept.value));

const formatSiteIdentityForPrompt = identity => {
  if (!identity) return '';
  const fragments = [];
  if (identity.siteTitle) fragments.push(`Название: ${normalizeString(identity.siteTitle)}`);
  if (identity.siteTagline) fragments.push(`Tagline: ${normalizeString(identity.siteTagline)}`);
  if (identity.homeDescription) fragments.push(`Описание: ${normalizeString(identity.homeDescription)}`);
  if (Array.isArray(identity.categories) && identity.categories.length) {
    // В prompt для шага 3 передаем только имена категорий (без slug/description).
    const categoryNames = identity.categories
      .map(item => normalizeString(item?.name))
      .filter(Boolean)
      .join(', ');
    if (categoryNames) fragments.push(`Категории: ${categoryNames}`);
  }
  return fragments.join('; ').replace(/\s+/g, ' ').trim();
};

const localizedSiteIdentityPromptLine = computed(() => {
  const localizedIdentity = toPlainObject(localizationData.value?.site_identity);
  const hasLocalizedIdentity = Boolean(
    normalizeString(localizedIdentity.siteTitle)
    || normalizeString(localizedIdentity.siteTagline)
    || normalizeString(localizedIdentity.homeDescription)
    || (Array.isArray(localizedIdentity.categories) && localizedIdentity.categories.length)
  );
  return hasLocalizedIdentity
    ? formatSiteIdentityForPrompt(localizedIdentity)
    : formatSiteIdentityForPrompt(siteIdentity.value);
});

const isSelectedDomain = domain => normalizeString(domain?.id) === normalizeString(project.value?.selectedDomainId);

const domainLike = candidate => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;
  const keys = Object.keys(candidate).map(key => key.toLowerCase());
  return keys.includes('domain')
    || keys.includes('domainname')
    || keys.includes('domain_name')
    || keys.includes('name');
};

const normalizeDomainNameCandidate = value => normalizeString(value).toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');

const isValidDomainName = value => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(normalizeString(value));

const extractDomainsFromAiPayload = payload => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const source = toPlainObject(payload);
  const knownKeys = ['domains', 'domain_list', 'domainList', 'items', 'variants'];
  for (const key of knownKeys) {
    if (Array.isArray(source[key])) return source[key];
  }

  const queue = [source];
  const visited = new Set();
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) continue;
    visited.add(current);

    for (const value of Object.values(current)) {
      if (Array.isArray(value) && value.length) {
        if (value.some(item => typeof item === 'string' || domainLike(item))) return value;
      } else if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }
  return [];
};

const mapAiDomainsToDto = payload => {
  const seen = new Set();
  const mapped = [];
  const raw = extractDomainsFromAiPayload(payload);

  raw.forEach((item, index) => {
    let domainName = '';
    if (typeof item === 'string') {
      domainName = normalizeDomainNameCandidate(item);
    } else if (item && typeof item === 'object') {
      const source = toPlainObject(item);
      domainName = normalizeDomainNameCandidate(source.domainName || source.domain_name || source.domain || source.name || source.value);
    }
    if (!domainName || !isValidDomainName(domainName)) return;
    const key = domainName.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    mapped.push({
      domainOrder: index + 1,
      domainName,
    });
  });

  return mapped
    .slice(0, 20)
    .map((item, index) => ({ domainOrder: index + 1, domainName: item.domainName }));
};

const getPreferredTldsByLocale = localeCode => {
  const code = normalizeString(localeCode).toLowerCase();
  const defaults = DOMAIN_TLDS_BY_LOCALE[code] || DOMAIN_TLDS_BY_LOCALE.ru;
  return defaults.filter(tld => DOMAIN_TLD_OPTIONS.includes(tld));
};

const applyPreferredTldsByLocale = localeCode => {
  const next = getPreferredTldsByLocale(localeCode);
  selectedTlds.value = next.length ? next : ['ru'];
};

const togglePreferredTld = tld => {
  const normalizedTld = normalizeString(tld).toLowerCase();
  if (!normalizedTld) return;
  const next = new Set(selectedTlds.value.map(item => normalizeString(item).toLowerCase()).filter(Boolean));
  if (next.has(normalizedTld)) next.delete(normalizedTld);
  else next.add(normalizedTld);
  if (!next.size) next.add('ru');
  selectedTlds.value = DOMAIN_TLD_OPTIONS.filter(item => next.has(item));
};

const startAddDomain = () => {
  if (isSelectingDomain.value || isDeletingDomain.value || isCreatingDomain.value || generatingDomains.value) return;
  isAddingDomain.value = true;
  newDomainName.value = '';
  uiError.value = '';
};

const cancelAddDomain = () => {
  if (isCreatingDomain.value) return;
  isAddingDomain.value = false;
  newDomainName.value = '';
};

const domainKeyForAvailability = (domainId, domainName) => {
  const id = normalizeString(domainId);
  if (id) return `id:${id}`;
  const name = normalizeDomainNameCandidate(domainName);
  if (!name) return '';
  return `name:${name}`;
};

const closeDomainAvailabilityStream = () => {
  if (!domainAvailabilityAbortController) return;
  domainAvailabilityAbortController.abort();
  domainAvailabilityAbortController = null;
};

const clearDomainAvailabilityState = () => {
  closeDomainAvailabilityStream();
  isCheckingDomainAvailability.value = false;
  domainAvailabilityCheckId.value = '';
  domainAvailabilityProcessed.value = 0;
  domainAvailabilityTotal.value = 0;
  domainAvailabilityStatus.value = '';
  domainAvailabilityError.value = '';
  domainAvailabilityByKey.value = {};
};

const setDomainAvailabilityEntry = payload => {
  const domainId = normalizeString(payload?.domainId);
  const domainName = normalizeDomainNameCandidate(payload?.domainName);
  const keyById = domainKeyForAvailability(domainId, '');
  const keyByName = domainKeyForAvailability('', domainName);
  const nextValue = {
    isAvailable: typeof payload?.isAvailable === 'boolean' ? payload.isAvailable : null,
    result: normalizeString(payload?.result).toLowerCase(),
    source: normalizeString(payload?.source).toLowerCase(),
    details: normalizeString(payload?.details),
    checkedAt: normalizeString(payload?.checkedAt),
  };
  const nextMap = { ...domainAvailabilityByKey.value };
  if (keyById) nextMap[keyById] = nextValue;
  if (keyByName) nextMap[keyByName] = nextValue;
  domainAvailabilityByKey.value = nextMap;
};

const getDomainAvailabilityEntry = domain => {
  const keyById = domainKeyForAvailability(domain?.id, '');
  const keyByName = domainKeyForAvailability('', domain?.domainName);
  return domainAvailabilityByKey.value[keyById] || domainAvailabilityByKey.value[keyByName] || null;
};

const getDomainAvailabilityBadge = domain => {
  const entry = getDomainAvailabilityEntry(domain);
  if (entry) {
    if (entry.result === 'available' || entry.isAvailable === true) return { className: 'wpc-domain-status--available', label: 'доступен' };
    if (entry.result === 'unavailable' || entry.result === 'taken' || entry.isAvailable === false) return { className: 'wpc-domain-status--unavailable', label: 'занят' };
    if (entry.result === 'error') return { className: 'wpc-domain-status--error', label: 'ошибка' };
  }
  if (typeof domain?.isAvailable === 'boolean') {
    return domain.isAvailable
      ? { className: 'wpc-domain-status--available', label: 'доступен' }
      : { className: 'wpc-domain-status--unavailable', label: 'занят' };
  }
  return { className: 'wpc-domain-status--unknown', label: 'не проверен' };
};

const parseAvailabilityEventPayload = (eventType, rawData) => {
  try {
    const parsed = JSON.parse(rawData || '{}');
    if (!parsed.type) parsed.type = eventType;
    return parsed;
  } catch {
    return {
      type: eventType,
      details: normalizeString(rawData),
      checkedAt: new Date().toISOString(),
    };
  }
};

const processAvailabilityEvent = payload => {
  const eventType = normalizeString(payload?.type).toLowerCase();
  if (eventType === 'domain_checked') {
    setDomainAvailabilityEntry(payload);
    domainAvailabilityStatus.value = normalizeString(payload?.result || 'running');
    domainAvailabilityProcessed.value = Number(payload?.processed) || domainAvailabilityProcessed.value;
    domainAvailabilityTotal.value = Number(payload?.total) || domainAvailabilityTotal.value;
    return;
  }
  if (eventType === 'completed') {
    domainAvailabilityStatus.value = normalizeString(payload?.result || 'completed');
    domainAvailabilityProcessed.value = Number(payload?.processed) || domainAvailabilityProcessed.value;
    domainAvailabilityTotal.value = Number(payload?.total) || domainAvailabilityTotal.value;
    isCheckingDomainAvailability.value = false;
    closeDomainAvailabilityStream();
  }
};

const openDomainAvailabilityEvents = async (projectId, checkId) => {
  closeDomainAvailabilityStream();
  const url = buildSiteProjectDomainAvailabilityEventsUrl(projectId, checkId);
  const controller = new AbortController();
  domainAvailabilityAbortController = controller;

  try {
    const authHeader = resolveAuthHeaderValue();
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`SSE request failed with status ${response.status}`);
    }
    if (!response.body) {
      throw new Error('SSE stream has no body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let eventName = '';
    let dataLines = [];

    const flushEvent = () => {
      if (!dataLines.length) {
        eventName = '';
        return;
      }
      const type = normalizeString(eventName || 'message').toLowerCase();
      const payload = parseAvailabilityEventPayload(type, dataLines.join('\n'));
      processAvailabilityEvent(payload);
      eventName = '';
      dataLines = [];
    };

    let done = false;
    while (!done) {
      const chunk = await reader.read();
      done = Boolean(chunk?.done);
      if (done) break;

      buffer += decoder.decode(chunk.value, { stream: true });
      let newlineIndex = buffer.indexOf('\n');
      while (newlineIndex >= 0) {
        let line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        if (line.endsWith('\r')) line = line.slice(0, -1);

        if (line === '') {
          flushEvent();
        } else if (!line.startsWith(':')) {
          const sepIndex = line.indexOf(':');
          const field = sepIndex < 0 ? line : line.slice(0, sepIndex);
          let value = sepIndex < 0 ? '' : line.slice(sepIndex + 1);
          if (value.startsWith(' ')) value = value.slice(1);
          if (field === 'event') eventName = value;
          if (field === 'data') dataLines.push(value);
        }
        newlineIndex = buffer.indexOf('\n');
      }
    }

    if (buffer) {
      let tail = buffer;
      if (tail.endsWith('\r')) tail = tail.slice(0, -1);
      if (tail) dataLines.push(tail);
    }
    flushEvent();
  } finally {
    if (domainAvailabilityAbortController === controller) {
      domainAvailabilityAbortController = null;
    }
  }
};

const checkDomainsAvailability = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || !canCheckDomainAvailability.value) return;

  clearMessages();
  closeDomainAvailabilityStream();
  domainAvailabilityCheckId.value = '';
  domainAvailabilityProcessed.value = 0;
  domainAvailabilityTotal.value = 0;
  domainAvailabilityStatus.value = '';
  domainAvailabilityError.value = '';
  isCheckingDomainAvailability.value = true;

  const payloadDomains = domains.value
    .filter(item => getDomainAvailabilityBadge(item).className === 'wpc-domain-status--unknown')
    .map(item => ({
      domainId: normalizeString(item?.id) || null,
      domainName: normalizeDomainNameCandidate(item?.domainName),
    }))
    .filter(item => item.domainName);

  if (!payloadDomains.length) {
    isCheckingDomainAvailability.value = false;
    uiError.value = 'Нет доменов для проверки.';
    return;
  }

  const initialMap = {};
  payloadDomains.forEach(item => {
    const keyById = domainKeyForAvailability(item.domainId, '');
    const keyByName = domainKeyForAvailability('', item.domainName);
    const initialEntry = {
      isAvailable: null,
      result: 'checking',
      source: '',
      details: '',
      checkedAt: '',
    };
    if (keyById) initialMap[keyById] = initialEntry;
    if (keyByName) initialMap[keyByName] = initialEntry;
  });
  domainAvailabilityByKey.value = initialMap;

  try {
    const started = await startSiteProjectDomainAvailabilityCheck(projectId, { domains: payloadDomains });
    const checkId = normalizeString(started?.checkId);
    if (!checkId) throw new Error('Бэкенд не вернул checkId проверки доменов.');

    domainAvailabilityCheckId.value = checkId;
    domainAvailabilityStatus.value = normalizeString(started?.status || 'started');
    domainAvailabilityProcessed.value = 0;
    domainAvailabilityTotal.value = Number(started?.total) || payloadDomains.length;
    infoMessage.value = 'Проверка доступности доменов запущена.';

    openDomainAvailabilityEvents(projectId, checkId).catch(error => {
      if (error?.name === 'AbortError') return;
      domainAvailabilityError.value = normalizeString(error?.message) || 'Не удалось подключиться к SSE проверки доменов.';
      isCheckingDomainAvailability.value = false;
    });
  } catch (error) {
    isCheckingDomainAvailability.value = false;
    uiError.value = extractError(error);
  }
};

const formatProjectStatus = value => {
  const status = normalizeString(value);
  return status ? status.toUpperCase() : '—';
};

const formatDateTime = value => {
  const raw = normalizeString(value);
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
};

const formatTargetLocales = value => {
  const locales = toStringArray(value).map(item => normalizeString(item).toLowerCase()).filter(Boolean);
  return locales.length ? locales.join(', ') : '—';
};

const formatLocaleLabel = code => {
  const normalizedCode = normalizeString(code).toLowerCase();
  if (!normalizedCode) return 'Не выбран';
  const known = LOCALIZATION_LANGUAGE_OPTIONS.find(item => item.code === normalizedCode);
  return known?.label || normalizedCode.toUpperCase();
};

const extractLocalizedSiteTitle = payload => {
  const source = toPlainObject(payload);
  const identity = toPlainObject(source.identity || source.site_identity);
  return normalizeString(identity.siteTitle || identity.site_title);
};

const toTime = value => {
  const raw = normalizeString(value);
  if (!raw) return 0;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const clearMessages = () => {
  uiError.value = '';
  infoMessage.value = '';
};

const clearInfoMessageTimer = () => {
  if (infoMessageTimeoutId) {
    clearTimeout(infoMessageTimeoutId);
    infoMessageTimeoutId = null;
  }
};

const stopLogoPolling = () => {
  if (logoPollingTimeoutId) {
    clearTimeout(logoPollingTimeoutId);
    logoPollingTimeoutId = null;
  }
  logoPolling.value = false;
};

const clearLogoState = () => {
  stopLogoPolling();
  logoImage.value = null;
  logoLoading.value = false;
  logoGenerating.value = false;
  logoDeleting.value = false;
  logoUploading.value = false;
};

const stopFaviconPolling = () => {
  if (faviconPollingTimeoutId) {
    clearTimeout(faviconPollingTimeoutId);
    faviconPollingTimeoutId = null;
  }
  faviconPolling.value = false;
};

const clearFaviconState = () => {
  stopFaviconPolling();
  faviconImage.value = null;
  faviconLoading.value = false;
  faviconGenerating.value = false;
  faviconUploading.value = false;
};

const resetWizard = () => {
  clearDomainAvailabilityState();
  clearLogoState();
  clearFaviconState();
  clearFaviconState();
  project.value = null;
  siteIdentity.value = null;
  identityDraft.value = {
    siteTitle: '',
    siteTagline: '',
    homeDescription: '',
  };
  isEditingIdentity.value = false;
  categoryDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
  isAddingCategory.value = false;
  editingCategoryId.value = '';
  categoryEditDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
  selectedTlds.value = ['ru'];
  generatingDomains.value = false;
  selectingDomainId.value = '';
  deletingDomainId.value = '';
  isAddingDomain.value = false;
  isCreatingDomain.value = false;
  newDomainName.value = '';
  showDomainsOverwriteModal.value = false;
  deletingProjectId.value = '';
  projectDeleteCandidate.value = null;
  deletingConceptId.value = '';
  savingConceptId.value = '';
  identitySaving.value = false;
  identityLoading.value = false;
  identityGenerating.value = false;
  identityDeleting.value = false;
  identityCategorySaving.value = false;
  deletingCategoryId.value = '';
  showIdentityOverwriteModal.value = false;
  selectedLocalizationLanguageCode.value = 'ru';
  projectDefaultLocaleCode.value = 'ru';
  localizationData.value = null;
  localizationRecord.value = null;
  selectingLocalizationId.value = '';
  deletingLocalizationLanguageCode.value = '';
  localizationDeleteCandidate.value = null;
  setResolvedContentMeta({});
  localizationLoading.value = false;
  localizationGenerating.value = false;
  logoLoading.value = false;
  logoGenerating.value = false;
  faviconLoading.value = false;
  faviconGenerating.value = false;
  conceptDeleteCandidate.value = null;
  editingConceptId.value = '';
  conceptDraft.value = null;
  selectedConceptId.value = '';
  activeStep.value = 1;
  clearMessages();
};

const tryParseJson = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const parseJsonFromCodeFence = text => {
  const matches = text.match(/```(?:json)?\s*[\s\S]*?```/gi) || [];
  for (const block of matches) {
    const cleaned = block.replace(/^```(?:json)?\s*/i, '').replace(/```$/, '').trim();
    const parsed = tryParseJson(cleaned);
    if (parsed !== null) return parsed;
  }
  return null;
};

const parseJsonFromSlice = text => {
  const objectStart = text.indexOf('{');
  const objectEnd = text.lastIndexOf('}');
  if (objectStart !== -1 && objectEnd > objectStart) {
    const parsedObject = tryParseJson(text.slice(objectStart, objectEnd + 1));
    if (parsedObject !== null) return parsedObject;
  }

  const arrayStart = text.indexOf('[');
  const arrayEnd = text.lastIndexOf(']');
  if (arrayStart !== -1 && arrayEnd > arrayStart) {
    return tryParseJson(text.slice(arrayStart, arrayEnd + 1));
  }

  return null;
};

const parseAiPayload = content => {
  if (content && typeof content === 'object') {
    const objectContent = toPlainObject(content);
    const nestedContent = objectContent.content;
    if (nestedContent && nestedContent !== content) {
      return parseAiPayload(nestedContent);
    }
    return objectContent;
  }
  const text = normalizeString(content);
  if (!text) throw new Error('AI вернул пустой content.');

  const direct = tryParseJson(text);
  if (direct !== null) return direct;

  const fenced = parseJsonFromCodeFence(text);
  if (fenced !== null) return fenced;

  const sliced = parseJsonFromSlice(text);
  if (sliced !== null) return sliced;

  throw new Error('Не удалось распарсить JSON с концепциями из ответа AI.');
};

const conceptLike = candidate => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;
  const keys = Object.keys(candidate).map(key => key.toLowerCase());
  return keys.includes('concept_name')
    || keys.includes('conceptname')
    || keys.includes('concept_angle')
    || keys.includes('tone_of_voice')
    || keys.includes('content_strategy');
};

const extractConceptsArray = payload => {
  if (Array.isArray(payload)) {
    return payload.filter(item => item && typeof item === 'object' && !Array.isArray(item));
  }

  const source = toPlainObject(payload);
  const knownKeys = ['concepts', 'site_concepts', 'wordpress_concepts', 'items', 'variants'];
  for (const key of knownKeys) {
    if (Array.isArray(source[key])) {
      return source[key].filter(item => item && typeof item === 'object' && !Array.isArray(item));
    }
  }

  const queue = [source];
  const visited = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) continue;
    visited.add(current);

    for (const value of Object.values(current)) {
      if (Array.isArray(value)) {
        const objectItems = value.filter(item => item && typeof item === 'object' && !Array.isArray(item));
        if (!objectItems.length) continue;
        if (objectItems.some(conceptLike)) return objectItems;
      } else if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return [];
};

const mapAiConceptToSaveDto = (concept, index) => {
  const source = toPlainObject(concept);
  const toneOfVoice = toPlainObject(source.tone_of_voice || source.toneOfVoice);
  const contentStrategy = toPlainObject(source.content_strategy || source.contentStrategy);
  const contentBoundaries = toPlainObject(source.content_boundaries || source.contentBoundaries);

  const conceptOrder = index + 1;
  const conceptName = normalizeString(source.concept_name || source.conceptName || source.name) || `Концепция ${conceptOrder}`;

  return {
    conceptOrder,
    conceptName,
    conceptAngle: normalizeString(source.concept_angle || source.conceptAngle),
    positioning: normalizeString(source.positioning),
    differentiation: normalizeString(source.differentiation),
    targetAudience: toStringArray(source.target_audience || source.targetAudience),
    toneStyle: normalizeString(toneOfVoice.style || source.toneStyle),
    toneExpertiseLevel: normalizeString(toneOfVoice.expertise_level || toneOfVoice.expertiseLevel || source.toneExpertiseLevel),
    toneAvoid: toStringArray(toneOfVoice.avoid || source.toneAvoid),
    articleTypes: toStringArray(contentStrategy.article_types || contentStrategy.articleTypes || source.articleTypes),
    contentAllowed: toStringArray(contentBoundaries.allowed || source.contentAllowed),
    contentNotAllowed: toStringArray(contentBoundaries.not_allowed || contentBoundaries.notAllowed || source.contentNotAllowed),
    requiredDisclaimers: toStringArray(contentBoundaries.required_disclaimers || contentBoundaries.requiredDisclaimers || source.requiredDisclaimers),
  };
};

const slugify = value => normalizeString(value)
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const categoryLike = candidate => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;
  const keys = Object.keys(candidate).map(key => key.toLowerCase());
  return keys.includes('name') || keys.includes('slug') || keys.includes('description');
};

const identityLike = candidate => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;
  const keys = Object.keys(candidate).map(key => key.toLowerCase());
  return keys.includes('sitetitle')
    || keys.includes('site_title')
    || keys.includes('sitetagline')
    || keys.includes('site_tagline')
    || keys.includes('homedescription')
    || keys.includes('home_description');
};

const findIdentityObject = payload => {
  const source = toPlainObject(payload);
  const directIdentity = source.site_identity || source.siteIdentity || source.identity;
  if (directIdentity && typeof directIdentity === 'object' && !Array.isArray(directIdentity)) {
    return directIdentity;
  }

  if (identityLike(source)) return source;

  const knownKeys = ['site_identity', 'siteIdentity', 'identity', 'result', 'data'];
  for (const key of knownKeys) {
    const nested = source[key];
    if (identityLike(nested)) return nested;
  }

  const queue = [source];
  const visited = new Set();
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) continue;
    visited.add(current);

    if (identityLike(current)) return current;

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') queue.push(value);
    }
  }
  return source;
};

const extractIdentityCategories = payload => {
  const source = toPlainObject(payload);
  if (Array.isArray(source.categories)) {
    return source.categories.filter(item => item && typeof item === 'object' && !Array.isArray(item));
  }
  const queue = [source];
  const visited = new Set();
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) continue;
    visited.add(current);
    for (const value of Object.values(current)) {
      if (Array.isArray(value)) {
        const items = value.filter(item => item && typeof item === 'object' && !Array.isArray(item));
        if (items.length && items.every(categoryLike)) return items;
      } else if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }
  return [];
};

const mapAiCategoryToDto = (category, index) => {
  const source = toPlainObject(category);
  const name = normalizeString(source.name || source.title || source.category);
  const generatedSlug = slugify(normalizeString(source.slug) || name);
  return {
    name,
    slug: generatedSlug || `category-${index + 1}`,
    description: normalizeString(source.description || source.summary || source.excerpt),
  };
};

const mapAiIdentityPayload = payload => {
  const root = toPlainObject(payload);
  const source = findIdentityObject(payload);
  const siteTitle = normalizeString(source.siteTitle || source.site_title || source.title || source.name);
  const siteTagline = normalizeString(source.siteTagline || source.site_tagline || source.tagline || source.subtitle);
  const homeDescription = normalizeString(source.homeDescription || source.home_description || source.description);
  const rawCategories = extractIdentityCategories(root);
  const categoriesSource = rawCategories.length ? rawCategories : extractIdentityCategories(source);
  const categories = categoriesSource.map((item, index) => mapAiCategoryToDto(item, index));
  const seen = new Set();
  const uniqueCategories = [];
  categories.forEach(item => {
    const slugKey = normalizeString(item.slug).toLowerCase();
    if (!item.name || !slugKey || seen.has(slugKey)) return;
    seen.add(slugKey);
    uniqueCategories.push(item);
  });

  return {
    siteTitle,
    siteTagline,
    homeDescription,
    categories: uniqueCategories,
  };
};

const normalizeSiteImageCandidate = candidate => {
  const source = toPlainObject(candidate);
  return {
    storageKey: toNullableString(source.storageKey || source.storage_key),
    publicUrl: toNullableString(source.publicUrl || source.public_url),
    fontKey: normalizeString(source.fontKey || source.font_key),
    textColor: normalizeString(source.textColor || source.text_color),
    layout: normalizeString(source.layout),
  };
};

const normalizeSiteImage = payload => {
  const source = toPlainObject(payload);
  const status = normalizeString(source.status).toLowerCase() || 'not_started';
  return {
    id: toNullableString(source.id),
    projectId: toNullableString(source.projectId || source.project_id),
    imageType: normalizeString(source.imageType || source.image_type),
    status,
    storageBucket: toNullableString(source.storageBucket || source.storage_bucket),
    storageKey: toNullableString(source.storageKey || source.storage_key),
    publicUrl: toNullableString(source.publicUrl || source.public_url),
    contentType: toNullableString(source.contentType || source.content_type),
    sizeBytes: Number.isFinite(Number(source.sizeBytes ?? source.size_bytes))
      ? Number(source.sizeBytes ?? source.size_bytes)
      : null,
    croppedStorageBucket: toNullableString(source.croppedStorageBucket || source.cropped_storage_bucket),
    croppedStorageKey: toNullableString(source.croppedStorageKey || source.cropped_storage_key),
    croppedPublicUrl: toNullableString(source.croppedPublicUrl || source.cropped_public_url),
    croppedContentType: toNullableString(source.croppedContentType || source.cropped_content_type),
    croppedSizeBytes: Number.isFinite(Number(source.croppedSizeBytes ?? source.cropped_size_bytes))
      ? Number(source.croppedSizeBytes ?? source.cropped_size_bytes)
      : null,
    cropX: Number.isFinite(Number(source.cropX ?? source.crop_x)) ? Number(source.cropX ?? source.crop_x) : null,
    cropY: Number.isFinite(Number(source.cropY ?? source.crop_y)) ? Number(source.cropY ?? source.crop_y) : null,
    cropWidth: Number.isFinite(Number(source.cropWidth ?? source.crop_width)) ? Number(source.cropWidth ?? source.crop_width) : null,
    cropHeight: Number.isFinite(Number(source.cropHeight ?? source.crop_height)) ? Number(source.cropHeight ?? source.crop_height) : null,
    selectedVariant: normalizeString(source.selectedVariant || source.selected_variant).toLowerCase() || 'original',
    selectedPublicUrl: toNullableString(source.selectedPublicUrl || source.selected_public_url),
    candidates: Array.isArray(source.candidates)
      ? source.candidates.map(normalizeSiteImageCandidate).filter(candidate => candidate.publicUrl)
      : [],
    iconPublicUrl: toNullableString(source.iconPublicUrl || source.icon_public_url),
    logoText: normalizeString(source.logoText ?? source.logo_text),
    logoFontKey: normalizeString(source.logoFontKey || source.logo_font_key),
    logoTextColor: normalizeString(source.logoTextColor || source.logo_text_color),
    logoTextOffsetX: Number.isFinite(Number(source.logoTextOffsetX ?? source.logo_text_offset_x))
      ? Number(source.logoTextOffsetX ?? source.logo_text_offset_x)
      : null,
    logoTextOffsetY: Number.isFinite(Number(source.logoTextOffsetY ?? source.logo_text_offset_y))
      ? Number(source.logoTextOffsetY ?? source.logo_text_offset_y)
      : null,
    logoFontSize: Number.isFinite(Number(source.logoFontSize ?? source.logo_font_size))
      ? Number(source.logoFontSize ?? source.logo_font_size)
      : null,
    logoIconScale: Number.isFinite(Number(source.logoIconScale ?? source.logo_icon_scale))
      ? Number(source.logoIconScale ?? source.logo_icon_scale)
      : null,
    logoIconOffsetX: Number.isFinite(Number(source.logoIconOffsetX ?? source.logo_icon_offset_x))
      ? Number(source.logoIconOffsetX ?? source.logo_icon_offset_x)
      : null,
    logoIconOffsetY: Number.isFinite(Number(source.logoIconOffsetY ?? source.logo_icon_offset_y))
      ? Number(source.logoIconOffsetY ?? source.logo_icon_offset_y)
      : null,
    logoSubtitleText: normalizeString(source.logoSubtitleText ?? source.logo_subtitle_text),
    logoSubtitleFontKey: normalizeString(source.logoSubtitleFontKey || source.logo_subtitle_font_key),
    logoSubtitleFontSize: Number.isFinite(Number(source.logoSubtitleFontSize ?? source.logo_subtitle_font_size))
      ? Number(source.logoSubtitleFontSize ?? source.logo_subtitle_font_size)
      : null,
    logoSubtitleColor: normalizeString(source.logoSubtitleColor || source.logo_subtitle_color),
    logoSubtitleOffsetX: Number.isFinite(Number(source.logoSubtitleOffsetX ?? source.logo_subtitle_offset_x))
      ? Number(source.logoSubtitleOffsetX ?? source.logo_subtitle_offset_x)
      : null,
    logoSubtitleOffsetY: Number.isFinite(Number(source.logoSubtitleOffsetY ?? source.logo_subtitle_offset_y))
      ? Number(source.logoSubtitleOffsetY ?? source.logo_subtitle_offset_y)
      : null,
    logoPalette: Array.isArray(source.logoPalette || source.logo_palette)
      ? (source.logoPalette || source.logo_palette).map(normalizeString).filter(Boolean)
      : [],
    generations: Array.isArray(source.generations)
      ? source.generations
        .map(g => ({
          id: toNullableString(g?.id),
          publicUrl: toNullableString(g?.publicUrl || g?.public_url),
        }))
        .filter(g => g.id && g.publicUrl)
      : [],
    selectedGenerationId: toNullableString(source.selectedGenerationId || source.selected_generation_id),
    errorMessage: normalizeString(source.errorMessage || source.error_message),
    startedAt: toNullableString(source.startedAt || source.started_at),
    completedAt: toNullableString(source.completedAt || source.completed_at),
    createdAt: toNullableString(source.createdAt || source.created_at),
    updatedAt: toNullableString(source.updatedAt || source.updated_at),
  };
};

const normalizeLocalizationCategory = category => ({
  name: normalizeString(category?.name),
  slug: normalizeString(category?.slug),
  description: normalizeString(category?.description),
});

const mapLocalizationPayload = payload => {
  const source = toPlainObject(payload);
  const selectedConceptSource = toPlainObject(source.selected_concept || source.selectedConcept || source.concept);
  const siteIdentitySource = toPlainObject(source.site_identity || source.siteIdentity || source.identity);

  const categoriesSource = Array.isArray(siteIdentitySource.categories) ? siteIdentitySource.categories : [];

  return {
    selected_concept: {
      conceptName: normalizeString(selectedConceptSource.conceptName || selectedConceptSource.concept_name),
      conceptAngle: normalizeString(selectedConceptSource.conceptAngle || selectedConceptSource.concept_angle),
      positioning: normalizeString(selectedConceptSource.positioning),
      differentiation: normalizeString(selectedConceptSource.differentiation),
      targetAudience: toStringArray(selectedConceptSource.targetAudience || selectedConceptSource.target_audience),
      toneStyle: normalizeString(selectedConceptSource.toneStyle || selectedConceptSource.tone_style),
      toneExpertiseLevel: normalizeString(selectedConceptSource.toneExpertiseLevel || selectedConceptSource.tone_expertise_level),
      toneAvoid: toStringArray(selectedConceptSource.toneAvoid || selectedConceptSource.tone_avoid),
      articleTypes: toStringArray(selectedConceptSource.articleTypes || selectedConceptSource.article_types),
      contentAllowed: toStringArray(selectedConceptSource.contentAllowed || selectedConceptSource.content_allowed),
      contentNotAllowed: toStringArray(selectedConceptSource.contentNotAllowed || selectedConceptSource.content_not_allowed),
      requiredDisclaimers: toStringArray(selectedConceptSource.requiredDisclaimers || selectedConceptSource.required_disclaimers),
    },
    site_identity: {
      siteTitle: normalizeString(siteIdentitySource.siteTitle || siteIdentitySource.site_title),
      siteTagline: normalizeString(siteIdentitySource.siteTagline || siteIdentitySource.site_tagline),
      homeDescription: normalizeString(siteIdentitySource.homeDescription || siteIdentitySource.home_description),
      categories: categoriesSource.map(normalizeLocalizationCategory).filter(item => item.name || item.slug || item.description),
    },
  };
};

const normalizeLocalizationRecord = payload => {
  const source = toPlainObject(payload);
  return {
    id: toNullableString(source.id),
    projectId: toNullableString(source.projectId || source.project_id),
    languageCode: normalizeString(source.languageCode || source.language_code).toLowerCase(),
    stale: source.stale === true,
    concept: normalizeConcept(source.concept || source.selected_concept || {}, 0),
    identity: normalizeSiteIdentity(source.identity || source.site_identity || {}),
    createdAt: toNullableString(source.createdAt || source.created_at),
    updatedAt: toNullableString(source.updatedAt || source.updated_at),
  };
};

const setResolvedContentMeta = payload => {
  const source = toPlainObject(payload);
  resolvedContentMeta.value = {
    localizationId: toNullableString(source.localizationId || source.localization_id),
    requestedLocale: toNullableString(source.requestedLocale || source.requested_locale),
    resolvedLocale: normalizeString(source.resolvedLocale || source.resolved_locale).toLowerCase(),
    sourceLocale: normalizeString(source.sourceLocale || source.source_locale).toLowerCase(),
    localized: source.localized === true,
    fallbackToSource: source.fallbackToSource === true || source.fallback_to_source === true,
    stale: source.stale === true,
  };
};

const getResolvedLocaleFromPayload = payload => normalizeString(
  toPlainObject(payload).resolvedLocale || toPlainObject(payload).resolved_locale
).toLowerCase();

const applyResolvedContentPayload = payload => {
  setResolvedContentMeta(payload);
  localizationData.value = mapLocalizationPayload(resolveLocalizationDataPayload(payload));
  localizationRecord.value = buildLocalizationRecordFromResolvedContent(payload);
};

const buildLocalizationRecordFromResolvedContent = payload => {
  const source = toPlainObject(payload);
  const localizationId = toNullableString(source.localizationId || source.localization_id);
  const resolvedLocale = normalizeString(source.resolvedLocale || source.resolved_locale).toLowerCase();
  const localized = source.localized === true;
  if (!localized || !localizationId || !resolvedLocale) return null;

  return {
    id: localizationId,
    projectId: toNullableString(source.projectId || source.project_id),
    languageCode: resolvedLocale,
    stale: source.stale === true,
    concept: normalizeConcept(source.concept || source.selected_concept || {}, 0),
    identity: normalizeSiteIdentity(source.identity || source.site_identity || {}),
    createdAt: null,
    updatedAt: null,
  };
};

const resolveLocalizationDataPayload = payload => {
  const source = toPlainObject(payload);
  if (source.localizationData && typeof source.localizationData === 'object') return source.localizationData;
  if (source.localization_data && typeof source.localization_data === 'object') return source.localization_data;
  if (source.concept || source.identity) {
    return {
      selected_concept: source.concept,
      site_identity: source.identity,
    };
  }
  return source;
};

const buildLocalizationProjectData = () => {
  const concept = selectedProjectConcept.value;
  const identity = siteIdentity.value;
  return {
    selected_concept: {
      conceptName: normalizeString(concept?.conceptName),
      conceptAngle: normalizeString(concept?.conceptAngle),
      positioning: normalizeString(concept?.positioning),
      differentiation: normalizeString(concept?.differentiation),
      targetAudience: toStringArray(concept?.targetAudience),
      toneStyle: normalizeString(concept?.toneStyle),
      toneExpertiseLevel: normalizeString(concept?.toneExpertiseLevel),
      toneAvoid: toStringArray(concept?.toneAvoid),
      articleTypes: toStringArray(concept?.articleTypes),
      contentAllowed: toStringArray(concept?.contentAllowed),
      contentNotAllowed: toStringArray(concept?.contentNotAllowed),
      requiredDisclaimers: toStringArray(concept?.requiredDisclaimers),
    },
    site_identity: {
      siteTitle: normalizeString(identity?.siteTitle),
      siteTagline: normalizeString(identity?.siteTagline),
      homeDescription: normalizeString(identity?.homeDescription),
      categories: (Array.isArray(identity?.categories) ? identity.categories : [])
        .map(normalizeLocalizationCategory)
        .filter(item => item.name || item.slug || item.description),
    },
  };
};

const loadLocalization = async (projectId, languageCode, options = {}) => {
  const normalizedProjectId = normalizeString(projectId);
  const normalizedLanguageCode = normalizeString(languageCode).toLowerCase();
  if (!normalizedProjectId || !normalizedLanguageCode) {
    localizationData.value = null;
    localizationRecord.value = null;
    setResolvedContentMeta({});
    return;
  }
  const silentNotFound = options?.silentNotFound !== false;
  localizationLoading.value = true;
  localizationRecord.value = null;
  try {
    const payload = await getSiteProjectResolvedContent(normalizedProjectId, normalizedLanguageCode);
    applyResolvedContentPayload(payload);
    if (!normalizeString(localizationRecord.value?.id)) {
      try {
        const localizationPayload = await getSiteProjectLocalization(normalizedProjectId, normalizedLanguageCode);
        localizationRecord.value = normalizeLocalizationRecord(localizationPayload);
      } catch (localizationError) {
        if (Number(localizationError?.response?.status) !== 404) throw localizationError;
      }
    }
  } catch (error) {
    if (Number(error?.response?.status) === 404 && silentNotFound) {
      localizationData.value = null;
      localizationRecord.value = null;
      setResolvedContentMeta({});
      return;
    }
    throw error;
  } finally {
    localizationLoading.value = false;
  }
};

const syncRouteProjectId = async projectId => {
  const normalizedId = normalizeString(projectId);
  const routeProjectId = normalizeString(route.params.projectId);
  if (!normalizedId || routeProjectId === normalizedId) return;
  await router.replace({ name: 'WordPressConceptWizard', params: { projectId: normalizedId } });
};

const loadTopics = async () => {
  topicsLoading.value = true;
  topicsError.value = '';
  try {
    const list = await getSiteTopicDictionaryValues();
    const unique = new Map();
    list.forEach(item => {
      const normalized = normalizeTopicOption(item);
      if (!normalized) return;
      const uniqueKey = normalizeString(normalized.code || normalized.value).toLowerCase();
      if (!uniqueKey) return;
      if (unique.has(uniqueKey)) return;
      unique.set(uniqueKey, normalized);
    });

    topicOptions.value = [...unique.values()].sort((a, b) => {
      const orderDelta = Number(a.sortOrder) - Number(b.sortOrder);
      if (orderDelta !== 0) return orderDelta;
      return String(a.label || '').localeCompare(String(b.label || ''), 'ru');
    });

    if (!selectedTopicName.value && topicOptions.value.length) {
      selectedTopicName.value = topicOptions.value[0].value;
    }
  } catch (error) {
    topicsError.value = extractError(error);
    topicOptions.value = [];
  } finally {
    topicsLoading.value = false;
  }
};

const loadProjects = async () => {
  projectsLoading.value = true;
  projectsError.value = '';
  try {
    const list = await getSiteProjects();
    const normalizedList = list.map(item => normalizeProject(item));
    const enrichedList = await Promise.all(normalizedList.map(async item => {
      const projectId = normalizeString(item.id);
      if (!projectId) return item;

      try {
        const detailedProject = normalizeProject(await getSiteProject(projectId));
        const selectedConceptName = normalizeString(
          detailedProject.concepts.find(concept => normalizeString(concept.id) === normalizeString(detailedProject.selectedConceptId))?.conceptName
        );
        const selectedDomainName = normalizeString(
          detailedProject.domains.find(domain => normalizeString(domain.id) === normalizeString(detailedProject.selectedDomainId))?.domainName
        );

        let selectedLocaleLabel = 'Не выбран';
        let localizedSiteTitle = '';

        try {
          const resolvedPayload = await getSiteProjectResolvedContent(projectId);
          localizedSiteTitle = extractLocalizedSiteTitle(resolvedPayload);
          if (normalizeString(detailedProject.selectedLocalId)) {
            const resolvedLocale = normalizeString(
              toPlainObject(resolvedPayload).resolvedLocale || toPlainObject(resolvedPayload).resolved_locale
            ).toLowerCase();
            selectedLocaleLabel = formatLocaleLabel(resolvedLocale);
          }
        } catch {
          localizedSiteTitle = '';
        }

        return {
          ...detailedProject,
          targetLocalesLabel: formatTargetLocales(detailedProject.targetLocales),
          selectedConceptName,
          selectedDomainName,
          selectedLocaleLabel,
          localizedSiteTitle,
        };
      } catch {
        return {
          ...item,
          targetLocalesLabel: formatTargetLocales(item.targetLocales),
        };
      }
    }));

    projectList.value = enrichedList
      .sort((a, b) => {
        const timeDelta = toTime(b.updatedAt || b.createdAt) - toTime(a.updatedAt || a.createdAt);
        if (timeDelta !== 0) return timeDelta;
        return String(b.id || '').localeCompare(String(a.id || ''));
      });
  } catch (error) {
    projectsError.value = extractError(error);
    projectList.value = [];
  } finally {
    projectsLoading.value = false;
  }
};

const openExistingProject = async projectId => {
  const normalizedId = normalizeString(projectId);
  if (!normalizedId) return;
  if (normalizedId === currentProjectId.value) {
    await loadProject(normalizedId);
    return;
  }
  await router.replace({ name: 'WordPressConceptWizard', params: { projectId: normalizedId } });
};

const requestDeleteProject = projectItem => {
  const projectId = normalizeString(projectItem?.id);
  if (!projectId || deletingProjectId.value) return;
  projectDeleteCandidate.value = {
    id: projectId,
    topicName: normalizeString(projectItem?.topicName) || 'Без тематики',
  };
};

const closeDeleteProjectModal = () => {
  if (deletingProjectId.value) return;
  projectDeleteCandidate.value = null;
};

const confirmDeleteProject = async () => {
  const projectId = normalizeString(projectDeleteCandidate.value?.id);
  if (!projectId || deletingProjectId.value) return;

  deletingProjectId.value = projectId;
  clearMessages();
  try {
    await deleteSiteProject(projectId);
    projectDeleteCandidate.value = null;
    await loadProjects();
    infoMessage.value = 'Проект удален.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    deletingProjectId.value = '';
  }
};

const loadProject = async (projectId, options = {}) => {
  const preserveActiveStep = Boolean(options?.preserveActiveStep);
  const normalizedId = normalizeString(projectId);
  if (!normalizedId) {
    resetWizard();
    return;
  }

  clearLogoState();
  isLoadingProject.value = true;
  clearMessages();
  try {
    const payload = await getSiteProject(normalizedId);
    const normalizedProject = normalizeProject(payload);
    project.value = normalizedProject;
    projectDefaultLocaleCode.value = normalizeString(normalizedProject.sourceLocale || normalizedProject.languageCode || 'ru').toLowerCase() || 'ru';
    if (normalizedProject.topicName) selectedTopicName.value = normalizedProject.topicName;
    selectedConceptId.value = normalizedProject.selectedConceptId || normalizedProject.concepts[0]?.id || '';
    await loadDesignForCurrentProject();
    await loadIdentity(normalizedId, { silentNotFound: true });
    if (siteIdentity.value?.id) {
      let languageCode = 'ru';
      if (normalizedProject.selectedLocalId) {
        try {
          const selectedPayload = await getSiteProjectResolvedContent(normalizedId);
          applyResolvedContentPayload(selectedPayload);
          const selectedLocale = getResolvedLocaleFromPayload(selectedPayload);
          if (selectedLocale) {
            languageCode = selectedLocale;
            projectDefaultLocaleCode.value = selectedLocale;
          }
        } catch (error) {
          const statusCode = Number(error?.response?.status);
          if (statusCode !== 400 && statusCode !== 404) throw error;
          localizationData.value = null;
          localizationRecord.value = null;
          setResolvedContentMeta({});
        }
      } else {
        localizationData.value = null;
        localizationRecord.value = null;
        setResolvedContentMeta({});
      }

      selectedLocalizationLanguageCode.value = languageCode;
      await loadLocalization(normalizedId, languageCode, { silentNotFound: true });
    } else {
      selectedLocalizationLanguageCode.value = 'ru';
      projectDefaultLocaleCode.value = normalizeString(normalizedProject.sourceLocale || normalizedProject.languageCode || 'ru').toLowerCase() || 'ru';
      localizationData.value = null;
      localizationRecord.value = null;
      setResolvedContentMeta({});
    }

    await Promise.all([
      fetchLogoStatus(normalizedId, { silent: true, silentNotFound: true }).catch(() => null),
      fetchFaviconStatus(normalizedId, { silent: true, silentNotFound: true }).catch(() => null),
    ]);

    applyPreferredTldsByLocale(selectedLocaleForDomains.value);
    if (!preserveActiveStep) {
      if (isWizardComplete.value) {
        activeStep.value = 8;
      } else if (canOpenStepSeven.value) {
        activeStep.value = 7;
      } else if (canOpenStepSix.value) {
        activeStep.value = 6;
      } else if (canOpenStepFive.value) {
        activeStep.value = 5;
      } else if (normalizedProject.selectedLocalId) {
        activeStep.value = 4;
      } else if (siteIdentity.value?.id) {
        activeStep.value = 3;
      } else if (normalizedProject.selectedConceptId) {
        activeStep.value = 2;
      } else {
        activeStep.value = 1;
      }
    }

    if (activeStep.value === 5) {
      await loadLogoForCurrentProject({ silentNotFound: true });
      stopFaviconPolling();
    } else if (activeStep.value === 6) {
      await loadFaviconForCurrentProject({ silentNotFound: true });
      stopLogoPolling();
    } else if (activeStep.value === 7) {
      stopLogoPolling();
      stopFaviconPolling();
    } else {
      stopLogoPolling();
      stopFaviconPolling();
    }
  } catch (error) {
    uiError.value = Number(error?.response?.status) === 404
      ? `Проект ${normalizedId} не найден.`
      : extractError(error);
    project.value = null;
    projectDefaultLocaleCode.value = 'ru';
    siteIdentity.value = null;
    localizationData.value = null;
    localizationRecord.value = null;
    selectingLocalizationId.value = '';
    deletingLocalizationLanguageCode.value = '';
    localizationDeleteCandidate.value = null;
    setResolvedContentMeta({});
    syncIdentityDraft(null);
    categoryDraft.value = {
      name: '',
      slug: '',
      description: '',
    };
    isEditingIdentity.value = false;
    isAddingCategory.value = false;
    editingCategoryId.value = '';
    categoryEditDraft.value = {
      name: '',
      slug: '',
      description: '',
    };
    selectedConceptId.value = '';
    clearLogoState();
    clearFaviconState();
    if (!preserveActiveStep) {
      activeStep.value = 1;
    }
  } finally {
    deletingConceptId.value = '';
    savingConceptId.value = '';
    conceptDeleteCandidate.value = null;
    editingConceptId.value = '';
    conceptDraft.value = null;
    isEditingIdentity.value = false;
    isAddingCategory.value = false;
    editingCategoryId.value = '';
    categoryEditDraft.value = {
      name: '',
      slug: '',
      description: '',
    };
    generatingDomains.value = false;
    selectingDomainId.value = '';
    deletingDomainId.value = '';
    isAddingDomain.value = false;
    isCreatingDomain.value = false;
    newDomainName.value = '';
    clearDomainAvailabilityState();
    localizationLoading.value = false;
    localizationGenerating.value = false;
    identityDeleting.value = false;
    selectingLocalizationId.value = '';
    deletingLocalizationLanguageCode.value = '';
    localizationDeleteCandidate.value = null;
    showIdentityOverwriteModal.value = false;
    showDomainsOverwriteModal.value = false;
    isLoadingProject.value = false;
  }
};

const loadIdentity = async (projectId, options = {}) => {
  const normalizedProjectId = normalizeString(projectId);
  if (!normalizedProjectId) {
    siteIdentity.value = null;
    syncIdentityDraft(null);
    return;
  }

  const silentNotFound = options?.silentNotFound !== false;
  identityLoading.value = true;
  try {
    const payload = await getSiteProjectIdentity(normalizedProjectId);
    setIdentity(payload);
  } catch (error) {
    if (Number(error?.response?.status) === 404 && silentNotFound) {
      siteIdentity.value = null;
      syncIdentityDraft(null);
      isEditingIdentity.value = false;
      isAddingCategory.value = false;
      editingCategoryId.value = '';
      categoryEditDraft.value = {
        name: '',
        slug: '',
        description: '',
      };
      return;
    }
    throw error;
  } finally {
    identityLoading.value = false;
  }
};

const getIdentityPayloadFromDraft = () => ({
  siteTitle: normalizeString(identityDraft.value.siteTitle),
  siteTagline: normalizeString(identityDraft.value.siteTagline),
  homeDescription: normalizeString(identityDraft.value.homeDescription),
});

const validateIdentityPayload = payload => {
  if (!payload.siteTitle || !payload.siteTagline || !payload.homeDescription) {
    throw new Error('Заполните название, tagline и описание главной страницы.');
  }
};

const startEditIdentity = () => {
  if (isIdentityBusy.value) return;
  syncIdentityDraft(siteIdentity.value);
  isEditingIdentity.value = true;
  uiError.value = '';
};

const cancelEditIdentity = () => {
  if (identitySaving.value) return;
  syncIdentityDraft(siteIdentity.value);
  isEditingIdentity.value = false;
};

const saveIdentity = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || identitySaving.value) return;

  const payload = getIdentityPayloadFromDraft();
  try {
    validateIdentityPayload(payload);
  } catch (error) {
    uiError.value = extractError(error);
    return;
  }

  identitySaving.value = true;
  clearMessages();
  try {
    const hasIdentity = Boolean(siteIdentity.value?.id);
    const updated = hasIdentity
      ? await updateSiteProjectIdentity(projectId, payload)
      : await createSiteProjectIdentity(projectId, payload);
    setIdentity(updated);
    isEditingIdentity.value = false;
    infoMessage.value = hasIdentity ? 'Профиль сайта обновлен.' : 'Профиль сайта создан.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    identitySaving.value = false;
  }
};

const startAddCategory = () => {
  if (isIdentityBusy.value || !siteIdentity.value?.id) return;
  categoryDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
  isAddingCategory.value = true;
  editingCategoryId.value = '';
  uiError.value = '';
};

const cancelAddCategory = () => {
  if (identityCategorySaving.value) return;
  isAddingCategory.value = false;
  categoryDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
};

const closeIdentityOverwriteModal = () => {
  if (identityGenerating.value) return;
  showIdentityOverwriteModal.value = false;
};

const confirmGenerateIdentityOverwrite = async () => {
  if (identityGenerating.value) return;
  showIdentityOverwriteModal.value = false;
  await generateIdentityByAi();
};

const closeDomainsOverwriteModal = () => {
  if (generatingDomains.value) return;
  showDomainsOverwriteModal.value = false;
};

const confirmGenerateDomainsOverwrite = async () => {
  if (generatingDomains.value) return;
  showDomainsOverwriteModal.value = false;
  await generateDomainsByAi();
};

const startEditCategory = category => {
  const categoryKey = normalizeString(category?.id || category?.slug);
  if (!categoryKey || isIdentityBusy.value) return;
  editingCategoryId.value = categoryKey;
  categoryEditDraft.value = createCategoryDraft(category);
  isAddingCategory.value = false;
  uiError.value = '';
};

const cancelEditCategory = () => {
  if (identityCategorySaving.value) return;
  editingCategoryId.value = '';
  categoryEditDraft.value = {
    name: '',
    slug: '',
    description: '',
  };
};

const replaceIdentityCategories = async (projectId, nextCategories) => {
  const currentCategories = Array.isArray(siteIdentity.value?.categories) ? siteIdentity.value.categories : [];
  for (const category of currentCategories) {
    const categoryId = normalizeString(category?.id);
    if (!categoryId) continue;
    await deleteSiteProjectIdentityCategory(projectId, categoryId);
  }

  let latestIdentity = null;
  for (const category of nextCategories) {
    latestIdentity = await addSiteProjectIdentityCategory(projectId, {
      name: normalizeString(category.name),
      slug: normalizeString(category.slug),
      description: normalizeString(category.description),
    });
  }

  if (latestIdentity) {
    setIdentity(latestIdentity);
    return;
  }

  await loadIdentity(projectId, { silentNotFound: false });
};

const generateIdentityByAi = async () => {
  const projectId = normalizeString(project.value?.id);
  const selectedConceptText = normalizeString(selectedConceptPromptLine.value);
  if (!projectId || !selectedConceptText || identityGenerating.value) return;

  identityGenerating.value = true;
  clearMessages();
  try {
    const aiMessage = await executeAiProfile({
      profileKey: AI_PROFILE_STEP_TWO_KEY,
      promptVariables: {
        selected_concept: selectedConceptText,
        language: 'Russian',
      },
    });

    const aiPayload = parseAiPayload(aiMessage?.content);
    const mappedIdentity = mapAiIdentityPayload(aiPayload);
    const identityPayload = {
      siteTitle: mappedIdentity.siteTitle,
      siteTagline: mappedIdentity.siteTagline,
      homeDescription: mappedIdentity.homeDescription,
    };
    if (!identityPayload.siteTitle || !identityPayload.siteTagline || !identityPayload.homeDescription) {
      const availableRootKeys = Object.keys(toPlainObject(aiPayload)).join(', ');
      const availableIdentityKeys = Object.keys(findIdentityObject(aiPayload)).join(', ');
      throw new Error(
        `AI ответ распознан, но в профиле сайта нет обязательных полей. Корневые ключи: [${availableRootKeys || '—'}], ключи профиля: [${availableIdentityKeys || '—'}].`
      );
    }
    validateIdentityPayload(identityPayload);

    const hasIdentity = Boolean(siteIdentity.value?.id);
    const savedIdentity = hasIdentity
      ? await updateSiteProjectIdentity(projectId, identityPayload)
      : await createSiteProjectIdentity(projectId, identityPayload);
    setIdentity(savedIdentity);
    isEditingIdentity.value = false;
    isAddingCategory.value = false;
    editingCategoryId.value = '';
    categoryEditDraft.value = {
      name: '',
      slug: '',
      description: '',
    };

    await replaceIdentityCategories(projectId, mappedIdentity.categories);
    await loadProjects();
    infoMessage.value = 'Профиль сайта сформирован на основе выбранной концепции.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    identityGenerating.value = false;
  }
};

const generateDesignByAi = async () => {
  const projectId = normalizeString(project.value?.id);
  const identity = siteIdentity.value;
  // Prefer the localized concept/identity for the selected localization so the template
  // is generated in the chosen language (mirrors the logo step). Fall back to the source
  // concept/identity if no localization is loaded.
  const localizedConcept = toPlainObject(localizationData.value?.selected_concept);
  const localizedIdentity = toPlainObject(localizationData.value?.site_identity);
  const selectedConceptText = normalizeString(formatSelectedConceptForPrompt(localizedConcept))
    || normalizeString(selectedConceptPromptLine.value);
  if (!projectId || !selectedConceptText || !identity?.id || designGenerating.value) return;

  const promptCategories = (Array.isArray(localizedIdentity.categories) && localizedIdentity.categories.length
    ? localizedIdentity.categories
    : (identity.categories || []))
    .map(category => normalizeString(category?.name))
    .filter(Boolean);

  designGenerating.value = true;
  clearMessages();
  try {
    const aiMessage = await executeAiProfile({
      profileKey: AI_PROFILE_DESIGN_KEY,
      promptVariables: {
        selected_concept: selectedConceptText,
        site_identity: JSON.stringify({
          site_title: normalizeString(localizedIdentity.siteTitle) || identity.siteTitle,
          site_tagline: normalizeString(localizedIdentity.siteTagline) || identity.siteTagline,
          home_description: normalizeString(localizedIdentity.homeDescription) || identity.homeDescription,
          categories: promptCategories,
        }),
        language: logoPromptLanguage.value || 'Russian',
      },
    });

    const design = toPlainObject(parseAiPayload(aiMessage?.content));
    if (!design.palette || !design.typography || !design.texts) {
      throw new Error('AI ответ распознан, но шаблон не содержит palette/typography/texts.');
    }
    siteDesign.value = await updateSiteProjectDesign(projectId, { payload: design });
    isEditingDesign.value = false;
    designDraft.value = null;
    infoMessage.value = 'Шаблон сгенерирован и сохранён.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    designGenerating.value = false;
  }
};

const designView = computed(() => {
  const payload = toPlainObject(siteDesign.value?.payload);
  const palette = toPlainObject(payload.palette);
  const typography = toPlainObject(payload.typography);
  const texts = toPlainObject(payload.texts);
  const privacy = toPlainObject(payload.privacyPolicy);
  const sections = Array.isArray(privacy.sections) ? privacy.sections : [];
  return {
    palette,
    fontKey: normalizeString(typography.fontKey) || '—',
    googleFontName: normalizeString(typography.googleFontName),
    textsSummary: designMicrocopyKeys.map(key => normalizeString(texts[key])).filter(Boolean).join(' · ') || '—',
    privacyTitle: normalizeString(privacy.title) || '—',
    privacySectionsCount: sections.length,
    privacyHeadings: sections.map(section => normalizeString(toPlainObject(section).heading)).filter(Boolean),
  };
});

const loadDesignForCurrentProject = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId) return;
  designLoading.value = true;
  try {
    siteDesign.value = await getSiteProjectDesign(projectId);
  } catch (error) {
    siteDesign.value = null;
  } finally {
    designLoading.value = false;
  }
};

const buildDesignDraft = () => {
  const payload = toPlainObject(siteDesign.value?.payload);
  const palette = toPlainObject(payload.palette);
  const typography = toPlainObject(payload.typography);
  const texts = toPlainObject(payload.texts);
  const privacy = toPlainObject(payload.privacyPolicy);
  const paletteDraft = {};
  designPaletteRoles.forEach(role => { paletteDraft[role] = normalizeString(palette[role]) || '#000000'; });
  const textsDraft = {};
  designMicrocopyKeys.forEach(key => { textsDraft[key] = normalizeString(texts[key]); });
  const sections = (Array.isArray(privacy.sections) ? privacy.sections : []).map(section => {
    const plain = toPlainObject(section);
    return {
      heading: normalizeString(plain.heading),
      paragraphsText: (Array.isArray(plain.paragraphs) ? plain.paragraphs : []).join('\n'),
      itemsText: (Array.isArray(plain.items) ? plain.items : []).join('\n'),
    };
  });
  return {
    palette: paletteDraft,
    typography: {
      fontKey: normalizeString(typography.fontKey) || designFontKeys[0],
      googleFontName: normalizeString(typography.googleFontName),
    },
    texts: textsDraft,
    privacyPolicy: { title: normalizeString(privacy.title), sections },
  };
};

const toggleDesignEdit = () => {
  if (isEditingDesign.value) {
    isEditingDesign.value = false;
    designDraft.value = null;
    return;
  }
  designDraft.value = buildDesignDraft();
  isEditingDesign.value = true;
};

const addDesignSection = () => {
  if (!designDraft.value) return;
  designDraft.value.privacyPolicy.sections.push({ heading: '', paragraphsText: '', itemsText: '' });
};

const removeDesignSection = index => {
  if (!designDraft.value) return;
  designDraft.value.privacyPolicy.sections.splice(index, 1);
};

const saveDesignEdits = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || !designDraft.value || designSaving.value) return;
  designSaving.value = true;
  clearMessages();
  try {
    const draft = designDraft.value;
    const splitLines = text => normalizeString(text).split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    const sections = draft.privacyPolicy.sections
      .map(section => {
        const items = splitLines(section.itemsText);
        return {
          heading: normalizeString(section.heading),
          paragraphs: splitLines(section.paragraphsText),
          ...(items.length ? { items } : {}),
        };
      })
      .filter(section => section.heading || section.paragraphs.length || (section.items && section.items.length));
    const payload = {
      palette: { ...draft.palette },
      typography: {
        fontKey: draft.typography.fontKey,
        ...(draft.typography.googleFontName ? { googleFontName: draft.typography.googleFontName } : {}),
      },
      texts: { ...draft.texts },
      privacyPolicy: { title: normalizeString(draft.privacyPolicy.title), sections },
    };
    siteDesign.value = await updateSiteProjectDesign(projectId, { payload });
    isEditingDesign.value = false;
    designDraft.value = null;
    infoMessage.value = 'Шаблон сохранён.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    designSaving.value = false;
  }
};

const addIdentityCategory = async () => {
  const projectId = normalizeString(project.value?.id);
  const identityId = normalizeString(siteIdentity.value?.id);
  if (!projectId || !identityId || identityCategorySaving.value) return;

  const payload = {
    name: normalizeString(categoryDraft.value.name),
    slug: normalizeString(categoryDraft.value.slug),
    description: normalizeString(categoryDraft.value.description),
  };
  if (!payload.name || !payload.slug) {
    uiError.value = 'Для категории обязательны name и slug.';
    return;
  }

  identityCategorySaving.value = true;
  clearMessages();
  try {
    const updatedIdentity = await addSiteProjectIdentityCategory(projectId, payload);
    setIdentity(updatedIdentity);
    isAddingCategory.value = false;
    editingCategoryId.value = '';
    categoryDraft.value = {
      name: '',
      slug: '',
      description: '',
    };
    infoMessage.value = 'Категория добавлена.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    identityCategorySaving.value = false;
  }
};

const saveCategoryEdits = async category => {
  const projectId = normalizeString(project.value?.id);
  const categoryKey = normalizeString(category?.id || category?.slug);
  if (!projectId || !categoryKey || !isEditingCategory(category) || identityCategorySaving.value) return;

  const payload = {
    name: normalizeString(categoryEditDraft.value.name),
    slug: normalizeString(categoryEditDraft.value.slug),
    description: normalizeString(categoryEditDraft.value.description),
  };
  if (!payload.name || !payload.slug) {
    uiError.value = 'Для категории обязательны name и slug.';
    return;
  }

  identityCategorySaving.value = true;
  clearMessages();
  try {
    const nextCategories = identityCategories.value.map(item => {
      const itemKey = normalizeString(item?.id || item?.slug);
      if (itemKey !== categoryKey) {
        return {
          name: normalizeString(item?.name),
          slug: normalizeString(item?.slug),
          description: normalizeString(item?.description),
        };
      }
      return payload;
    });
    await replaceIdentityCategories(projectId, nextCategories);
    editingCategoryId.value = '';
    categoryEditDraft.value = {
      name: '',
      slug: '',
      description: '',
    };
    infoMessage.value = 'Категория обновлена.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    identityCategorySaving.value = false;
  }
};

const deleteIdentityCategory = async categoryId => {
  const projectId = normalizeString(project.value?.id);
  const normalizedCategoryId = normalizeString(categoryId);
  if (!projectId || !normalizedCategoryId || deletingCategoryId.value) return;

  deletingCategoryId.value = normalizedCategoryId;
  clearMessages();
  try {
    await deleteSiteProjectIdentityCategory(projectId, normalizedCategoryId);
    await loadIdentity(projectId, { silentNotFound: false });
    if (editingCategoryId.value === normalizedCategoryId) {
      editingCategoryId.value = '';
      categoryEditDraft.value = {
        name: '',
        slug: '',
        description: '',
      };
    }
    infoMessage.value = 'Категория удалена.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    deletingCategoryId.value = '';
  }
};

const goToStepThree = async () => {
  if (!canOpenStepThree.value) return;
  await openStep(3);
};

const generateDomainsByAi = async () => {
  const projectId = normalizeString(project.value?.id);
  const identityText = normalizeString(localizedSiteIdentityPromptLine.value);
  if (!projectId || !identityText || !canGenerateDomains.value) return;

  generatingDomains.value = true;
  clearMessages();
  try {
    const aiMessage = await executeAiProfile({
      profileKey: AI_PROFILE_STEP_THREE_KEY,
      promptVariables: {
        site_identity: identityText,
        preferred_tlds: selectedTlds.value.join(', '),
        number: 20,
      },
    });

    const aiPayload = parseAiPayload(aiMessage?.content);
    const mappedDomains = mapAiDomainsToDto(aiPayload);
    if (!mappedDomains.length) {
      throw new Error('AI не вернул валидный список доменов.');
    }

    const updatedProject = normalizeProject(await saveSiteProjectDomains(projectId, { domains: mappedDomains }));
    project.value = updatedProject;
    clearDomainAvailabilityState();
    isAddingDomain.value = false;
    newDomainName.value = '';
    infoMessage.value = `Сформирован список доменов: ${updatedProject.domains.length}.`;
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    generatingDomains.value = false;
  }
};

const selectDomain = async domain => {
  const projectId = normalizeString(project.value?.id);
  const domainId = normalizeString(domain?.id);
  if (!projectId || !domainId || selectingDomainId.value) return;
  if (normalizeString(project.value?.selectedDomainId) === domainId) return;

  selectingDomainId.value = domainId;
  clearMessages();
  try {
    const updatedProject = normalizeProject(await selectSiteProjectDomain(projectId, { domainId }));
    project.value = updatedProject;
    infoMessage.value = 'Основной домен выбран.';
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    selectingDomainId.value = '';
  }
};

const handleDomainCardClick = domain => {
  if (isDeletingDomain.value || isCreatingDomain.value) return;
  void selectDomain(domain);
};

const addDomain = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || !isAddingDomain.value || isCreatingDomain.value) return;

  const domainName = normalizeDomainNameCandidate(newDomainName.value);
  if (!domainName || !isValidDomainName(domainName)) {
    uiError.value = 'Введите валидное доменное имя.';
    return;
  }
  const duplicateName = domains.value.some(item => normalizeString(item?.domainName).toLowerCase() === domainName.toLowerCase());
  if (duplicateName) {
    uiError.value = 'Такое доменное имя уже есть в проекте.';
    return;
  }

  isCreatingDomain.value = true;
  clearMessages();
  try {
    const maxOrder = domains.value.reduce((maxValue, item) => Math.max(maxValue, Number(item?.domainOrder) || 0), 0);
    const payload = {
      domainOrder: maxOrder + 1,
      domainName,
      isAvailable: null,
    };

    const updatedProject = normalizeProject(await addSiteProjectDomain(projectId, payload));
    project.value = updatedProject;
    isAddingDomain.value = false;
    newDomainName.value = '';
    infoMessage.value = 'Домен добавлен.';
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    isCreatingDomain.value = false;
  }
};

const deleteDomain = async domain => {
  const projectId = normalizeString(project.value?.id);
  const domainId = normalizeString(domain?.id);
  if (!projectId || !domainId || deletingDomainId.value) return;

  deletingDomainId.value = domainId;
  clearMessages();
  try {
    await deleteSiteProjectDomain(projectId, domainId);
    const refreshedProject = normalizeProject(await getSiteProject(projectId));
    project.value = refreshedProject;
    infoMessage.value = 'Домен удален.';
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    deletingDomainId.value = '';
  }
};

const goToStepFour = async () => {
  if (!canOpenStepFour.value) return;
  await openStep(4);
};

const selectCurrentLocalization = async () => {
  const projectId = normalizeString(project.value?.id);
  const localId = normalizeString(localizationRecord.value?.id);
  if (!projectId || selectingLocalizationId.value) return;
  if (!localId) return;
  if (normalizeString(project.value?.selectedLocalId) === localId) return;

  selectingLocalizationId.value = localId;
  clearMessages();
  try {
    const updatedProject = normalizeProject(await selectSiteProjectLocalization(projectId, { localId }));
    project.value = updatedProject;
    projectDefaultLocaleCode.value = normalizeString(localizationRecord.value?.languageCode).toLowerCase() || projectDefaultLocaleCode.value;
    if (normalizeString(localizationRecord.value?.languageCode)) {
      selectedLocalizationLanguageCode.value = normalizeString(localizationRecord.value.languageCode).toLowerCase();
    }
    applyPreferredTldsByLocale(selectedLocaleForDomains.value);
    infoMessage.value = 'Локализация выбрана.';
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    selectingLocalizationId.value = '';
  }
};

const requestDeleteCurrentLocalization = () => {
  const localizationId = normalizeString(localizationRecord.value?.id);
  const languageCode = normalizeString(localizationRecord.value?.languageCode).toLowerCase();
  if (!localizationId || !languageCode || deletingLocalizationLanguageCode.value) return;
  localizationDeleteCandidate.value = {
    id: localizationId,
    languageCode,
  };
};

const closeDeleteLocalizationModal = () => {
  if (deletingLocalizationLanguageCode.value) return;
  localizationDeleteCandidate.value = null;
};

const confirmDeleteLocalization = async () => {
  const projectId = normalizeString(project.value?.id);
  const localizationId = normalizeString(localizationDeleteCandidate.value?.id);
  const languageCode = normalizeString(localizationDeleteCandidate.value?.languageCode).toLowerCase();
  if (!projectId || !localizationId || !languageCode || deletingLocalizationLanguageCode.value) return;

  deletingLocalizationLanguageCode.value = languageCode;
  clearMessages();
  try {
    await deleteSiteProjectLocalization(projectId, localizationId);
    localizationDeleteCandidate.value = null;
    await loadProject(projectId, { preserveActiveStep: true });
    await loadProjects();
    infoMessage.value = `Локализация ${languageCode.toUpperCase()} удалена.`;
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    deletingLocalizationLanguageCode.value = '';
  }
};

const generateLocalizationByAi = async () => {
  const projectId = normalizeString(project.value?.id);
  const languageOption = selectedLocalizationOption.value;
  if (!projectId || !languageOption || !canGenerateLocalization.value) return;

  localizationGenerating.value = true;
  clearMessages();
  try {
    const projectData = buildLocalizationProjectData();
    const isSourceLanguage = normalizeString(languageOption.code).toLowerCase() === (projectSourceLocale.value || 'ru');
    let mappedLocalization = null;
    if (isSourceLanguage) {
      mappedLocalization = mapLocalizationPayload(projectData);
    } else {
      const aiMessage = await executeAiProfile({
        profileKey: AI_PROFILE_STEP_FOUR_KEY,
        promptVariables: {
          project_data: projectData,
          target_language: languageOption.targetLanguage,
        },
      });
      const aiPayload = parseAiPayload(aiMessage?.content);
      mappedLocalization = mapLocalizationPayload(aiPayload);
    }

    const savedLocalization = await createSiteProjectLocalization(projectId, {
      languageCode: languageOption.code,
      forceRegenerate: true,
      localizationData: mappedLocalization,
    });

    localizationRecord.value = normalizeLocalizationRecord(savedLocalization);
    localizationData.value = mapLocalizationPayload(resolveLocalizationDataPayload(savedLocalization));
    setResolvedContentMeta({
      localizationId: localizationRecord.value?.id,
      requestedLocale: languageOption.code,
      resolvedLocale: languageOption.code,
      sourceLocale: projectSourceLocale.value,
      localized: true,
      fallbackToSource: false,
      stale: localizationRecord.value?.stale === true,
    });
    infoMessage.value = `Локализация на ${languageOption.label} сохранена. Выберите её для перехода к доменам.`;
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    localizationGenerating.value = false;
  }
};

const isLogoProcessingStatus = status => {
  const normalized = normalizeString(status).toLowerCase();
  return normalized === 'queued' || normalized === 'processing';
};

const fetchLogoStatus = async (projectId, options = {}) => {
  const normalizedProjectId = normalizeString(projectId);
  if (!normalizedProjectId) {
    logoImage.value = null;
    return null;
  }

  const silentNotFound = options?.silentNotFound !== false;
  const silent = options?.silent === true;
  if (!silent) {
    logoLoading.value = true;
  }

  try {
    const payload = await getSiteProjectLogo(normalizedProjectId);
    const normalized = normalizeSiteImage(payload);
    logoImage.value = normalized;
    return normalized;
  } catch (error) {
    if (Number(error?.response?.status) === 404 && silentNotFound) {
      const emptyState = normalizeSiteImage({ projectId: normalizedProjectId, imageType: 'logo', status: 'not_started' });
      logoImage.value = emptyState;
      return emptyState;
    }
    throw error;
  } finally {
    if (!silent) {
      logoLoading.value = false;
    }
  }
};

const scheduleLogoPolling = projectId => {
  const normalizedProjectId = normalizeString(projectId);
  stopLogoPolling();
  if (!normalizedProjectId) return;

  logoPolling.value = true;
  logoPollingTimeoutId = setTimeout(async () => {
    logoPollingTimeoutId = null;
    const currentProjectId = normalizeString(project.value?.id);
    if (currentProjectId !== normalizedProjectId || activeStep.value !== 5) {
      logoPolling.value = false;
      return;
    }

    try {
      const latestLogo = await fetchLogoStatus(normalizedProjectId, { silent: true, silentNotFound: true });
      if (isLogoProcessingStatus(latestLogo?.status)) {
        scheduleLogoPolling(normalizedProjectId);
        return;
      }
      logoPolling.value = false;
    } catch (error) {
      logoPolling.value = false;
      uiError.value = extractError(error);
    }
  }, LOGO_POLL_INTERVAL_MS);
};

const loadLogoForCurrentProject = async options => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId) {
    clearLogoState();
    return;
  }

  try {
    const latestLogo = await fetchLogoStatus(projectId, options);
    if (isLogoProcessingStatus(latestLogo?.status) && activeStep.value === 5) {
      scheduleLogoPolling(projectId);
      return;
    }
    stopLogoPolling();
  } catch (error) {
    stopLogoPolling();
    uiError.value = extractError(error);
  }
};

const fetchFaviconStatus = async (projectId, options = {}) => {
  const normalizedProjectId = normalizeString(projectId);
  if (!normalizedProjectId) {
    faviconImage.value = null;
    return null;
  }

  const silentNotFound = options?.silentNotFound !== false;
  const silent = options?.silent === true;
  if (!silent) {
    faviconLoading.value = true;
  }

  try {
    const payload = await getSiteProjectFavicon(normalizedProjectId);
    const normalized = normalizeSiteImage(payload);
    faviconImage.value = normalized;
    return normalized;
  } catch (error) {
    if (Number(error?.response?.status) === 404 && silentNotFound) {
      const emptyState = normalizeSiteImage({ projectId: normalizedProjectId, imageType: 'favicon', status: 'not_started' });
      faviconImage.value = emptyState;
      return emptyState;
    }
    throw error;
  } finally {
    if (!silent) {
      faviconLoading.value = false;
    }
  }
};

const scheduleFaviconPolling = projectId => {
  const normalizedProjectId = normalizeString(projectId);
  stopFaviconPolling();
  if (!normalizedProjectId) return;

  faviconPolling.value = true;
  faviconPollingTimeoutId = setTimeout(async () => {
    faviconPollingTimeoutId = null;
    const currentProjectId = normalizeString(project.value?.id);
    if (currentProjectId !== normalizedProjectId || activeStep.value !== 6) {
      faviconPolling.value = false;
      return;
    }

    try {
      const latest = await fetchFaviconStatus(normalizedProjectId, { silent: true, silentNotFound: true });
      if (isLogoProcessingStatus(latest?.status)) {
        scheduleFaviconPolling(normalizedProjectId);
        return;
      }
      faviconPolling.value = false;
    } catch (error) {
      faviconPolling.value = false;
      uiError.value = extractError(error);
    }
  }, FAVICON_POLL_INTERVAL_MS);
};

const loadFaviconForCurrentProject = async options => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId) {
    clearFaviconState();
    return;
  }

  try {
    const latest = await fetchFaviconStatus(projectId, options);
    if (isLogoProcessingStatus(latest?.status) && activeStep.value === 6) {
      scheduleFaviconPolling(projectId);
      return;
    }
    stopFaviconPolling();
  } catch (error) {
    stopFaviconPolling();
    uiError.value = extractError(error);
  }
};

// --- Logo editor (freeform brand text over the AI icon) ----------------------
const clampNumber = (value, minValue, maxValue) => Math.min(maxValue, Math.max(minValue, value));

const normalizeHexColor = value => {
  let hex = normalizeString(value).trim();
  if (!hex) return '';
  if (!hex.startsWith('#')) hex = `#${hex}`;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    hex = `#${hex.slice(1).split('').map(ch => ch + ch).join('')}`;
  }
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toLowerCase() : '';
};

const logoDefaultSubtitle = () => normalizeString(localizationData.value?.site_identity?.siteTitle);

const defaultLogoBrandText = () => normalizeString(selectedDomain.value?.domainName) || 'Logo';

const handleLogoEditorIconLoad = () => {
  const imageEl = logoEditorIconRef.value;
  if (!imageEl) return;
  logoEditorIconNatural.value = {
    width: Number(imageEl.naturalWidth) || 0,
    height: Number(imageEl.naturalHeight) || 0,
  };
  nextTick(() => recenterLogoEditor());
};

const initLogoEditorFromImage = () => {
  const image = logoImage.value;
  if (!image || image.status !== 'completed' || !normalizeString(image.iconPublicUrl)) return;
  // Icon.
  logoEditorIconScale.value = Number.isFinite(Number(image.logoIconScale)) && Number(image.logoIconScale) > 0
    ? Number(image.logoIconScale)
    : 1;
  logoEditorIconOffset.value = {
    x: Number.isFinite(Number(image.logoIconOffsetX)) ? Number(image.logoIconOffsetX) : 0,
    y: Number.isFinite(Number(image.logoIconOffsetY)) ? Number(image.logoIconOffsetY) : 0,
  };
  // Brand layer.
  logoEditorText.value = normalizeString(image.logoText) || defaultLogoBrandText();
  const fontKey = normalizeString(image.logoFontKey);
  logoEditorFontKey.value = LOGO_FONT_KEYS.includes(fontKey) ? fontKey : 'pt-sans';
  logoEditorColor.value = normalizeHexColor(image.logoTextColor) || '#222222';
  const fontSize = Number(image.logoFontSize);
  logoEditorFontSize.value = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 200;
  logoEditorOffset.value = {
    x: Number.isFinite(Number(image.logoTextOffsetX)) ? Number(image.logoTextOffsetX) : 0,
    y: Number.isFinite(Number(image.logoTextOffsetY)) ? Number(image.logoTextOffsetY) : 0,
  };
  // Independent subtitle layer.
  const subtitle = normalizeString(image.logoSubtitleText);
  logoEditorHasSubtitle.value = Boolean(subtitle);
  logoEditorSubtitle.value = subtitle || logoDefaultSubtitle();
  const subtitleFontKey = normalizeString(image.logoSubtitleFontKey);
  logoEditorSubtitleFontKey.value = LOGO_FONT_KEYS.includes(subtitleFontKey) ? subtitleFontKey : logoEditorFontKey.value;
  logoEditorSubtitleColor.value = normalizeHexColor(image.logoSubtitleColor) || logoEditorColor.value;
  const subtitleFontSize = Number(image.logoSubtitleFontSize);
  logoEditorSubtitleFontSize.value = Number.isFinite(subtitleFontSize) && subtitleFontSize > 0
    ? subtitleFontSize
    : Math.round(logoEditorFontSize.value * 0.55);
  logoEditorSubtitleOffset.value = {
    x: Number.isFinite(Number(image.logoSubtitleOffsetX)) ? Number(image.logoSubtitleOffsetX) : logoEditorOffset.value.x,
    y: Number.isFinite(Number(image.logoSubtitleOffsetY))
      ? Number(image.logoSubtitleOffsetY)
      : logoEditorOffset.value.y + Math.round(logoEditorFontSize.value * 1.12),
  };
  nextTick(() => recenterLogoEditor());
};

const logoEditorOffsetRef = target => {
  if (target === 'icon') return logoEditorIconOffset;
  if (target === 'subtitle') return logoEditorSubtitleOffset;
  return logoEditorOffset;
};
const logoEditorFontSizeRef = target => (target === 'subtitle' ? logoEditorSubtitleFontSize : logoEditorFontSize);

const resetLogoEditorDrag = () => {
  logoEditorDrag.value = {
    active: false, pointerId: null, target: '', mode: 'move',
    anchorX: 0, anchorY: 0, startX: 0, startY: 0, startSize: 0, startDist: 0,
  };
};

const startLogoEditorDrag = (event, target) => {
  if (isLogoBusy.value) return;
  const offset = logoEditorOffsetRef(target).value;
  logoEditorDrag.value = {
    active: true,
    pointerId: event.pointerId,
    target,
    mode: 'move',
    anchorX: Number(event.clientX) || 0,
    anchorY: Number(event.clientY) || 0,
    startX: Number(offset.x) || 0,
    startY: Number(offset.y) || 0,
    startSize: 0,
    startDist: 0,
  };
  event.currentTarget?.setPointerCapture?.(event.pointerId);
};

// Resize by dragging the corner handle: font size scales with the pointer's distance from the
// layer's top-left anchor, so the text box grows like a Paint selection.
const startLogoEditorResize = (event, target) => {
  if (isLogoBusy.value) return;
  const layerEl = event.currentTarget?.parentElement;
  if (!layerEl) return;
  const rect = layerEl.getBoundingClientRect();
  const px = Number(event.clientX) || 0;
  const py = Number(event.clientY) || 0;
  logoEditorDrag.value = {
    active: true,
    pointerId: event.pointerId,
    target,
    mode: 'resize',
    anchorX: rect.left,
    anchorY: rect.top,
    startX: 0,
    startY: 0,
    startSize: target === 'icon'
      ? (Number(logoEditorIconScale.value) || 1)
      : (Number(logoEditorFontSizeRef(target).value) || 0),
    startDist: Math.max(1, Math.hypot(px - rect.left, py - rect.top)),
  };
  layerEl.setPointerCapture?.(event.pointerId);
};

const moveLogoEditorDrag = event => {
  const drag = logoEditorDrag.value;
  if (!drag.active || event.pointerId !== drag.pointerId) return;
  if (drag.mode === 'resize') {
    const px = Number(event.clientX) || 0;
    const py = Number(event.clientY) || 0;
    const dist = Math.max(1, Math.hypot(px - drag.anchorX, py - drag.anchorY));
    const factor = dist / drag.startDist;
    if (drag.target === 'icon') {
      logoEditorIconScale.value = Math.min(5, Math.max(0.1, drag.startSize * factor));
    } else {
      logoEditorFontSizeRef(drag.target).value = clampNumber(Math.round(drag.startSize * factor), EDITOR_MIN_FONT, EDITOR_MAX_FONT);
    }
    return;
  }
  const scale = logoEditorScale.value || 0.2;
  const deltaX = ((Number(event.clientX) || 0) - drag.anchorX) / scale;
  const deltaY = ((Number(event.clientY) || 0) - drag.anchorY) / scale;
  logoEditorOffsetRef(drag.target).value = {
    x: Math.round(drag.startX + deltaX),
    y: Math.round(drag.startY + deltaY),
  };
};

const endLogoEditorDrag = event => {
  const drag = logoEditorDrag.value;
  if (drag.pointerId != null && event.pointerId !== drag.pointerId) return;
  if (event.currentTarget?.hasPointerCapture?.(drag.pointerId)) {
    event.currentTarget.releasePointerCapture(drag.pointerId);
  }
  resetLogoEditorDrag();
};

const addLogoEditorSubtitle = () => {
  logoEditorHasSubtitle.value = true;
  if (!normalizeString(logoEditorSubtitle.value)) {
    logoEditorSubtitle.value = logoDefaultSubtitle();
  }
};

const removeLogoEditorSubtitle = () => {
  logoEditorHasSubtitle.value = false;
};

const toggleLogoColorPopover = target => {
  logoEditorColorPopover.value = logoEditorColorPopover.value === target ? '' : target;
};

const closeLogoColorPopover = () => {
  logoEditorColorPopover.value = '';
};

const setLogoColor = (target, color, close = true) => {
  const value = normalizeHexColor(color) || color;
  if (target === 'subtitle') {
    logoEditorSubtitleColor.value = value;
  } else {
    logoEditorColor.value = value;
  }
  if (close) closeLogoColorPopover();
};

const selectLogoGeneration = async generationId => {
  const projectId = normalizeString(project.value?.id);
  const id = normalizeString(generationId);
  if (!projectId || !id || isLogoBusy.value) return;
  if (id === normalizeString(logoImage.value?.selectedGenerationId)) return;

  logoSelectingGeneration.value = id;
  clearMessages();
  stopLogoPolling();
  try {
    const updated = normalizeSiteImage(await selectSiteProjectLogoGeneration(projectId, { generationId: id }));
    logoImage.value = updated;
    infoMessage.value = 'Логотип выбран.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    logoSelectingGeneration.value = '';
  }
};

const applyLogoEditor = async () => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || isLogoBusy.value || logoEditorApplying.value) return;

  logoEditorApplying.value = true;
  clearMessages();
  stopLogoPolling();
  try {
    const hasSubtitle = logoEditorHasSubtitle.value && Boolean(normalizeString(logoEditorSubtitle.value));
    const payload = {
      iconScale: Number(logoEditorIconScale.value) || 1,
      iconOffsetX: Math.round(Number(logoEditorIconOffset.value.x) || 0),
      iconOffsetY: Math.round(Number(logoEditorIconOffset.value.y) || 0),
      text: normalizeString(logoEditorText.value),
      fontKey: logoEditorFontKey.value,
      textColor: logoEditorColor.value,
      fontSize: Math.round(Number(logoEditorFontSize.value) || 200),
      textOffsetX: Math.round(Number(logoEditorOffset.value.x) || 0),
      textOffsetY: Math.round(Number(logoEditorOffset.value.y) || 0),
      subtitleText: hasSubtitle ? normalizeString(logoEditorSubtitle.value) : '',
      subtitleFontKey: logoEditorSubtitleFontKey.value,
      subtitleColor: logoEditorSubtitleColor.value,
      subtitleFontSize: Math.round(Number(logoEditorSubtitleFontSize.value) || 110),
      subtitleOffsetX: Math.round(Number(logoEditorSubtitleOffset.value.x) || 0),
      subtitleOffsetY: Math.round(Number(logoEditorSubtitleOffset.value.y) || 0),
    };
    const updated = normalizeSiteImage(await composeSiteProjectLogo(projectId, payload));
    logoImage.value = updated;
    infoMessage.value = 'Логотип обновлён.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    logoEditorApplying.value = false;
  }
};

watch(
  () => [logoImage.value?.id, logoImage.value?.status, logoImage.value?.publicUrl],
  () => {
    initLogoEditorFromImage();
  }
);

const buildLogoSiteIdentityPrompt = () => {
  const identity = toPlainObject(localizationData.value?.site_identity);
  const siteTitle = normalizeString(identity.siteTitle);
  const siteTagline = normalizeString(identity.siteTagline);
  const homeDescription = normalizeString(identity.homeDescription);

  return [
    `Название сайта: ${siteTitle}`,
    `Tagline: ${siteTagline}`,
    `Описание главной: ${homeDescription}`,
  ].join('\n');
};

const generateLogo = async (options = {}) => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || logoGenerating.value) return;

  logoGenerating.value = true;
  clearMessages();
  stopLogoPolling();
  try {
    const forceRegenerate = options?.forceRegenerate === true;
    const promptVariables = {
      domain: normalizeString(selectedDomain.value?.domainName),
      language: logoPromptLanguage.value,
      site_identity: buildLogoSiteIdentityPrompt(),
      site_title: logoDefaultSubtitle(),
    };
    const payload = { forceRegenerate, promptVariables };

    const started = normalizeSiteImage(await generateSiteProjectLogo(projectId, payload));
    logoImage.value = started;
    infoMessage.value = forceRegenerate
      ? 'Повторная генерация логотипа запущена.'
      : 'Генерация логотипа запущена.';

    if (isLogoProcessingStatus(started.status)) {
      scheduleLogoPolling(projectId);
    } else {
      await loadLogoForCurrentProject({ silentNotFound: true });
    }
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    logoGenerating.value = false;
  }
};

const retryGenerateLogo = async () => {
  await generateLogo({ forceRegenerate: true });
};

const suggestAnotherLogo = async () => {
  await generateLogo({ forceRegenerate: true });
};

const triggerLogoUploadSelect = () => {
  if (isLogoBusy.value || !canOpenStepFive.value) return;
  logoUploadInputRef.value?.click();
};

const handleLogoUploadChange = async event => {
  const inputEl = event?.target;
  const file = inputEl?.files?.[0] || null;
  if (inputEl) inputEl.value = '';
  if (!file) return;
  await uploadLogoFile(file);
};

const uploadLogoFile = async file => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || isLogoBusy.value || !file) return;

  logoUploading.value = true;
  clearMessages();
  stopLogoPolling();
  try {
    const uploaded = normalizeSiteImage(await uploadSiteProjectLogo(projectId, file));
    logoImage.value = uploaded;
    infoMessage.value = 'Логотип загружен.';
    if (isLogoProcessingStatus(uploaded.status)) {
      scheduleLogoPolling(projectId);
    }
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    logoUploading.value = false;
  }
};

const generateFavicon = async (options = {}) => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || faviconGenerating.value) return;

  faviconGenerating.value = true;
  clearMessages();
  stopFaviconPolling();
  try {
    const forceRegenerate = options?.forceRegenerate === true;
    const payload = {
      forceRegenerate,
      promptVariables: {
        domain: normalizeString(selectedDomain.value?.domainName),
        language: logoPromptLanguage.value,
        site_identity: buildLogoSiteIdentityPrompt(),
      },
    };
    const started = normalizeSiteImage(await generateSiteProjectFavicon(projectId, payload));
    faviconImage.value = started;
    infoMessage.value = forceRegenerate
      ? 'Повторная генерация favicon запущена.'
      : 'Генерация favicon запущена.';

    if (isLogoProcessingStatus(started.status)) {
      scheduleFaviconPolling(projectId);
    } else {
      await loadFaviconForCurrentProject({ silentNotFound: true });
    }
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    faviconGenerating.value = false;
  }
};

const retryGenerateFavicon = async () => {
  await generateFavicon({ forceRegenerate: true });
};

const suggestAnotherFavicon = async () => {
  await generateFavicon({ forceRegenerate: true });
};

const triggerFaviconUploadSelect = () => {
  if (isFaviconBusy.value || !canOpenStepSix.value) return;
  faviconUploadInputRef.value?.click();
};

const handleFaviconUploadChange = async event => {
  const inputEl = event?.target;
  const file = inputEl?.files?.[0] || null;
  if (inputEl) inputEl.value = '';
  if (!file) return;
  await uploadFaviconFile(file);
};

const uploadFaviconFile = async file => {
  const projectId = normalizeString(project.value?.id);
  if (!projectId || isFaviconBusy.value || !file) return;

  faviconUploading.value = true;
  clearMessages();
  stopFaviconPolling();
  try {
    const uploaded = normalizeSiteImage(await uploadSiteProjectFavicon(projectId, file));
    faviconImage.value = uploaded;
    infoMessage.value = 'Favicon загружен.';
    if (isLogoProcessingStatus(uploaded.status)) {
      scheduleFaviconPolling(projectId);
    }
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    faviconUploading.value = false;
  }
};

const goToStepFive = async () => {
  if (!canOpenStepFive.value) return;
  await openStep(5);
};

const goToStepSix = async () => {
  if (!canOpenStepSix.value) return;
  await openStep(6);
};

const goToStepSeven = async () => {
  if (!canOpenStepSeven.value) return;
  await openStep(7);
};

const goToStepEight = async () => {
  if (!canOpenStepEight.value) return;
  await openStep(8);
};

const finishConceptWizard = async () => {
  await goToProjectsList();
};

const startNewProject = async () => {
  resetWizard();
  await router.replace({ name: 'WordPressConceptWizardNew' });
};

const goToProjectsList = async () => {
  await router.replace({ name: 'WordPressConceptProjects' });
};

const generateConcepts = async () => {
  if (hasConcepts.value) return;
  const topicName = normalizeString(selectedTopicName.value);
  if (!topicName) {
    uiError.value = 'Выберите тематику сайта.';
    return;
  }

  isGenerating.value = true;
  clearMessages();
  try {
    const sourceLocale = 'ru';
    const targetLocales = LOCALIZATION_LANGUAGE_OPTIONS
      .map(item => normalizeString(item.code).toLowerCase())
      .filter(code => code && code !== sourceLocale);
    const createdProject = normalizeProject(await createSiteProject({
      topicName,
      sourceLocale,
      targetLocales,
      languageCode: sourceLocale,
    }));
    if (!createdProject.id) throw new Error('Бэкенд не вернул id созданного проекта.');

    project.value = createdProject;
    selectedConceptId.value = '';
    activeStep.value = 1;

    const aiMessage = await executeAiProfile({
      profileKey: AI_PROFILE_STEP_ONE_KEY,
      promptVariables: {
        topic_name: topicName,
        number: 3,
        language: 'Russian',
      },
    });

    const aiPayload = parseAiPayload(aiMessage?.content);
    const aiConcepts = extractConceptsArray(aiPayload);
    if (!aiConcepts.length) throw new Error('AI не вернул список концепций.');

    const conceptsForSave = aiConcepts.map((item, index) => mapAiConceptToSaveDto(item, index));
    const savedProject = normalizeProject(await saveSiteProjectConcepts(createdProject.id, { concepts: conceptsForSave }));

    project.value = savedProject;
    selectedConceptId.value = savedProject.selectedConceptId || savedProject.concepts[0]?.id || '';
    infoMessage.value = `Готово: сгенерировано концепций ${savedProject.concepts.length}.`;

    await syncRouteProjectId(savedProject.id);
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    isGenerating.value = false;
  }
};

const setSelectedConcept = concept => {
  const conceptId = normalizeString(concept?.id);
  if (!conceptId) {
    uiError.value = 'Невозможно выбрать концепцию: отсутствует conceptId.';
    return;
  }
  uiError.value = '';
  selectedConceptId.value = conceptId;
};

const isSelectedConcept = concept => normalizeString(concept?.id) === normalizeString(selectedConceptId.value);

const isEditingConcept = concept => normalizeString(concept?.id) === normalizeString(editingConceptId.value);

const createConceptDraft = concept => ({
  conceptName: normalizeString(concept?.conceptName),
  conceptAngle: normalizeString(concept?.conceptAngle),
  positioning: normalizeString(concept?.positioning),
  differentiation: normalizeString(concept?.differentiation),
  toneStyle: normalizeString(concept?.toneStyle),
  toneExpertiseLevel: normalizeString(concept?.toneExpertiseLevel),
  targetAudience: toMultilineText(concept?.targetAudience),
  articleTypes: toMultilineText(concept?.articleTypes),
  contentNotAllowed: toMultilineText(concept?.contentNotAllowed),
});

const startEditConcept = concept => {
  const conceptId = normalizeString(concept?.id);
  if (!conceptId || isDeletingConcept.value || isSelectingConcept.value || isSavingConcept.value) return;
  if (isEditingAnyConcept.value && editingConceptId.value !== conceptId) return;
  editingConceptId.value = conceptId;
  conceptDraft.value = createConceptDraft(concept);
  uiError.value = '';
};

const cancelEditConcept = () => {
  if (isSavingConcept.value) return;
  editingConceptId.value = '';
  conceptDraft.value = null;
};

const saveConceptEdits = async concept => {
  const projectId = normalizeString(project.value?.id);
  const conceptId = normalizeString(concept?.id);
  if (!projectId || !conceptId || !isEditingConcept(concept) || !conceptDraft.value || isSavingConcept.value) return;

  savingConceptId.value = conceptId;
  clearMessages();
  try {
    const draft = conceptDraft.value;
    const conceptOrder = Number(concept?.conceptOrder);
    const payload = {
      conceptOrder: Number.isFinite(conceptOrder) && conceptOrder > 0 ? Math.round(conceptOrder) : 1,
      conceptName: normalizeString(draft.conceptName) || `Концепция ${concept?.conceptOrder || 1}`,
      conceptAngle: normalizeString(draft.conceptAngle),
      positioning: normalizeString(draft.positioning),
      differentiation: normalizeString(draft.differentiation),
      targetAudience: multilineToArray(draft.targetAudience),
      toneStyle: normalizeString(draft.toneStyle),
      toneExpertiseLevel: normalizeString(draft.toneExpertiseLevel),
      toneAvoid: toStringArray(concept?.toneAvoid),
      articleTypes: multilineToArray(draft.articleTypes),
      contentAllowed: toStringArray(concept?.contentAllowed),
      contentNotAllowed: multilineToArray(draft.contentNotAllowed),
      requiredDisclaimers: toStringArray(concept?.requiredDisclaimers),
    };

    await updateSiteProjectConcept(projectId, conceptId, payload);
    await loadProject(projectId, { preserveActiveStep: true });
    await loadProjects();
    infoMessage.value = 'Концепция обновлена.';
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    savingConceptId.value = '';
  }
};

const requestDeleteConcept = concept => {
  const conceptId = normalizeString(concept?.id);
  if (!conceptId || isDeletingConcept.value || isSelectingConcept.value || isSavingConcept.value || isEditingAnyConcept.value) return;
  conceptDeleteCandidate.value = {
    id: conceptId,
    name: normalizeString(concept?.conceptName) || 'Концепция',
  };
};

const closeDeleteConceptModal = () => {
  if (isDeletingConcept.value) return;
  conceptDeleteCandidate.value = null;
};

const confirmDeleteConcept = async () => {
  const projectId = normalizeString(project.value?.id);
  const conceptId = normalizeString(conceptDeleteCandidate.value?.id);
  if (!projectId || !conceptId || isDeletingConcept.value) return;
  const selectedConceptBeforeDelete = normalizeString(project.value?.selectedConceptId);
  const shouldDeleteSiteProfile = Boolean(selectedConceptBeforeDelete && selectedConceptBeforeDelete === conceptId);

  deletingConceptId.value = conceptId;
  clearMessages();
  try {
    await deleteSiteProjectConcept(projectId, conceptId);
    conceptDeleteCandidate.value = null;
    const refreshedProject = normalizeProject(await getSiteProject(projectId));
    if (!refreshedProject.concepts.length) {
      await deleteSiteProject(projectId);
      await router.replace({ name: 'WordPressConceptProjects' });
      return;
    }

    project.value = refreshedProject;
    if (refreshedProject.topicName) selectedTopicName.value = refreshedProject.topicName;
    selectedConceptId.value = refreshedProject.selectedConceptId || refreshedProject.concepts[0]?.id || '';
    if (shouldDeleteSiteProfile) {
      const wasDeleted = await deleteSiteProfileForProject(projectId);
      infoMessage.value = wasDeleted
        ? 'Концепция удалена. Профиль сайта удален, так как базовая концепция была удалена.'
        : 'Концепция удалена.';
    } else {
      infoMessage.value = 'Концепция удалена.';
    }
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    deletingConceptId.value = '';
  }
};

const goToNextStep = async () => {
  if (!canProceedStepTwo.value) return;
  if (hasUnsavedSelection.value) {
    await confirmConceptSelection();
    return;
  }
  await openStep(2);
};

const confirmConceptSelection = async () => {
  const projectId = normalizeString(project.value?.id);
  const conceptId = normalizeString(selectedConceptId.value);
  if (!projectId || !conceptId) {
    uiError.value = 'Сначала выберите концепцию.';
    return;
  }
  const selectedConceptBeforeChange = normalizeString(project.value?.selectedConceptId);

  isSelectingConcept.value = true;
  clearMessages();
  try {
    const updatedProject = normalizeProject(await selectSiteProjectConcept(projectId, { conceptId }));
    project.value = updatedProject;
    const selectedConceptAfterChange = normalizeString(updatedProject.selectedConceptId || conceptId);
    selectedConceptId.value = selectedConceptAfterChange;
    const selectedConceptChanged = Boolean(
      selectedConceptBeforeChange
      && selectedConceptAfterChange
      && selectedConceptBeforeChange !== selectedConceptAfterChange
    );
    if (selectedConceptChanged) {
      const wasDeleted = await deleteSiteProfileForProject(projectId);
      infoMessage.value = wasDeleted
        ? 'Основная концепция выбрана. Профиль сайта удален, так как концепция изменена.'
        : 'Основная концепция выбрана.';
    } else {
      infoMessage.value = 'Основная концепция выбрана.';
    }
    await openStep(2);
    await loadProjects();
  } catch (error) {
    uiError.value = extractError(error);
  } finally {
    isSelectingConcept.value = false;
  }
};

const openStep = async step => {
  if (step === 1) {
    activeStep.value = 1;
    return;
  }
  if (step === 2 && canOpenStepTwo.value) {
    activeStep.value = 2;
    const projectId = normalizeString(project.value?.id);
    if (projectId && !siteIdentity.value && !identityLoading.value) {
      try {
        await loadIdentity(projectId, { silentNotFound: true });
      } catch (error) {
        uiError.value = extractError(error);
      }
    }
    return;
  }
  if (step === 3 && canOpenStepThree.value) {
    activeStep.value = 3;
    const projectId = normalizeString(project.value?.id);
    let languageCode = 'ru';
    if (normalizeString(project.value?.selectedLocalId)) {
      try {
        const selectedPayload = await getSiteProjectResolvedContent(projectId);
        applyResolvedContentPayload(selectedPayload);
        languageCode = getResolvedLocaleFromPayload(selectedPayload) || 'ru';
      } catch (error) {
        const statusCode = Number(error?.response?.status);
        if (statusCode !== 400 && statusCode !== 404) {
          uiError.value = extractError(error);
        }
        languageCode = 'ru';
      }
    }
    selectedLocalizationLanguageCode.value = languageCode;
    if (projectId && languageCode && !localizationLoading.value) {
      try {
        await loadLocalization(projectId, languageCode, { silentNotFound: true });
      } catch (error) {
        uiError.value = extractError(error);
      }
    }
    return;
  }
  if (step === 4 && canOpenStepFour.value) {
    applyPreferredTldsByLocale(selectedLocaleForDomains.value);
    activeStep.value = 4;
    return;
  }
  if (step === 5 && canOpenStepFive.value) {
    activeStep.value = 5;
    await loadLogoForCurrentProject({ silentNotFound: true });
    return;
  }
  if (step === 6 && canOpenStepSix.value) {
    activeStep.value = 6;
    await loadFaviconForCurrentProject({ silentNotFound: true });
    return;
  }
  if (step === 7 && canOpenStepSeven.value) {
    activeStep.value = 7;
    await loadDesignForCurrentProject();
    return;
  }
  if (step === 8 && canOpenStepEight.value) {
    activeStep.value = 8;
    await loadDesignForCurrentProject();
  }
};

watch(
  () => infoMessage.value,
  message => {
    clearInfoMessageTimer();
    if (!normalizeString(message)) return;
    infoMessageTimeoutId = setTimeout(() => {
      infoMessage.value = '';
      infoMessageTimeoutId = null;
    }, INFO_MESSAGE_TIMEOUT_MS);
  }
);

watch(
  () => route.name,
  async routeName => {
    if (routeName === 'WordPressConceptProjects') {
      stopLogoPolling();
      stopFaviconPolling();
      await loadProjects();
      return;
    }
    await loadTopics();
  }
);

watch(
  () => availableLocalizationOptions.value.map(item => item.code).join(','),
  () => {
    const currentCode = normalizeString(selectedLocalizationLanguageCode.value).toLowerCase();
    const hasCurrent = availableLocalizationOptions.value.some(item => item.code === currentCode);
    if (hasCurrent) return;
    selectedLocalizationLanguageCode.value = availableLocalizationOptions.value[0]?.code || '';
  },
  { immediate: true }
);

watch(
  () => normalizeString(selectedLocalizationLanguageCode.value).toLowerCase(),
  async languageCode => {
    const projectId = normalizeString(project.value?.id);
    if (!projectId || !languageCode || activeStep.value < 3) return;
    try {
      await loadLocalization(projectId, languageCode, { silentNotFound: true });
    } catch (error) {
      uiError.value = extractError(error);
    }
  }
);

watch(
  () => selectedLocaleForDomains.value,
  () => {
    if (activeStep.value < 4) return;
    applyPreferredTldsByLocale(selectedLocaleForDomains.value);
  }
);

watch(
  () => activeStep.value,
  step => {
    if (step !== 5) {
      stopLogoPolling();
    }
    if (step !== 6) {
      stopFaviconPolling();
    }
  }
);

watch(
  () => normalizeString(route.params.projectId),
  async projectId => {
    if (!projectId) {
      resetWizard();
      return;
    }
    await loadProject(projectId);
  },
  { immediate: true }
);

onMounted(async () => {
  // Re-measure logo fonts once the webfonts are actually loaded, so the editor baseline
  // correction uses real metrics instead of the fallback font.
  if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      Object.keys(logoFontMetricsCache).forEach(k => delete logoFontMetricsCache[k]);
      logoFontMetricsVersion.value += 1;
      nextTick(() => recenterLogoEditor());
    });
  }
  window.addEventListener('resize', recenterLogoEditor);
  document.addEventListener('click', closeLogoColorPopover);
  if (isProjectsMode.value) {
    await loadProjects();
    return;
  }
  await loadTopics();
});

onBeforeUnmount(() => {
  clearInfoMessageTimer();
  closeDomainAvailabilityStream();
  stopLogoPolling();
  stopFaviconPolling();
  window.removeEventListener('resize', recenterLogoEditor);
  document.removeEventListener('click', closeLogoColorPopover);
});
</script>

<style scoped>
.wp-concept-page {
  min-height: 100%;
  padding-top: 10px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
}

.wpc-hero {
  margin: 0 20px 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
}

.wpc-hero__title {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.wpc-hero__subtitle {
  color: rgba(232, 242, 255, 0.92);
  font-size: 13px;
}

.wpc-hero__actions {
  flex-wrap: wrap;
}

.tool-notifications {
  position: fixed;
  top: 14px;
  right: 18px;
  z-index: 1100;
  pointer-events: none;
}

.tool-notifications__list {
  display: grid;
  gap: 8px;
}

.tool-notification {
  max-width: min(440px, calc(100vw - 24px));
  border-radius: 10px;
  border: 1px solid;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.35;
  box-shadow: 0 12px 24px rgba(16, 48, 95, 0.18);
  pointer-events: auto;
}

.tool-notification--error {
  border-color: #f3c7ce;
  background: #fff0f2;
  color: #943543;
}

.tool-notification--info {
  border-color: #cfe1fb;
  background: #eef5ff;
  color: #234f87;
}

.tool-toast-enter-active,
.tool-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tool-toast-enter-from,
.tool-toast-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.wpc-surface {
  border: 1px solid #dce7fa !important;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(24, 55, 100, 0.08) !important;
  padding: 12px;
}

.wpc-projects-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.history-headline {
  margin-bottom: 2px;
}

.wpc-projects-head h6 {
  color: #1f2d45;
  font-weight: 700;
}

.wpc-projects-head__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.wpc-projects-error {
  margin-top: 10px;
  border: 1px solid #f3c7ce;
  border-radius: 10px;
  background: #fff0f2;
  color: #943543;
  font-size: 13px;
  padding: 10px;
}

.wpc-projects-list {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.wpc-project-item {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 10px 11px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.wpc-project-item:hover {
  border-color: #bed5f7;
  box-shadow: 0 8px 18px rgba(24, 55, 100, 0.09);
}

.wpc-project-item.active {
  border-color: #b8cdf1;
  box-shadow: 0 0 0 1px #dce7fb inset;
  background: #f7fbff;
}

.wpc-project-item__main {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.wpc-project-item__date {
  color: #5a7094;
  font-size: 12px;
  line-height: 1.2;
}

.wpc-project-item__topic {
  font-size: 17px;
  font-weight: 700;
  color: #213453;
  line-height: 1.3;
  margin: 0;
}

.wpc-project-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-left: -4px;
}

.wpc-project-item__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.wpc-project-item__actions .btn {
  font-weight: 600;
}

.wpc-project-item__actions .btn-outline-primary {
  border-color: #d6deed;
  background: #fff;
  color: #2f3d56;
}

.wpc-projects-head__actions .btn-primary,
.wpc-projects-head__actions .btn-outline-primary {
  border-color: #d6deed;
  background: #fff;
  color: #2f3d56;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 8px;
  padding-right: 8px;
}

.wpc-projects-head__actions .btn-primary:hover,
.wpc-projects-head__actions .btn-outline-primary:hover,
.wpc-project-item__actions .btn-outline-primary:hover {
  background: #f5f8ff;
  border-color: #c8d7f3;
}

.wpc-project-delete-btn {
  min-height: 30px;
  font-weight: 600;
}

.wpc-steps {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.wpc-step {
  border: 1px solid #d9e5f9;
  border-radius: 11px;
  background: #f7fbff;
  padding: 10px;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  text-align: left;
  transition: border-color 0.16s ease, background-color 0.16s ease, transform 0.16s ease;
}

.wpc-step:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.wpc-step:not(:disabled):hover {
  border-color: #bdd4f7;
  transform: translateY(-1px);
}

.wpc-step.active {
  border-color: #89b2f6;
  background: #ecf4ff;
}

.wpc-step.complete {
  border-color: #8fd3af;
  background: #effbf4;
}

.wpc-step.active.complete {
  border-color: #89b2f6;
  background: #ecf4ff;
}

.wpc-step.viewing:not(.active) {
  box-shadow: 0 0 0 1px #d7e3f7 inset;
}

.wpc-step__index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #deeaff;
  color: #274b87;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
}

.wpc-step.complete .wpc-step__index {
  background: #c9efd9;
  color: #245c3d;
}

.wpc-step.active .wpc-step__index,
.wpc-step.active.complete .wpc-step__index {
  background: #deeaff;
  color: #274b87;
}

.wpc-step__label {
  color: #2a3f63;
  font-size: 13px;
  font-weight: 700;
}

.wpc-step-pane {
  border: 1px solid #e2eaf9;
  border-radius: 12px;
  background: linear-gradient(180deg, #fcfdff 0%, #f7fbff 100%);
  padding: 11px;
}

.wpc-config-card {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #ffffff;
  padding: 10px;
}

.wpc-config-card__head {
  margin-bottom: 10px;
}

.wpc-config-card__head h6 {
  color: #1f2d45;
  font-weight: 700;
}

.wpc-config-grid {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) auto;
  gap: 10px;
  align-items: end;
}

.wpc-field .form-label {
  font-size: 12px;
  font-weight: 700;
  color: #3f4f70;
  margin-bottom: 4px;
}

.wpc-field .form-select {
  min-height: 34px;
  font-size: 13px;
  border-color: #dfe5f2;
}

.wpc-action {
  display: inline-flex;
}

.wpc-generate-btn {
  font-weight: 700;
  min-height: 34px;
}

.wpc-project-meta {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.wpc-meta-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d5e4fb;
  background: #f2f7ff;
  color: #345786;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  line-height: 1;
}

.wpc-meta-pill__value {
  margin-left: 4px;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.wpc-loading-state,
.wpc-empty-state {
  margin-top: 11px;
  border: 1px dashed #cad8f0;
  border-radius: 11px;
  background: #fff;
  padding: 14px;
  color: #5a6f92;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wpc-empty-state i {
  font-size: 18px;
  color: #6f88b1;
}

.wpc-concepts-block {
  margin-top: 11px;
}

.wpc-concepts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 10px;
}

.wpc-concept-card {
  border: 1px solid #d9e4f8;
  border-radius: 12px;
  background: #fff;
  padding: 10px;
  position: relative;
  box-shadow: 0 8px 18px rgba(24, 55, 100, 0.08);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.wpc-concept-card:hover {
  border-color: #b8cff3;
  transform: translateY(-1px);
}

.wpc-concept-card.selected {
  border-color: #2eaa6a;
  box-shadow: 0 0 0 2px #d5f5e4 inset, 0 10px 24px rgba(35, 94, 56, 0.18);
  background: linear-gradient(180deg, #ffffff 0%, #f8fffb 100%);
}

.wpc-concept-card.dimmed {
  opacity: 0.5;
  filter: grayscale(0.35);
  box-shadow: none;
}

.wpc-concept-card.dimmed:hover {
  transform: none;
}

.wpc-concept-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.wpc-concept-card__head > .wpc-select-btn {
  margin-left: auto;
}

.wpc-concept-card__head > .wpc-edit-btn,
.wpc-concept-card__head > .wpc-delete-btn {
  margin-left: 6px;
}

.wpc-select-btn {
  font-weight: 700;
}

.wpc-select-btn.is-selected {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35) inset;
}

.wpc-delete-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.wpc-edit-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.wpc-edit-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.wpc-edit-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wpc-edit-field > span {
  color: #5f7698;
  font-size: 11px;
  font-weight: 700;
}

.wpc-edit-field--full {
  grid-column: 1 / -1;
}

.wpc-concept-card__edit-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.wpc-concept-card__order {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d3e2fb;
  background: #edf4ff;
  color: #355b95;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  line-height: 1;
}

.wpc-concept-card__title {
  color: #1e2d46;
  font-weight: 700;
}

.wpc-concept-card__angle {
  color: #556987;
  font-size: 13px;
  line-height: 1.35;
}

.wpc-info-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 8px;
  padding: 4px 0;
  border-top: 1px solid #edf2fa;
}

.wpc-info-row:first-of-type {
  border-top: none;
}

.wpc-info-label {
  color: #617696;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
}

.wpc-info-value {
  color: #2c3f5f;
  font-size: 13px;
}

.wpc-template-swatch {
  display: inline-block;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  margin-right: 4px;
  vertical-align: middle;
}

.wpc-template-privacy {
  margin: 6px 0 0;
  padding-left: 18px;
}

.wpc-template-privacy li {
  font-size: 13px;
  color: #768192;
}

.wpc-tags-group {
  margin-top: 8px;
}

.wpc-tone-subgroup + .wpc-tone-subgroup {
  margin-top: 6px;
}

.wpc-tone-subgroup {
  padding-left: 8px;
}

.wpc-tone-subgroup__label {
  color: #5e7394;
  font-size: 11px;
  font-weight: 700;
}

.wpc-tags-label {
  color: #5e7394;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
}

.wpc-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 5px;
}

.wpc-tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d5e4fa;
  background: #eff5ff;
  color: #345a92;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  line-height: 1.2;
}

.wpc-tag--danger {
  border-color: #f4ccd1;
  background: #fff1f3;
  color: #973d49;
}

.wpc-tag--muted {
  border-color: #dfe6f3;
  background: #f5f8fd;
  color: #6a7d97;
}

.wpc-concepts-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #dce7fa;
  border-radius: 10px;
  background: #ffffff;
  padding: 9px;
}

.wpc-concepts-actions .btn {
  font-weight: 700;
}

.wpc-concepts-actions__summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #2a4167;
  font-size: 13px;
  min-width: 0;
}

.wpc-concepts-actions__summary strong {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wpc-concepts-actions__label {
  color: #5f7698;
  font-weight: 700;
}

.wpc-concepts-actions__pending {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #f4dcc1;
  background: #fff6ec;
  color: #9f642a;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 8px;
}

.wpc-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 33, 61, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1040;
}

.wpc-modal {
  width: min(100%, 460px);
  border: 1px solid #dce7fa;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 36px rgba(15, 33, 61, 0.22);
  overflow: hidden;
}

.wpc-modal__head {
  padding: 12px 14px;
  border-bottom: 1px solid #e3ebfb;
  background: #f8fbff;
  color: #1f2d45;
}

.wpc-modal__head h6 {
  font-weight: 700;
}

.wpc-modal__body {
  padding: 14px;
  color: #2e456a;
  font-size: 14px;
}

.wpc-modal__actions {
  padding: 0 14px 14px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.wpc-identity-categories {
  border-top: 1px solid #e6eefb;
  padding-top: 10px;
}

.wpc-localization-preview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.wpc-localization-preview--final-assets {
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  grid-template-areas:
    "logo domain"
    "logo favicon";
}

.wpc-final-asset-panel--logo {
  grid-area: logo;
}

.wpc-final-asset-panel--domain {
  grid-area: domain;
  display: flex;
  flex-direction: column;
}

.wpc-final-domain-value {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 30px;
  line-height: 1.15;
  font-weight: 800;
  color: #1f3d73;
  padding: 8px;
  overflow-wrap: anywhere;
}

.wpc-final-asset-panel--favicon {
  grid-area: favicon;
}

.wpc-localization-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-localization-toolbar__locale {
  min-width: 220px;
  margin-bottom: 0;
}

.wpc-localization-toolbar__locale .form-select {
  min-height: 34px;
}

.wpc-localization-panel {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 10px;
  min-width: 0;
}

.wpc-localization-panel__head {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8eefb;
}

.wpc-localization-panel__head h6 {
  color: #1f2d45;
  font-weight: 700;
}

.wpc-identity-fields {
  margin-bottom: 10px;
}

.wpc-overwrite-warning {
  border: 1px solid #f4dcc1;
  border-radius: 10px;
  background: #fff6ec;
  color: #8a5a27;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  padding: 9px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wpc-overwrite-warning__label {
  display: inline-flex;
  align-items: center;
  border: 1px solid #efcda8;
  border-radius: 999px;
  background: #fff0de;
  color: #8a5a27;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 8px;
  flex-shrink: 0;
}

.wpc-identity-categories__head h6 {
  color: #1f2d45;
  font-weight: 700;
}

.wpc-identity-categories__head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.wpc-identity-categories__list {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.wpc-identity-category {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 8px 9px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.wpc-identity-category__main {
  min-width: 0;
  color: #2d4264;
  font-size: 13px;
  display: grid;
  gap: 2px;
}

.wpc-identity-category__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.wpc-identity-category__slug {
  display: inline-flex;
  width: fit-content;
  border-radius: 999px;
  border: 1px solid #d5e4fa;
  background: #eff5ff;
  color: #345a92;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
}

.wpc-identity-categories__form {
  margin-top: 10px;
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 9px;
  display: grid;
  gap: 8px;
}

.wpc-identity-categories__actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.wpc-step-nav {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.wpc-domain-tlds {
  margin-bottom: 10px;
}

.wpc-domain-tlds__list {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wpc-domain-tld {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #d5e4fa;
  border-radius: 999px;
  background: #f5f9ff;
  color: #335a91;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 9px;
}

.wpc-domain-availability-actions {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.wpc-domain-availability-progress {
  font-size: 12px;
  font-weight: 700;
  color: #244b84;
}

.wpc-domain-availability-status {
  font-size: 12px;
  color: #55719a;
}

.wpc-domain-availability-error {
  margin-bottom: 8px;
  border: 1px solid #f3c7ce;
  border-radius: 10px;
  background: #fff0f2;
  color: #943543;
  font-size: 12px;
  padding: 8px 10px;
}

.wpc-domain-list {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.wpc-domain-item {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
}

.wpc-domain-item.selected {
  border-color: #9fd7b9;
  background: #f4fff8;
  box-shadow: 0 0 0 1px #d6f3e4 inset;
}

.wpc-domain-item.busy {
  cursor: wait;
}

.wpc-domain-item__main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2d4264;
  font-size: 13px;
}

.wpc-domain-item__main strong {
  font-size: 18px;
  line-height: 1.2;
}

.wpc-domain-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6deed;
  border-radius: 999px;
  background: #f5f8ff;
  color: #3c4f6d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 8px;
  text-transform: lowercase;
  min-width: 74px;
}

.wpc-domain-status--available {
  border-color: #bce8cd;
  background: #f3fff8;
  color: #1d7d45;
}

.wpc-domain-status--unavailable {
  border-color: #f1c5cb;
  background: #fff2f4;
  color: #b03a48;
}

.wpc-domain-status--error {
  border-color: #f6d7a7;
  background: #fff8ea;
  color: #8c5c0f;
}

.wpc-domain-status--unknown {
  border-color: #d6deed;
  background: #f5f8ff;
  color: #3c4f6d;
}

.wpc-domain-item__actions {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
}

.wpc-domain-add-form {
  margin-top: 10px;
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 9px;
}

.wpc-domain-add-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.wpc-logo-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-logo-top-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-logo-preview {
  margin-top: 10px;
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.wpc-logo-preview__image {
  max-width: 100%;
  max-height: 240px;
  object-fit: contain;
}

.wpc-favicon-preview__image {
  width: 96px;
  height: 96px;
  object-fit: contain;
  image-rendering: auto;
}

.wpc-logo-hint {
  max-width: 560px;
}

.wpc-logo-candidates {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.wpc-logo-candidates__grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.wpc-logo-candidate {
  position: relative;
  border: 2px solid #dce7fa;
  border-radius: 11px;
  background:
    linear-gradient(45deg, #f3f6fb 25%, transparent 25%, transparent 75%, #f3f6fb 75%) 0 0 / 16px 16px,
    linear-gradient(45deg, #f3f6fb 25%, #fff 25%, #fff 75%, #f3f6fb 75%) 8px 8px / 16px 16px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  height: 84px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.wpc-logo-candidate:hover:not(:disabled) {
  border-color: #9cc0f5;
}

.wpc-logo-candidate.active {
  border-color: #2f6fd1;
  box-shadow: 0 0 0 2px rgba(47, 111, 209, 0.25);
}

.wpc-logo-candidate:disabled {
  cursor: default;
  opacity: 0.75;
}

.wpc-logo-candidate__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.wpc-logo-candidate__spinner {
  position: absolute;
  color: #2f6fd1;
}

.wpc-logo-editor {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wpc-logo-gallery {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wpc-logo-gallery__grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.wpc-logo-gallery__item {
  position: relative;
  border: 2px solid #dce7fa;
  border-radius: 11px;
  background:
    linear-gradient(45deg, #f3f6fb 25%, transparent 25%, transparent 75%, #f3f6fb 75%) 0 0 / 16px 16px,
    linear-gradient(45deg, #f3f6fb 25%, #fff 25%, #fff 75%, #f3f6fb 75%) 8px 8px / 16px 16px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  height: 84px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.wpc-logo-gallery__item:hover:not(:disabled) {
  border-color: #9cc0f5;
}

.wpc-logo-gallery__item.active {
  border-color: #2f6fd1;
  box-shadow: 0 0 0 2px rgba(47, 111, 209, 0.25);
}

.wpc-logo-gallery__item:disabled {
  cursor: default;
  opacity: 0.75;
}

.wpc-logo-gallery__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.wpc-logo-gallery__spinner {
  position: absolute;
  color: #2f6fd1;
}

.wpc-logo-editor__stage {
  position: relative;
  width: 100%;
  min-height: 260px;
  border: 1px solid #dce7fa;
  border-radius: 11px;
  overflow: hidden;
  background:
    linear-gradient(45deg, #f3f6fb 25%, transparent 25%, transparent 75%, #f3f6fb 75%) 0 0 / 16px 16px,
    linear-gradient(45deg, #f3f6fb 25%, #fff 25%, #fff 75%, #f3f6fb 75%) 8px 8px / 16px 16px;
  touch-action: none;
  user-select: none;
}

.wpc-logo-editor__icon-wrap {
  position: absolute;
  cursor: grab;
  touch-action: none;
}

.wpc-logo-editor__icon-wrap:hover {
  outline: 1px dashed rgba(47, 111, 209, 0.6);
}

.wpc-logo-editor__icon-wrap.dragging {
  cursor: grabbing;
  outline: 1px solid rgba(47, 111, 209, 0.85);
}

.wpc-logo-editor__icon {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
}

/* Apply button pinned to the bottom-right corner of the editing canvas. */
.wpc-logo-editor__apply {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 5;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

/* Single-line draggable text layer. No padding and line-height:1 so the rendered position
   matches the server compose (which draws the baseline at top + font ascent). */
.wpc-logo-editor__layer {
  position: absolute;
  display: block;
  line-height: 1;
  white-space: nowrap;
  cursor: grab;
  padding: 0;
}

.wpc-logo-editor__layer:hover,
.wpc-logo-editor__layer.dragging {
  outline: 1px dashed rgba(47, 111, 209, 0.7);
}

.wpc-logo-editor__layer.dragging {
  cursor: grabbing;
  outline-style: solid;
}

/* Paint-style resize handle at the layer's bottom-right corner. */
.wpc-logo-editor__handle {
  position: absolute;
  right: -6px;
  bottom: -6px;
  width: 12px;
  height: 12px;
  border: 1px solid #fff;
  border-radius: 2px;
  background: #2f6fd1;
  cursor: nwse-resize;
  opacity: 0;
  transition: opacity 0.12s ease;
}

.wpc-logo-editor__layer:hover .wpc-logo-editor__handle,
.wpc-logo-editor__layer.dragging .wpc-logo-editor__handle,
.wpc-logo-editor__icon-wrap:hover .wpc-logo-editor__handle,
.wpc-logo-editor__icon-wrap.dragging .wpc-logo-editor__handle {
  opacity: 1;
}

.wpc-logo-editor__controls {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
}

.wpc-logo-editor__row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-logo-editor__font-select {
  flex: 1 1 120px;
  min-width: 110px;
  width: auto;
}

.wpc-logo-editor__group {
  flex: 1 1 260px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid #e6edf8;
  border-radius: 10px;
  background: #fbfdff;
}

.wpc-logo-editor__group-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #2f4368;
}

.wpc-logo-editor__group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

/* Keep the add/remove-subtitle button compact and left-aligned at the bottom of the group. */
.wpc-logo-editor__group-btn {
  align-self: flex-start;
  margin-top: 2px;
}

.wpc-logo-editor__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wpc-logo-editor__field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #5a6b85;
}

.wpc-logo-editor__subtitle-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.wpc-logo-editor__swatches {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

/* Color control: a trigger swatch with a palette popover that opens to its right. */
.wpc-logo-editor__color {
  position: relative;
  display: inline-flex;
}

.wpc-logo-editor__color-trigger {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #cbd6ea;
  cursor: pointer;
  padding: 0;
}

.wpc-logo-editor__color-pop {
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  max-width: 220px;
  padding: 8px;
  background: #fff;
  border: 1px solid #dce7fa;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
}

.wpc-logo-editor__swatch {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #dce7fa;
  cursor: pointer;
  padding: 0;
}

.wpc-logo-editor__swatch.active {
  border-color: #2f6fd1;
  box-shadow: 0 0 0 2px rgba(47, 111, 209, 0.25);
}

.wpc-logo-editor__swatch:disabled {
  cursor: default;
  opacity: 0.75;
}

.wpc-logo-editor__color-input {
  width: 32px;
  height: 28px;
  padding: 0;
  border: 1px solid #dce7fa;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.wpc-logo-editor__stepper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-logo-editor__stepper input[type='range'] {
  flex: 1 1 140px;
  min-width: 120px;
}

.wpc-logo-editor__actions {
  display: flex;
  gap: 8px;
}

.wpc-logo-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-logo-toolbar {
  margin-top: 10px;
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.wpc-logo-toolbar__group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-logo-toolbar__group--actions {
  margin-left: auto;
}

.wpc-logo-toolbar__label {
  color: #5f7698;
  font-size: 12px;
  font-weight: 700;
}

.wpc-logo-variant-btn.active,
.wpc-logo-variant-btn:active {
  background: #2f6fd1;
  border-color: #2f6fd1;
  color: #fff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.28) inset;
}

.wpc-logo-crop-note {
  border: 1px solid #dce7fa;
  border-radius: 10px;
  background: #f7fbff;
  color: #3a547a;
  font-size: 13px;
  line-height: 1.4;
  padding: 10px 11px;
}

.wpc-logo-crop-tool {
  border: 1px solid #dce7fa;
  border-radius: 11px;
  background: #fff;
  padding: 10px;
  display: grid;
  justify-items: center;
}

.wpc-logo-crop-frame {
  position: relative;
  display: inline-block;
  max-width: 100%;
  border: 1px dashed #c7d7f3;
  border-radius: 10px;
  background: #f7fbff;
  overflow: hidden;
  vertical-align: top;
}

.wpc-logo-crop-image {
  display: block;
  width: min(100%, 960px);
  max-width: 100%;
  max-height: 340px;
  object-fit: contain;
  user-select: none;
}

.wpc-logo-crop-overlay {
  position: absolute;
  inset: 0;
  cursor: crosshair;
  touch-action: none;
  outline: none;
}

.wpc-logo-crop-overlay:focus-visible {
  box-shadow: 0 0 0 2px rgba(47, 111, 209, 0.35) inset;
}

.wpc-logo-crop-selection {
  position: absolute;
  border: 2px solid #2f6fd1;
  background: rgba(47, 111, 209, 0.18);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.45) inset;
  cursor: move;
  pointer-events: auto;
}

.wpc-logo-crop-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  border: 1px solid #1f5dba;
  background: #fff;
  border-radius: 2px;
  padding: 0;
}

.wpc-logo-crop-handle--nw { top: -6px; left: -6px; cursor: nwse-resize; }
.wpc-logo-crop-handle--n { top: -6px; left: calc(50% - 5px); cursor: ns-resize; }
.wpc-logo-crop-handle--ne { top: -6px; right: -6px; cursor: nesw-resize; }
.wpc-logo-crop-handle--e { top: calc(50% - 5px); right: -6px; cursor: ew-resize; }
.wpc-logo-crop-handle--se { bottom: -6px; right: -6px; cursor: nwse-resize; }
.wpc-logo-crop-handle--s { bottom: -6px; left: calc(50% - 5px); cursor: ns-resize; }
.wpc-logo-crop-handle--sw { bottom: -6px; left: -6px; cursor: nesw-resize; }
.wpc-logo-crop-handle--w { top: calc(50% - 5px); left: -6px; cursor: ew-resize; }

.wpc-logo-crop-handle:focus-visible {
  outline: 1px solid #2f6fd1;
}

.wpc-logo-crop-meta {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wpc-logo-crop-actions {
  margin-top: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.wpc-placeholder-card {
  border: 1px solid #dce7fa;
  border-radius: 12px;
  background: #fff;
  padding: 26px 18px;
  text-align: center;
  color: #314868;
}

.wpc-placeholder-card__icon {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef5ff;
  border: 1px solid #d5e3fb;
  margin-bottom: 10px;
  color: #2f5d9a;
  font-size: 24px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
}

@media (max-width: 992px) {
  .wpc-hero {
    margin: 0 12px 12px;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start !important;
  }

  .wpc-hero__actions {
    width: 100%;
  }

  .wpc-projects-head {
    flex-direction: column;
  }

  .wpc-projects-head__actions {
    width: 100%;
  }

  .wpc-projects-head__actions .btn {
    flex: 1 1 auto;
  }

  .wpc-config-grid {
    grid-template-columns: 1fr;
  }

  .wpc-action {
    width: 100%;
  }

  .wpc-generate-btn {
    width: 100%;
  }

  .wpc-concepts-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .wpc-concepts-actions__summary {
    width: 100%;
    flex-wrap: wrap;
  }

  .wpc-concepts-actions__summary strong {
    max-width: 100%;
    white-space: normal;
  }

  .wpc-concepts-actions .btn {
    width: 100%;
  }
}

@media (max-width: 700px) {
  .wpc-project-item {
    flex-direction: column;
  }

  .wpc-project-item__actions {
    width: 100%;
    justify-content: space-between;
  }

  .wpc-project-item__actions .btn {
    width: 100%;
  }

  .wpc-steps {
    grid-template-columns: 1fr;
  }

  .wpc-concepts-grid {
    grid-template-columns: 1fr;
  }

  .wpc-domain-list {
    grid-template-columns: 1fr;
  }

  .wpc-localization-preview {
    grid-template-columns: 1fr;
  }

  .wpc-localization-preview--final-assets {
    grid-template-columns: 1fr;
    grid-template-areas:
      "logo"
      "domain"
      "favicon";
  }

  .wpc-localization-toolbar {
    align-items: stretch;
  }

  .wpc-localization-toolbar > .btn,
  .wpc-localization-toolbar__locale {
    width: 100%;
  }

  .wpc-edit-grid {
    grid-template-columns: 1fr;
  }

  .wpc-info-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .wpc-domain-item {
    flex-direction: column;
  }

  .wpc-domain-item__actions {
    width: 100%;
    justify-content: flex-end;
    align-items: center;
  }

  .wpc-logo-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .wpc-logo-toolbar__group,
  .wpc-logo-toolbar__group--actions {
    width: 100%;
    margin-left: 0;
  }

  .wpc-logo-toolbar .btn-group {
    width: 100%;
  }

  .wpc-logo-toolbar .btn-group .btn,
  .wpc-logo-toolbar__group--actions .btn,
  .wpc-logo-top-actions .btn,
  .wpc-logo-crop-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
